import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getHECPlannings, getHECPlanning, seedHECFromExcel, ouvrirFichier } from "../lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Truck, Calendar, MapPin, Sun, Moon, Phone, User, Loader2, Download, RefreshCw, 
  Utensils, Zap, Car, Building2, Edit, Plus, Trash2, Wine, Beer, Save, X, 
  FileText, GlassWater, Settings, LayoutDashboard, ClipboardList, ChefHat,
  Users, Clock, Info, Check, AlertCircle, Euro, Image, Camera, Map, Upload, Eye, GripVertical, ArrowLeftRight, Calculator, TrendingUp, BarChart3, Printer, Copy, CheckCircle, History, RotateCcw, Database, Eraser, Plug, UserPlus, Snowflake, Coffee, IceCream, Send, Mail, Archive, AlertTriangle, Sunrise, Sunset, Link2, Wand2, Palette
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import HECInteractiveMap from "../components/HECInteractiveMap";
import MenuGeneratorModal from "../components/MenuGeneratorModal";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Zone colors configuration
const ZONE_COLORS = {
  "Volley": { bg: "bg-yellow-100", border: "border-yellow-300", text: "text-yellow-800", header: "from-yellow-50 to-yellow-100" },
  "Central": { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-800", header: "from-emerald-50 to-emerald-100" },
  "Foot": { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-800", header: "from-blue-50 to-blue-100" },
  "Hall d'honneur": { bg: "bg-orange-100", border: "border-orange-300", text: "text-orange-800", header: "from-orange-50 to-orange-100" },
  "BAT S": { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-800", header: "from-purple-50 to-purple-100" },
};

const getZoneColor = (zone) => ZONE_COLORS[zone] || { bg: "bg-gray-100", border: "border-gray-300", text: "text-gray-800", header: "from-gray-50 to-gray-100" };

export default function HECPlanningPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isClient = user?.role === "client";
  const canEdit = !isClient; // Clients cannot edit
  const token = localStorage.getItem("token"); // Get token from localStorage
  const [loading, setLoading] = useState(true);
  const [plannings, setPlannings] = useState([]);
  const [selectedPlanning, setSelectedPlanning] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [activeTab, setActiveTab] = useState("planning");
  const [exportingPDF, setExportingPDF] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Food Truck states
  const [editingFoodTruck, setEditingFoodTruck] = useState(null);
  const [editingFoodTruckIndex, setEditingFoodTruckIndex] = useState(null);
  const [showFoodTruckDialog, setShowFoodTruckDialog] = useState(false);
  const [allAdherents, setAllAdherents] = useState([]); // Liste de tous les food trucks adhérents
  const [selectedAdherentId, setSelectedAdherentId] = useState(null); // Food truck sélectionné pour import
  
  // Food Truck Detail Modal (click on planning)
  const [showFoodTruckDetail, setShowFoodTruckDetail] = useState(false);
  const [selectedFoodTruckDetail, setSelectedFoodTruckDetail] = useState(null);
  
  // Bar states
  const [editingBar, setEditingBar] = useState(null);
  const [editingBarIndex, setEditingBarIndex] = useState(null);
  const [showBarDialog, setShowBarDialog] = useState(false);
  
  // Menu states
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedMenuJour, setSelectedMenuJour] = useState("Jeudi"); // Pour afficher les menus par jour
  const [importingMenus, setImportingMenus] = useState(false);
  const [exportingClientMenus, setExportingClientMenus] = useState(false);
  
  // Document upload states
  const [uploadingDoc, setUploadingDoc] = useState(null); // { type: 'identite' | 'assurance_rc' | 'haccp' | 'assurance_vehicule' }
  
  // Document migration states
  const [migratingDocs, setMigratingDocs] = useState(false);
  const [cleaningDocs, setCleaningDocs] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Drag and drop states
  const [draggedSlot, setDraggedSlot] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null);
  
  // Add/Remove food truck from planning slots
  const [showAddToZoneDialog, setShowAddToZoneDialog] = useState(false);
  const [addToZoneConfig, setAddToZoneConfig] = useState({ zone: "", service: "midi", jour: "" });
  const [selectedFtForZone, setSelectedFtForZone] = useState(null);
  const [pendingZoneAssignment, setPendingZoneAssignment] = useState(null); // Pour ajouter automatiquement après création

  // Équipements supplémentaires
  const [equipements, setEquipements] = useState([]);
  const [editingEquipement, setEditingEquipement] = useState(null);
  const [showEquipementDialog, setShowEquipementDialog] = useState(false);
  const TYPES_EQUIPEMENT = [
    "Tireuse à bière",
    "Beer truck", 
    "Camion frigo",
    "Frigo",
    "Congélateur",
    "Machine à glaçons",
    "Groupe électrogène",
    "Autre"
  ];

  // Staff Bar
  const [staffBar, setStaffBar] = useState({ staff: [], cout_global: null, notes: "", planning_horaire: {} });
  const [editingStaffMember, setEditingStaffMember] = useState(null);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [importingStaffExcel, setImportingStaffExcel] = useState(false);
  
  // Planning Horaire Staff Bar
  const [selectedPlanningDay, setSelectedPlanningDay] = useState("Jeudi");
  const PLANNING_HOURS = ["17:00", "18:00", "19:00", "20:00", "21:00", "22:00", "23:00", "00:00", "01:00", "02:00"];
  const PLANNING_DAYS = ["Jeudi", "Vendredi", "Samedi"];

  // Menu Generator
  const [showMenuGenerator, setShowMenuGenerator] = useState(false);
  const [menuGeneratorFoodTruck, setMenuGeneratorFoodTruck] = useState(null);

  // Coffee/Dessert Food Trucks
  const [coffeeDesserts, setCoffeeDesserts] = useState([]);
  const [editingCoffeeDessert, setEditingCoffeeDessert] = useState(null);
  const [showCoffeeDessertDialog, setShowCoffeeDessertDialog] = useState(false);
  const TYPES_COFFEE_DESSERT = [
    "Coffee & Dessert",
    "Coffee",
    "Desserts",
    "Glaces",
    "Crêpes & Gaufres",
    "Pâtisseries",
    "Bubble Tea",
    "Autre"
  ];

  // Projection boissons - État paramétrable
  const [projections, setProjections] = useState({
    // Beer Truck 2026 (NOUVEAU - n'existait pas en 2025)
    beerTruck: [
      { id: 1, nom: "Bière pression 25cl", projection2026: 2000, type: "biere", futType: "30L" },
      { id: 2, nom: "Bière pression 50cl", projection2026: 1500, type: "biere", futType: "30L" },
      { id: 3, nom: "Soft drink", projection2026: 500, type: "soft", futType: null },
      { id: 4, nom: "Eau minérale", projection2026: 300, type: "soft", futType: null },
    ],
    // Bar Mobile 1
    barMobile1: [
      { id: 1, nom: "Blonde pression", ref2025: 2915, projection2026: 3000, type: "biere", futType: "30L" },
      { id: 2, nom: "Cidre pression", ref2025: 0, projection2026: 800, type: "cidre", futType: "30L" },
      { id: 3, nom: "Coca", ref2025: 421, projection2026: 450, type: "soft", futType: null },
      { id: 4, nom: "Coca 0", ref2025: 174, projection2026: 200, type: "soft", futType: null },
      { id: 5, nom: "Eau", ref2025: 113, projection2026: 150, type: "soft", futType: null },
      { id: 6, nom: "Ice Tea", ref2025: 112, projection2026: 120, type: "soft", futType: null },
      { id: 7, nom: "Vin rouge", ref2025: 107, projection2026: 120, type: "vin", futType: null },
      { id: 8, nom: "Vin rosé", ref2025: 92, projection2026: 100, type: "vin", futType: null },
      { id: 9, nom: "Vin Blanc", ref2025: 191, projection2026: 200, type: "vin", futType: null },
    ],
    // Bar Mobile 2
    barMobile2: [
      { id: 10, nom: "Blonde pression", ref2025: 2915, projection2026: 3000, type: "biere", futType: "30L" },
      { id: 11, nom: "Cidre pression", ref2025: 0, projection2026: 800, type: "cidre", futType: "30L" },
      { id: 12, nom: "Vin rouge", ref2025: 107, projection2026: 120, type: "vin", futType: null },
      { id: 13, nom: "Vin rosé", ref2025: 92, projection2026: 100, type: "vin", futType: null },
      { id: 14, nom: "Vin Blanc", ref2025: 191, projection2026: 200, type: "vin", futType: null },
      { id: 15, nom: "Mojito", ref2025: 519, projection2026: 550, type: "cocktail", futType: null },
      { id: 16, nom: "Spritz", ref2025: 358, projection2026: 400, type: "cocktail", futType: null },
      { id: 17, nom: "Punch", ref2025: 213, projection2026: 250, type: "cocktail", futType: null },
      { id: 18, nom: "G27", ref2025: 49, projection2026: 60, type: "cocktail", futType: null },
      { id: 19, nom: "Sprite", ref2025: 15, projection2026: 20, type: "soft", futType: null },
      { id: 20, nom: "Jus Pomme", ref2025: 100, projection2026: 120, type: "soft", futType: null },
    ],
    // Référence 2025 - Tout était Bar (pas de Beer Truck)
    ref2025Total: {
      biere: 5830,
      soft: 2136,
      vin: 390,
      cocktail: 1139
    }
  });
  const [newBoissonBeerTruck, setNewBoissonBeerTruck] = useState({ nom: "", type: "biere", projection2026: 0 });
  const [newBoissonBar1, setNewBoissonBar1] = useState({ nom: "", type: "soft", projection2026: 0 });
  const [newBoissonBar2, setNewBoissonBar2] = useState({ nom: "", type: "soft", projection2026: 0 });
  const [projectionsLoaded, setProjectionsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Version history states
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [versionsData, setVersionsData] = useState({ current: null, versions: [] });
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);

  // Send dossier states
  const [showSendDossierDialog, setShowSendDossierDialog] = useState(false);
  const [sendingDossier, setSendingDossier] = useState(false);
  const [downloadingAllDocs, setDownloadingAllDocs] = useState(false);
  const [dossierForm, setDossierForm] = useState({
    email_destinataire: "",
    objet: "",
    message: "",
    include_documents: true,
    include_photos_link: true
  });

  // Dispatch Bière Blonde - Répartition entre Beer Truck et Bars Mobiles
  const [biereDispatch, setBiereDispatch] = useState({
    totalBlonde: 12000, // Total estimé de verres de blonde 2026
    beerTruckPct: 30,   // % vers Beer Truck (fûts 30L)
    barMobile1Pct: 35,  // % vers Bar Mobile 1 (fûts 30L)
    barMobile2Pct: 35,  // % vers Bar Mobile 2 (fûts 30L)
  });

  // Calculs dispatch
  const getDispatchVerres = (pct) => Math.round(biereDispatch.totalBlonde * pct / 100);
  const getDispatchFuts = (pct, tailleFut) => {
    const verres = getDispatchVerres(pct);
    return calculerFuts(verres, tailleFut, 50); // 50cl pour les bières
  };

  // Fonctions pour gérer les projections
  const updateProjection = (section, id, field, value) => {
    setProjections(prev => ({
      ...prev,
      [section]: prev[section].map(item => 
        item.id === id ? { ...item, [field]: field === 'projection2026' || field === 'ref2025' ? parseInt(value) || 0 : value } : item
      )
    }));
  };

  const addBoisson = (section) => {
    let newItem;
    if (section === 'beerTruck') {
      newItem = { ...newBoissonBeerTruck, id: Date.now(), ref2025: 0, futType: newBoissonBeerTruck.type === 'biere' ? '30L' : null };
    } else if (section === 'barMobile1') {
      newItem = { ...newBoissonBar1, id: Date.now(), ref2025: 0, futType: newBoissonBar1.type === 'biere' || newBoissonBar1.type === 'cidre' ? '30L' : null };
    } else {
      newItem = { ...newBoissonBar2, id: Date.now(), ref2025: 0, futType: newBoissonBar2.type === 'biere' || newBoissonBar2.type === 'cidre' ? '30L' : null };
    }
    
    if (!newItem.nom.trim()) {
      toast.error("Veuillez entrer un nom");
      return;
    }
    
    setProjections(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));
    
    if (section === 'beerTruck') {
      setNewBoissonBeerTruck({ nom: "", type: "biere", projection2026: 0 });
    } else if (section === 'barMobile1') {
      setNewBoissonBar1({ nom: "", type: "soft", projection2026: 0 });
    } else {
      setNewBoissonBar2({ nom: "", type: "soft", projection2026: 0 });
    }
    toast.success("Boisson ajoutée");
  };

  const removeBoisson = (section, id) => {
    setProjections(prev => ({
      ...prev,
      [section]: prev[section].filter(item => item.id !== id)
    }));
  };

  // Calcul des fûts de bière
  const calculerFuts = (verresTotal, tailleFut, tailleVerre = 25) => {
    const mlParFut = tailleFut * 1000;
    const mlParVerre = tailleVerre * 10;
    const verresParFut = Math.floor(mlParFut / mlParVerre);
    return Math.ceil(verresTotal / verresParFut);
  };

  // Calculs totaux
  const getTotalBeerTruck = () => projections.beerTruck.reduce((acc, item) => acc + item.projection2026, 0);
  const getTotalBiereBeerTruck = () => projections.beerTruck.filter(i => i.type === 'biere').reduce((acc, item) => acc + item.projection2026, 0);
  const getTotalBar1 = () => projections.barMobile1.reduce((acc, item) => acc + item.projection2026, 0);
  const getTotalBar2 = () => projections.barMobile2.reduce((acc, item) => acc + item.projection2026, 0);
  const getTotalStands = () => getTotalBar1() + getTotalBar2();
  const getTotalByType = (section, type) => projections[section].filter(i => i.type === type).reduce((acc, item) => acc + item.projection2026, 0);
  const getTotalBiereStands = () => getTotalByType('barMobile1', 'biere') + getTotalByType('barMobile2', 'biere');
  const getTotalCidreStands = () => getTotalByType('barMobile1', 'cidre') + getTotalByType('barMobile2', 'cidre');
  
  // Calcul fûts par bar
  const getFutsBlondeBar1 = () => calculerFuts(getTotalByType('barMobile1', 'biere'), 30, 50);
  const getFutsCidreBar1 = () => calculerFuts(getTotalByType('barMobile1', 'cidre'), 30, 50);
  const getFutsBlondeBar2 = () => calculerFuts(getTotalByType('barMobile2', 'biere'), 30, 50);
  const getFutsCidreBar2 = () => calculerFuts(getTotalByType('barMobile2', 'cidre'), 30, 50);
  
  // Handler pour ajuster les pourcentages de dispatch
  const updateDispatch = (field, value) => {
    const newValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    setBiereDispatch(prev => {
      const updated = { ...prev, [field]: newValue };
      // Auto-équilibrage pour que le total = 100%
      if (field !== 'totalBlonde') {
        const total = updated.beerTruckPct + updated.barMobile1Pct + updated.barMobile2Pct;
        if (total !== 100) {
          // Ajuster le dernier champ modifié pour compenser
          const diff = 100 - total;
          if (field === 'beerTruckPct') {
            updated.barMobile2Pct = Math.max(0, updated.barMobile2Pct + diff);
          } else if (field === 'barMobile1Pct') {
            updated.barMobile2Pct = Math.max(0, updated.barMobile2Pct + diff);
          }
        }
      }
      return updated;
    });
  };

  // Sauvegarde automatique des projections
  const saveProjections = async () => {
    const currentToken = localStorage.getItem("token");
    if (!selectedPlanning?.id || !projectionsLoaded || !currentToken) {
      console.log("Skip save - not ready:", { planningId: selectedPlanning?.id, loaded: projectionsLoaded, hasToken: !!currentToken });
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/hec/plannings/${selectedPlanning.id}/projections`, {
        beerTruck: projections.beerTruck,
        barMobile1: projections.barMobile1,
        barMobile2: projections.barMobile2,
        ref2025Total: projections.ref2025Total,
        biereDispatch: biereDispatch
      }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      console.log("Projections sauvegardées !");
    } catch (error) {
      console.error("Erreur sauvegarde projections:", error);
    } finally {
      setSaving(false);
    }
  };

  // Charger les projections sauvegardées
  const loadProjections = async (planningId) => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) {
      setProjectionsLoaded(true);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/hec/plannings/${planningId}/projections`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (response.data) {
        setProjections(prev => ({
          ...prev,
          beerTruck: response.data.beerTruck?.length > 0 ? response.data.beerTruck : prev.beerTruck,
          barMobile1: response.data.barMobile1?.length > 0 ? response.data.barMobile1 : prev.barMobile1,
          barMobile2: response.data.barMobile2?.length > 0 ? response.data.barMobile2 : prev.barMobile2,
          ref2025Total: response.data.ref2025Total || prev.ref2025Total
        }));
        if (response.data.biereDispatch) {
          setBiereDispatch(response.data.biereDispatch);
        }
      }
      setProjectionsLoaded(true);
    } catch (error) {
      console.error("Erreur chargement projections:", error);
      setProjectionsLoaded(true);
    }
  };

  // Auto-save quand les projections changent
  useEffect(() => {
    if (!projectionsLoaded || !selectedPlanning?.id) return;
    
    const timer = setTimeout(() => {
      saveProjections();
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [projections, biereDispatch, projectionsLoaded, selectedPlanning?.id]);

  // Charger les projections quand on sélectionne un planning
  useEffect(() => {
    if (selectedPlanning?.id) {
      setProjectionsLoaded(false);
      loadProjections(selectedPlanning.id);
    }
  }, [selectedPlanning?.id]);

  useEffect(() => {
    loadPlannings();
    loadAllAdherents();
  }, []);
  
  // Charger tous les food trucks adhérents pour import
  const loadAllAdherents = async () => {
    try {
      const currentToken = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/food-trucks`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setAllAdherents(response.data || []);
    } catch (error) {
      console.error("Error loading adherents:", error);
    }
  };

  const loadPlannings = async () => {
    try {
      const response = await getHECPlannings();
      setPlannings(response.data);
      if (response.data.length > 0) {
        loadPlanning(response.data[0].id);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des plannings");
    } finally {
      setLoading(false);
    }
  };

  const loadPlanning = async (planningId) => {
    try {
      const response = await getHECPlanning(planningId);
      setSelectedPlanning(response.data);
      if (response.data.planning_days?.length > 0) {
        setSelectedDay(response.data.planning_days[0].date);
      }
      // Load equipements, staff bar, and coffee/desserts
      setEquipements(response.data.equipements || []);
      setStaffBar(response.data.staff_bar || { staff: [], cout_global: null, notes: "" });
      setCoffeeDesserts(response.data.coffee_desserts || []);
    } catch (error) {
      toast.error("Erreur lors du chargement du planning");
    }
  };

  const handleSeedFromExcel = async () => {
    setSeeding(true);
    try {
      const response = await seedHECFromExcel();
      toast.success(`Planning ${response.data.status === 'created' ? 'créé' : 'mis à jour'} avec succès`);
      loadPlannings();
    } catch (error) {
      toast.error("Erreur lors de l'import");
    } finally {
      setSeeding(false);
    }
  };

  const getSlotsByDay = (date) => {
    if (!selectedPlanning) return { matin: [], midi: [], apres_midi: [], soir: [], nuit: [], bars_matin: [], bars_midi: [], bars_apres_midi: [], bars_soir: [], bars_nuit: [] };
    const day = selectedPlanning.planning_days?.find(d => d.date === date);
    return day || { matin: [], midi: [], apres_midi: [], soir: [], nuit: [], bars_matin: [], bars_midi: [], bars_apres_midi: [], bars_soir: [], bars_nuit: [] };
  };

  const getCurrentDaySlots = () => getSlotsByDay(selectedDay);

  // Group slots by zone
  const groupSlotsByZone = (slots) => {
    const grouped = {};
    slots?.forEach((slot, originalIndex) => {
      const zone = slot.zone || "Non assigné";
      if (!grouped[zone]) grouped[zone] = [];
      grouped[zone].push({ ...slot, originalIndex });
    });
    return grouped;
  };

  // Drag and drop handlers
  const handleDragStart = (e, slot, service, originalIndex) => {
    setDraggedSlot({ slot, service, originalIndex });
    e.dataTransfer.effectAllowed = "move";
    e.target.style.opacity = "0.5";
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedSlot(null);
    setDragOverZone(null);
  };

  const handleDragOver = (e, zone) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverZone(zone);
  };

  const handleDragLeave = () => {
    setDragOverZone(null);
  };

  const handleDrop = async (e, targetZone, targetService) => {
    e.preventDefault();
    setDragOverZone(null);
    
    if (!draggedSlot || draggedSlot.service !== targetService) {
      toast.error("Déplacement invalide : les services doivent correspondre");
      return;
    }

    // Get zones and emplacements
    const zones = selectedPlanning?.zones || [];
    const targetZoneInfo = zones.find(z => z.nom === targetZone);
    const emplacements = targetZoneInfo?.emplacements || ["Emplacement 1"];
    
    // Count current slots in target zone
    const currentSlots = getCurrentDaySlots()[targetService];
    const slotsInTargetZone = currentSlots.filter(s => s.zone === targetZone).length;
    const nextEmplacement = emplacements[slotsInTargetZone] || `Emplacement ${slotsInTargetZone + 1}`;

    try {
      await axios.put(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/move-slot`,
        {
          date: selectedDay,
          service: targetService,
          source_index: draggedSlot.originalIndex,
          target_zone: targetZone,
          target_emplacement: nextEmplacement
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      
      toast.success(`${draggedSlot.slot.food_truck_nom} déplacé vers ${targetZone}`);
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      toast.error("Erreur lors du déplacement");
    }
  };

  const handleSwapSlots = async (sourceIndex, targetIndex, service) => {
    try {
      await axios.put(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/swap-slots`,
        {
          date: selectedDay,
          service: service,
          source_index: sourceIndex,
          target_index: targetIndex
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      
      toast.success("Positions échangées");
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      toast.error("Erreur lors de l'échange");
    }
  };

  // Find food truck full info by ID
  const getFoodTruckInfo = (foodTruckId) => {
    return selectedPlanning?.food_trucks?.find(ft => ft.id === foodTruckId) || null;
  };

  // Get all menu entries for a food truck across all days
  const getFoodTruckMenus = (foodTruckId) => {
    const menus = [];
    selectedPlanning?.planning_days?.forEach(day => {
      day.midi?.forEach(slot => {
        if (slot.food_truck_id === foodTruckId) {
          menus.push({ date: day.date, creneau: 'midi', specialite: slot.specialite, menu: slot.menu_du_jour, zone: slot.zone });
        }
      });
      day.soir?.forEach(slot => {
        if (slot.food_truck_id === foodTruckId) {
          menus.push({ date: day.date, creneau: 'soir', specialite: slot.specialite, menu: slot.menu_du_jour, zone: slot.zone });
        }
      });
    });
    return menus;
  };

  // Open food truck detail modal
  const openFoodTruckDetail = (slot) => {
    const ftInfo = getFoodTruckInfo(slot.food_truck_id);
    const menus = getFoodTruckMenus(slot.food_truck_id);
    setSelectedFoodTruckDetail({ slot, ftInfo, menus });
    setShowFoodTruckDetail(true);
  };
  
  // Add food truck to a specific zone/slot
  const openAddToZone = (zone, service) => {
    setAddToZoneConfig({ zone, service, jour: selectedDay });
    setSelectedFtForZone(null);
    setShowAddToZoneDialog(true);
  };
  
  const addFoodTruckToZone = async () => {
    if (!selectedFtForZone) {
      toast.error("Sélectionnez un food truck");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const ft = selectedPlanning.food_trucks?.find(f => f.id === selectedFtForZone);
      
      const newSlot = {
        food_truck_id: selectedFtForZone,
        food_truck_nom: ft?.nom || "Food Truck",
        specialite: ft?.specialite || "",
        zone: addToZoneConfig.zone,
        horaire_debut: 
          addToZoneConfig.service === "matin" ? "17:00" : 
          addToZoneConfig.service === "midi" ? "11:00" : 
          addToZoneConfig.service === "apres_midi" ? "14:00" : 
          addToZoneConfig.service === "soir" ? "18:00" : "00:00",
        horaire_fin: 
          addToZoneConfig.service === "matin" ? "11:00" : 
          addToZoneConfig.service === "midi" ? "14:00" : 
          addToZoneConfig.service === "apres_midi" ? "18:00" : 
          addToZoneConfig.service === "soir" ? "23:00" : "02:00",
      };
      
      await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/slot`,
        {
          date: addToZoneConfig.jour,
          service: addToZoneConfig.service,
          slot: newSlot
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${ft?.nom || "Food truck"} ajouté à la zone ${addToZoneConfig.zone}`);
      setShowAddToZoneDialog(false);
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error adding to zone:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };
  
  const removeFromZone = async (service, slotIndex, ftNom) => {
    if (!window.confirm(`Retirer ${ftNom} de cette zone ?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      
      await axios.delete(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/slot`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: {
            date: selectedDay,
            service: service,
            slot_index: slotIndex
          }
        }
      );
      
      toast.success(`${ftNom} retiré de la zone`);
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error removing from zone:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  // Food Truck CRUD
  const openEditFoodTruck = (ft, index) => {
    setEditingFoodTruck({ ...ft, menu_items: ft.menu_items || [] });
    setEditingFoodTruckIndex(index);
    setShowFoodTruckDialog(true);
  };

  const openAddFoodTruck = () => {
    setSelectedAdherentId(null);
    setEditingFoodTruck({
      nom: "", responsable: "", telephone: "", specialite: "", type_vehicule: "", ouverture: "", 
      puissance_electrique: "", plaque_immatriculation: "", auvent: false, personnel: 1, 
      vehicule_personnel: "", plaque_vp: "", menu_default: "", photo_url: "",
      menu_items: [],
      // Documents
      doc_identite: null, doc_identite_filename: null,
      doc_assurance_rc: null, doc_assurance_rc_filename: null,
      doc_haccp: null, doc_haccp_filename: null,
      doc_assurance_vehicule: null, doc_assurance_vehicule_filename: null,
    });
    setEditingFoodTruckIndex(null);
    setShowFoodTruckDialog(true);
  };
  
  // Importer les données d'un adhérent existant
  const importAdherentData = (adherentId) => {
    if (!adherentId) {
      setSelectedAdherentId(null);
      setEditingFoodTruck({
        nom: "", responsable: "", telephone: "", email: "", specialite: "", 
        type_vehicule: "", taille_vehicule: "", longueur: "", largeur: "", hauteur: "",
        ouverture: "", puissance_electrique: "", plaque_immatriculation: "", 
        auvent: false, personnel: 1, equipe: [],
        vehicule_personnel: "", plaque_vp: "", menu_default: "", photo_url: "",
        menu_items: [],
        doc_identite: null, doc_identite_filename: null,
        doc_assurance_rc: null, doc_assurance_rc_filename: null,
        doc_haccp: null, doc_haccp_filename: null,
        doc_assurance_vehicule: null, doc_assurance_vehicule_filename: null,
      });
      return;
    }
    
    const adherent = allAdherents.find(a => a.id === adherentId);
    if (!adherent) return;
    
    setSelectedAdherentId(adherentId);
    
    // Extract documents from adherent's documents array
    const docs = adherent.documents || [];
    const findDoc = (type) => docs.find(d => d.type === type || d.type?.includes(type));
    
    const docIdentite = findDoc('identite') || findDoc('cni') || findDoc('passeport');
    const docAssuranceRc = findDoc('assurance_rc') || findDoc('rc_pro') || findDoc('assurance');
    const docHaccp = findDoc('haccp');
    const docAssuranceVehicule = findDoc('assurance_vehicule');
    
    // Get vehicle dimensions from point_restauration or direct fields
    const pointResto = adherent.point_restauration || {};
    const vehiculeInfo = pointResto.vehicule || {};
    
    // Get representant info (contact principal)
    const representant = adherent.representant || {};
    const responsableNom = representant.nom && representant.prenom 
      ? `${representant.prenom} ${representant.nom}`
      : adherent.nom_contact || adherent.contact_nom || "";
    
    // Get team/equipe info
    const equipeData = adherent.equipe || adherent.team || [];
    
    // Importer toutes les données de l'adhérent
    setEditingFoodTruck({
      adherent_id: adherent.id, // Garder la référence à l'adhérent original
      nom: adherent.nom_food_truck || adherent.nom_commercial || adherent.nom || "",
      responsable: responsableNom || `${adherent.prenom || ""} ${adherent.nom || ""}`.trim(),
      telephone: representant.telephone || adherent.telephone || adherent.contact_telephone || "",
      email: representant.email || adherent.email || "",
      specialite: adherent.specialite || (adherent.specialites && adherent.specialites.join(", ")) || "",
      // Véhicule
      type_vehicule: adherent.type_vehicule || vehiculeInfo.type || pointResto.type_vehicule || "",
      taille_vehicule: adherent.taille_vehicule || pointResto.taille || "",
      longueur: adherent.longueur || vehiculeInfo.longueur || pointResto.longueur || "",
      largeur: adherent.largeur || vehiculeInfo.largeur || pointResto.largeur || "",
      hauteur: adherent.hauteur || vehiculeInfo.hauteur || pointResto.hauteur || "",
      ouverture: adherent.ouverture || adherent.cote_ouverture || pointResto.cote_ouverture || "",
      puissance_electrique: adherent.puissance_electrique ? String(adherent.puissance_electrique) : (pointResto.puissance_electrique || ""),
      plaque_immatriculation: adherent.plaque_immatriculation || vehiculeInfo.immatriculation || "",
      dimensions: adherent.dimensions || "",
      auvent: adherent.auvent || pointResto.auvent || false,
      // Personnel
      personnel: adherent.personnel || adherent.nb_personnel || adherent.staff_base || 1,
      equipe: equipeData,
      // Véhicule personnel
      vehicule_personnel: adherent.vehicule_personnel || "",
      plaque_vp: adherent.plaque_vp || "",
      // Images
      photo_url: adherent.logo || adherent.logo_url || adherent.photo_url || "",
      logo_url: adherent.logo || adherent.logo_url || "",
      menu_items: [],
      // Documents - récupérer depuis l'adhérent (tableau documents)
      doc_identite: docIdentite?.url || adherent.doc_identite || null,
      doc_identite_filename: docIdentite?.nom || adherent.doc_identite_filename || (docIdentite ? "Pièce d'identité" : null),
      doc_assurance_rc: docAssuranceRc?.url || adherent.doc_assurance_rc || null,
      doc_assurance_rc_filename: docAssuranceRc?.nom || adherent.doc_assurance_rc_filename || (docAssuranceRc ? "Assurance RC Pro" : null),
      doc_haccp: docHaccp?.url || adherent.doc_haccp || null,
      doc_haccp_filename: docHaccp?.nom || adherent.doc_haccp_filename || (docHaccp ? "Attestation HACCP" : null),
      doc_assurance_vehicule: docAssuranceVehicule?.url || adherent.doc_assurance_vehicule || null,
      doc_assurance_vehicule_filename: docAssuranceVehicule?.nom || adherent.doc_assurance_vehicule_filename || (docAssuranceVehicule ? "Assurance Véhicule" : null),
    });
    
    // Count imported documents
    const docsImported = [docIdentite, docAssuranceRc, docHaccp, docAssuranceVehicule].filter(Boolean).length;
    
    toast.success(`Données importées depuis "${adherent.nom_food_truck || adherent.nom || 'Adhérent'}" ${docsImported > 0 ? `(${docsImported} documents récupérés)` : ""}`);
  };

  const saveFoodTruck = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Clean the data before sending - convert all to proper types (strings for text fields)
      const cleanedData = {
        nom: String(editingFoodTruck.nom || ""),
        responsable: String(editingFoodTruck.responsable || ""),
        telephone: String(editingFoodTruck.telephone || ""),
        email: String(editingFoodTruck.email || ""),
        specialite: String(editingFoodTruck.specialite || ""),
        type_vehicule: String(editingFoodTruck.type_vehicule || ""),
        taille: String(editingFoodTruck.taille || ""),
        dimensions: String(editingFoodTruck.dimensions || ""),
        ouverture: String(editingFoodTruck.ouverture || ""),
        puissance_electrique: String(editingFoodTruck.puissance_electrique || ""),
        plaque_immatriculation: String(editingFoodTruck.plaque_immatriculation || ""),
        auvent: Boolean(editingFoodTruck.auvent),
        personnel: editingFoodTruck.personnel ? String(editingFoodTruck.personnel) : null,
        employes: Array.isArray(editingFoodTruck.employes) ? editingFoodTruck.employes : [],
        equipe: editingFoodTruck.equipe || [],
        vehicule_perso: Boolean(editingFoodTruck.vehicule_perso),
        vehicule_personnel: String(editingFoodTruck.vehicule_personnel || ""),
        marque_vp: String(editingFoodTruck.marque_vp || ""),
        plaque_vp: String(editingFoodTruck.plaque_vp || ""),
        besoins_electriques: String(editingFoodTruck.besoins_electriques || ""),
        menu_default: String(editingFoodTruck.menu_default || ""),
        menu_items: editingFoodTruck.menu_items || [],
        menu_par_jour: editingFoodTruck.menu_par_jour || null,
        photo_url: String(editingFoodTruck.photo_url || ""),
        logo_url: String(editingFoodTruck.logo_url || ""),
        adherent_id: editingFoodTruck.adherent_id || null,
        // Services proposés
        propose_cafe: Boolean(editingFoodTruck.propose_cafe),
        // Documents
        doc_identite: editingFoodTruck.doc_identite || null,
        doc_identite_filename: editingFoodTruck.doc_identite_filename || null,
        doc_assurance_rc: editingFoodTruck.doc_assurance_rc || null,
        doc_assurance_rc_filename: editingFoodTruck.doc_assurance_rc_filename || null,
        doc_haccp: editingFoodTruck.doc_haccp || null,
        doc_haccp_filename: editingFoodTruck.doc_haccp_filename || null,
        doc_assurance_vehicule: editingFoodTruck.doc_assurance_vehicule || null,
        doc_assurance_vehicule_filename: editingFoodTruck.doc_assurance_vehicule_filename || null,
      };
      
      if (editingFoodTruckIndex !== null) {
        // If we're in the Menus tab, save menu for specific day
        if (activeTab === "menus" && editingFoodTruck.menu_items) {
          // Clean menu items before sending - remove _rawPrix and ensure prix is a valid number
          const cleanedMenuItems = editingFoodTruck.menu_items.map(item => {
            const { _rawPrix, ...rest } = item;
            // Convert prix to number if it's a string with comma
            let prix = rest.prix;
            if (typeof prix === 'string') {
              prix = parseFloat(prix.replace(',', '.')) || 0;
            }
            return { ...rest, prix: prix || 0 };
          });
          
          await axios.put(
            `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${editingFoodTruckIndex}/menu-jour/${selectedMenuJour}`,
            cleanedMenuItems,
            config
          );
          toast.success(`Menu ${selectedMenuJour} mis à jour pour ${editingFoodTruck.nom}`);
        } else {
          // Regular food truck update
          await axios.put(`${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${editingFoodTruckIndex}`, cleanedData, config);
          toast.success("Food truck mis à jour");
        }
      } else {
        // Creating new food truck
        const response = await axios.post(`${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck`, cleanedData, config);
        const newFtId = response.data?.food_truck_id || response.data?.id;
        
        // If we have a pending zone assignment, add the FT to that zone/slot
        if (pendingZoneAssignment && newFtId) {
          try {
            const newSlot = {
              food_truck_id: newFtId,
              food_truck_nom: cleanedData.nom,
              specialite: cleanedData.specialite || "",
              zone: pendingZoneAssignment.zone,
              horaire_debut: 
                pendingZoneAssignment.service === "matin" ? "17:00" : 
                pendingZoneAssignment.service === "midi" ? "11:00" : 
                pendingZoneAssignment.service === "apres_midi" ? "14:00" : 
                pendingZoneAssignment.service === "soir" ? "18:00" : "00:00",
              horaire_fin: 
                pendingZoneAssignment.service === "matin" ? "11:00" : 
                pendingZoneAssignment.service === "midi" ? "14:00" : 
                pendingZoneAssignment.service === "apres_midi" ? "18:00" : 
                pendingZoneAssignment.service === "soir" ? "23:00" : "02:00",
            };
            
            await axios.post(
              `${API_URL}/hec/plannings/${selectedPlanning.id}/slot`,
              {
                date: pendingZoneAssignment.jour,
                service: pendingZoneAssignment.service,
                slot: newSlot
              },
              config
            );
            
            toast.success(`${cleanedData.nom} ajouté à la zone ${pendingZoneAssignment.zone}`);
            setPendingZoneAssignment(null); // Clear the pending assignment
          } catch (slotError) {
            console.error("Error adding to slot:", slotError);
            toast.success("Food truck créé");
            toast.error("Erreur lors de l'ajout à la zone - ajoutez-le manuellement");
          }
        } else {
          toast.success("Food truck ajouté");
        }
      }
      setShowFoodTruckDialog(false);
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error saving food truck:", error);
      // Better error message extraction
      let errorMsg = "Erreur inconnue";
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMsg = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            // Pydantic validation errors
            errorMsg = error.response.data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
          } else {
            errorMsg = JSON.stringify(error.response.data.detail);
          }
        } else {
          errorMsg = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error("Erreur lors de la sauvegarde: " + errorMsg);
    }
  };

  const deleteFoodTruck = async (index) => {
    if (!window.confirm("Supprimer ce food truck ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${index}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Food truck supprimé");
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Delete entire planning
  const handleDeletePlanning = async () => {
    if (!selectedPlanning?.id) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le planning "${selectedPlanning.nom_evenement}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/hec/plannings/${selectedPlanning.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Planning supprimé avec succès");
      setSelectedPlanning(null);
      loadPlannings(); // Reload the list
    } catch (error) {
      console.error("Delete planning error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression du planning");
    }
  };

  // Send complete HEC dossier by email
  const handleSendDossier = async () => {
    if (!selectedPlanning?.id) return;
    if (!dossierForm.email_destinataire) {
      toast.error("Veuillez saisir une adresse email");
      return;
    }
    
    try {
      setSendingDossier(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/send-dossier`,
        {
          ...dossierForm,
          objet: dossierForm.objet || `Dossier Complet HEC - ${selectedPlanning.nom_evenement}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Dossier envoyé avec succès !");
      setShowSendDossierDialog(false);
      setDossierForm({
        email_destinataire: "",
        objet: "",
        message: "",
        include_documents: true,
        include_photos_link: true
      });
    } catch (error) {
      console.error("Send dossier error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du dossier");
    } finally {
      setSendingDossier(false);
    }
  };

  // Download all HEC documents as ZIP (admin function - creates token internally)
  const handleDownloadAllDocs = async () => {
    if (!selectedPlanning?.id) return;
    
    try {
      setDownloadingAllDocs(true);
      const token = localStorage.getItem("token");
      
      // First, generate/get a document token for this planning
      const tokenResponse = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/generate-docs-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Extract token from the link returned
      const docsLink = tokenResponse.data.link;
      const docToken = new URL(docsLink).searchParams.get('token');
      
      if (!docToken) {
        throw new Error("Impossible de générer le token de téléchargement");
      }
      
      // Now download the ZIP
      const response = await axios.get(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/download-all-docs?token=${docToken}`,
        { 
          responseType: 'blob' 
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const safeName = (selectedPlanning.nom_evenement || 'Planning').replace(/[^\w\s\-\.]/g, '').replace(/\s+/g, '_');
      const filename = `Documents_HEC_${safeName}.zip`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Documents téléchargés !");
    } catch (error) {
      console.error("Download all docs error:", error);
      if (error.response?.status === 404) {
        toast.error("Aucun document disponible à télécharger");
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors du téléchargement");
      }
    } finally {
      setDownloadingAllDocs(false);
    }
  };

  // Generate client access link
  const handleGenerateClientLink = async () => {
    if (!selectedPlanning?.id) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/generate-client-access`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const clientLink = response.data.link;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(clientLink);
      toast.success("Lien client copié dans le presse-papiers !", {
        description: `Valide pendant ${response.data.valid_days} jours`
      });
      
    } catch (error) {
      console.error("Generate client link error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération du lien");
    }
  };

  // Synchronize all food trucks with adherent data
  const handleSyncFoodTrucks = async () => {
    if (!selectedPlanning?.id) return;
    
    try {
      setSyncing(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/sync-food-trucks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      if (result.synced > 0) {
        toast.success(`${result.synced}/${result.total} food trucks synchronisés avec les données adhérents (documents, contacts, véhicule...)`);
      } else {
        toast.info("Aucun food truck à synchroniser ou aucune correspondance trouvée avec les adhérents");
      }
      
      // Reload planning to show updated data
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  // Document Migration to MongoDB (for production persistence)
  const handleMigrateDocuments = async () => {
    try {
      setMigratingDocs(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/migrate-documents-to-mongodb`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      setMigrationResult(result);
      
      if (result.migrated_count > 0) {
        toast.success(`${result.migrated_count} document(s) migré(s) vers MongoDB`);
        // Reload planning to show updated URLs
        if (selectedPlanning?.id) {
          loadPlanning(selectedPlanning.id);
        }
      } else if (result.error_count > 0) {
        toast.warning(`Aucun document migré. ${result.error_count} erreur(s) rencontrée(s).`);
      } else {
        toast.info("Aucun document à migrer. Tous les documents sont déjà dans MongoDB.");
      }
    } catch (error) {
      console.error("Migration error:", error);
      toast.error("Erreur lors de la migration: " + (error.response?.data?.detail || error.message));
      setMigrationResult({ status: "error", error: error.message });
    } finally {
      setMigratingDocs(false);
    }
  };

  // Cleanup invalid document references
  const handleCleanupDocuments = async () => {
    try {
      setCleaningDocs(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/cleanup-invalid-documents`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      setCleanupResult(result);
      
      if (result.cleaned_count > 0) {
        toast.success(`${result.cleaned_count} référence(s) invalide(s) nettoyée(s)`);
        // Reload planning to show updated data
        if (selectedPlanning?.id) {
          loadPlanning(selectedPlanning.id);
        }
      } else {
        toast.info("Aucune référence invalide à nettoyer.");
      }
    } catch (error) {
      console.error("Cleanup error:", error);
      toast.error("Erreur lors du nettoyage: " + (error.response?.data?.detail || error.message));
      setCleanupResult({ status: "error", error: error.message });
    } finally {
      setCleaningDocs(false);
    }
  };

  // Cleanup duplicate slots in planning
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  
  const cleanupDuplicateSlots = async () => {
    if (!selectedPlanning?.id) return;
    
    try {
      setCleaningDuplicates(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/cleanup-duplicates`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      if (result.total_duplicates_removed > 0) {
        toast.success(`${result.total_duplicates_removed} doublon(s) supprimé(s)`);
        // Show details
        if (result.details?.length > 0) {
          result.details.forEach(detail => toast.info(detail));
        }
        // Reload planning
        loadPlanning(selectedPlanning.id);
      } else {
        toast.info("Aucun doublon trouvé.");
      }
    } catch (error) {
      console.error("Cleanup duplicates error:", error);
      toast.error("Erreur lors du nettoyage: " + (error.response?.data?.detail || error.message));
    } finally {
      setCleaningDuplicates(false);
    }
  };

  // ==================== ÉQUIPEMENTS CRUD ====================
  
  const saveEquipements = async (updatedEquipements) => {
    if (!selectedPlanning?.id) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/equipements`,
        updatedEquipements,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEquipements(updatedEquipements);
      toast.success("Équipements sauvegardés");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleAddEquipement = () => {
    setEditingEquipement({ 
      id: null, 
      nom: "", 
      type_equipement: TYPES_EQUIPEMENT[0], 
      puissance_electrique: "",
      notes: ""
    });
    setShowEquipementDialog(true);
  };

  const handleEditEquipement = (eq) => {
    setEditingEquipement({ ...eq });
    setShowEquipementDialog(true);
  };

  const handleSaveEquipement = async () => {
    if (!editingEquipement?.nom) {
      toast.error("Le nom est requis");
      return;
    }
    
    let updated;
    if (editingEquipement.id) {
      // Update existing
      updated = equipements.map(eq => eq.id === editingEquipement.id ? editingEquipement : eq);
    } else {
      // Add new
      updated = [...equipements, { ...editingEquipement, id: `eq_${Date.now()}` }];
    }
    
    await saveEquipements(updated);
    setShowEquipementDialog(false);
    setEditingEquipement(null);
  };

  const handleDeleteEquipement = async (eqId) => {
    if (!confirm("Supprimer cet équipement ?")) return;
    const updated = equipements.filter(eq => eq.id !== eqId);
    await saveEquipements(updated);
  };

  // ==================== STAFF BAR CRUD ====================
  
  const saveStaffBar = async (updatedStaffBar) => {
    if (!selectedPlanning?.id) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/staff-bar`,
        updatedStaffBar,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStaffBar(updatedStaffBar);
      toast.success("Staff Bar sauvegardé");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleAddStaffMember = () => {
    setEditingStaffMember({ 
      id: null, 
      nom: "", 
      prenom: "",
      numero_piece_identite: "",
      plaque_immatriculation: "",
      fonction: "",
      telephone: "",
      presence_jeudi: false,
      presence_vendredi: false,
      presence_samedi: false,
      benevole: false,
      cout_journalier: null,
      notes: ""
    });
    setShowStaffDialog(true);
  };

  const handleEditStaffMember = (member) => {
    setEditingStaffMember({ ...member });
    setShowStaffDialog(true);
  };

  const handleSaveStaffMember = async () => {
    if (!editingStaffMember?.nom) {
      toast.error("Le nom est requis");
      return;
    }
    
    let updatedStaff;
    if (editingStaffMember.id) {
      // Update existing
      updatedStaff = staffBar.staff.map(m => m.id === editingStaffMember.id ? editingStaffMember : m);
    } else {
      // Add new
      updatedStaff = [...staffBar.staff, { ...editingStaffMember, id: `staff_${Date.now()}` }];
    }
    
    await saveStaffBar({ ...staffBar, staff: updatedStaff });
    setShowStaffDialog(false);
    setEditingStaffMember(null);
  };

  const handleDeleteStaffMember = async (memberId) => {
    if (!confirm("Supprimer ce membre du staff ?")) return;
    const updatedStaff = staffBar.staff.filter(m => m.id !== memberId);
    await saveStaffBar({ ...staffBar, staff: updatedStaff });
  };

  const handleSaveStaffBarGlobal = async () => {
    await saveStaffBar(staffBar);
  };

  // ===== IMPORT EXCEL STAFF BAR =====
  const handleImportStaffExcel = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingStaffExcel(true);
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        // Parse staff from Excel - simple approach
        // Columns expected: Jour, Nom, Structure, Rôle, Statut, Tarif
        const staffByKey = {};
        
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length < 2) continue;
          
          const jour = String(row[0] || '').trim().toLowerCase();
          const prenom = String(row[1] || '').trim();
          const structure = String(row[2] || '').trim();
          const role = String(row[3] || 'Bar').trim();
          const statut = String(row[4] || '').trim().toLowerCase();
          
          if (!prenom || prenom.toLowerCase() === 'nom') continue;
          
          const key = prenom.toLowerCase() + '_' + structure.toLowerCase();
          const isBenevole = statut.includes('bénévole') || statut.includes('benevole');
          
          if (!staffByKey[key]) {
            staffByKey[key] = {
              id: 'staff_' + Date.now() + '_' + Object.keys(staffByKey).length,
              nom: structure || 'Staff',
              prenom: prenom,
              fonction: role || 'Bar',
              telephone: '',
              presence_jeudi: false,
              presence_vendredi: false,
              presence_samedi: false,
              benevole: isBenevole,
              cout_journalier: null,
              notes: structure ? 'Structure: ' + structure : ''
            };
          }
          
          // Update presence
          if (jour.includes('jeudi')) staffByKey[key].presence_jeudi = true;
          if (jour.includes('vendredi')) staffByKey[key].presence_vendredi = true;
          if (jour.includes('samedi')) staffByKey[key].presence_samedi = true;
          if (isBenevole) staffByKey[key].benevole = true;
        }
        
        const importedStaff = Object.values(staffByKey);
        
        if (importedStaff.length === 0) {
          toast.error("Aucun membre trouvé dans le fichier");
          setImportingStaffExcel(false);
          event.target.value = '';
          return;
        }
        
        // Replace staff with imported data
        const updatedStaffBar = { 
          ...staffBar, 
          staff: importedStaff 
        };
        
        saveStaffBar(updatedStaffBar).then(() => {
          toast.success(importedStaff.length + ' membre(s) importé(s)');
          setImportingStaffExcel(false);
        });
        
      } catch (err) {
        console.error("Erreur parsing:", err);
        toast.error("Erreur lors du parsing du fichier");
        setImportingStaffExcel(false);
      }
      event.target.value = '';
    };
    
    reader.onerror = () => {
      toast.error("Erreur lecture fichier");
      setImportingStaffExcel(false);
      event.target.value = '';
    };
    
    reader.readAsArrayBuffer(file);
  };

  // ===== PLANNING HORAIRE STAFF BAR =====
  const getStaffForHour = (day, hour) => {
    const planningHoraire = staffBar.planning_horaire || {};
    const dayPlanning = planningHoraire[day] || {};
    return dayPlanning[hour] || [];
  };

  const toggleStaffHour = async (staffId, day, hour) => {
    const currentAssignments = getStaffForHour(day, hour);
    const isAssigned = currentAssignments.includes(staffId);
    
    const newPlanningHoraire = { ...staffBar.planning_horaire } || {};
    if (!newPlanningHoraire[day]) newPlanningHoraire[day] = {};
    
    if (isAssigned) {
      newPlanningHoraire[day][hour] = currentAssignments.filter(id => id !== staffId);
    } else {
      newPlanningHoraire[day][hour] = [...currentAssignments, staffId];
    }
    
    const updatedStaffBar = { ...staffBar, planning_horaire: newPlanningHoraire };
    await saveStaffBar(updatedStaffBar);
  };

  const getStaffMemberById = (staffId) => {
    return staffBar.staff?.find(m => m.id === staffId);
  };

  const getStaffMemberName = (staffId) => {
    const member = getStaffMemberById(staffId);
    if (!member) return staffId;
    return member.prenom ? `${member.prenom} ${member.nom}` : member.nom;
  };

  const getStaffHoursForDay = (staffId, day) => {
    const planningHoraire = staffBar.planning_horaire || {};
    const dayPlanning = planningHoraire[day] || {};
    let count = 0;
    Object.values(dayPlanning).forEach(staffList => {
      if (staffList.includes(staffId)) count++;
    });
    return count;
  };

  const getTotalStaffHours = (staffId) => {
    return PLANNING_DAYS.reduce((total, day) => total + getStaffHoursForDay(staffId, day), 0);
  };

  const exportPlanningHoraireExcel = () => {
    if (!staffBar.staff?.length) {
      toast.error("Aucun membre du staff à exporter");
      return;
    }

    const wb = XLSX.utils.book_new();
    
    PLANNING_DAYS.forEach(day => {
      const wsData = [["Heure", ...staffBar.staff.map(m => m.prenom ? `${m.prenom} ${m.nom}` : m.nom)]];
      
      PLANNING_HOURS.forEach(hour => {
        const row = [hour];
        staffBar.staff.forEach(member => {
          const isAssigned = getStaffForHour(day, hour).includes(member.id);
          row.push(isAssigned ? "✓" : "");
        });
        wsData.push(row);
      });
      
      // Add totals row
      const totalsRow = ["TOTAL HEURES"];
      staffBar.staff.forEach(member => {
        totalsRow.push(getStaffHoursForDay(member.id, day) + "h");
      });
      wsData.push(totalsRow);
      
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = [{ wch: 8 }, ...staffBar.staff.map(() => ({ wch: 15 }))];
      XLSX.utils.book_append_sheet(wb, ws, day);
    });
    
    // Summary sheet
    const summaryData = [
      ["RÉCAPITULATIF PLANNING STAFF BAR"],
      [],
      ["Membre", "Fonction", "Jeudi", "Vendredi", "Samedi", "Total"]
    ];
    
    staffBar.staff.forEach(member => {
      summaryData.push([
        member.prenom ? `${member.prenom} ${member.nom}` : member.nom,
        member.fonction || "-",
        getStaffHoursForDay(member.id, "Jeudi") + "h",
        getStaffHoursForDay(member.id, "Vendredi") + "h",
        getStaffHoursForDay(member.id, "Samedi") + "h",
        getTotalStaffHours(member.id) + "h"
      ]);
    });
    
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    summaryWs["!cols"] = [{ wch: 25 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, summaryWs, "Récapitulatif");
    
    const planningTitle = selectedPlanning?.titre || "HEC";
    XLSX.writeFile(wb, `Planning_Horaire_Staff_${planningTitle.replace(/\s+/g, '_')}.xlsx`);
    toast.success("Planning horaire exporté !");
  };

  // Calculate staff costs
  const calculateStaffCosts = () => {
    let totalJours = 0;
    let totalCout = 0;
    let benevoles = 0;
    
    staffBar.staff.forEach(member => {
      const jours = (member.presence_jeudi ? 1 : 0) + (member.presence_vendredi ? 1 : 0) + (member.presence_samedi ? 1 : 0);
      totalJours += jours;
      
      if (member.benevole) {
        benevoles++;
      } else if (member.cout_journalier) {
        totalCout += jours * member.cout_journalier;
      }
    });
    
    if (staffBar.cout_global) {
      totalCout += staffBar.cout_global;
    }
    
    return { totalJours, totalCout, staffCount: staffBar.staff.length, benevoles };
  };

  // ===== COFFEE/DESSERT FUNCTIONS =====
  const saveCoffeeDesserts = async (newList) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/hec/plannings/${selectedPlanning.id}/coffee-desserts`, newList, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoffeeDesserts(newList);
      toast.success("Coffee & Dessert sauvegardé");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleAddCoffeeDessert = () => {
    setEditingCoffeeDessert({
      nom: "",
      type_service: "Coffee & Dessert",
      responsable: "",
      telephone: "",
      email: "",
      presence_jeudi: false,
      presence_vendredi: false,
      presence_samedi: false,
      horaire_jeudi: "À préciser",
      horaire_vendredi: "À préciser",
      horaire_samedi: "À préciser",
      specialites: "",
      besoins_electriques: "",
      notes: ""
    });
    setShowCoffeeDessertDialog(true);
  };

  const handleEditCoffeeDessert = (item, index) => {
    setEditingCoffeeDessert({ ...item, _index: index });
    setShowCoffeeDessertDialog(true);
  };

  const handleSaveCoffeeDessert = async () => {
    if (!editingCoffeeDessert?.nom) {
      toast.error("Le nom est requis");
      return;
    }
    
    const { _index, ...itemData } = editingCoffeeDessert;
    let newList;
    
    if (_index !== undefined) {
      // Edit existing
      newList = [...coffeeDesserts];
      newList[_index] = { ...newList[_index], ...itemData };
    } else {
      // Add new
      newList = [...coffeeDesserts, { ...itemData, id: crypto.randomUUID() }];
    }
    
    await saveCoffeeDesserts(newList);
    setShowCoffeeDessertDialog(false);
    setEditingCoffeeDessert(null);
  };

  const handleDeleteCoffeeDessert = async (index) => {
    if (!confirm("Supprimer ce food truck Coffee/Dessert ?")) return;
    const newList = coffeeDesserts.filter((_, i) => i !== index);
    await saveCoffeeDesserts(newList);
  };

  // Count coffee/dessert presence by day
  const getCoffeeDessertStats = () => {
    const jeudi = coffeeDesserts.filter(cd => cd.presence_jeudi).length;
    const vendredi = coffeeDesserts.filter(cd => cd.presence_vendredi).length;
    const samedi = coffeeDesserts.filter(cd => cd.presence_samedi).length;
    return { total: coffeeDesserts.length, jeudi, vendredi, samedi };
  };

  // Bar CRUD with prices
  const openEditBar = (bar, index) => {
    const boissons = bar.boissons || [];
    // Convert old format (string array) to new format (object with prix)
    const formattedBoissons = boissons.map(b => {
      if (typeof b === 'string') return { nom: b, prix: 0 };
      return b;
    });
    setEditingBar({ ...bar, boissons: formattedBoissons });
    setEditingBarIndex(index);
    setShowBarDialog(true);
  };

  const openAddBar = () => {
    setEditingBar({ nom: "", type: "mobile", responsable: "", telephone: "", boissons: [], notes: "" });
    setEditingBarIndex(null);
    setShowBarDialog(true);
  };

  const saveBar = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Clean boissons before sending - remove _rawPrix and ensure prix is a valid number
      const cleanedBar = {
        ...editingBar,
        boissons: (editingBar.boissons || []).map(b => {
          const { _rawPrix, ...rest } = b;
          let prix = rest.prix;
          if (typeof prix === 'string') {
            prix = parseFloat(prix.replace(',', '.')) || 0;
          }
          return { ...rest, prix: prix || 0 };
        })
      };
      
      if (editingBarIndex !== null) {
        await axios.put(`${API_URL}/hec/plannings/${selectedPlanning.id}/bar/${editingBarIndex}`, cleanedBar, config);
        toast.success("Bar mis à jour");
      } else {
        await axios.post(`${API_URL}/hec/plannings/${selectedPlanning.id}/bar`, cleanedBar, config);
        toast.success("Bar ajouté");
      }
      setShowBarDialog(false);
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error saving bar:", error);
      toast.error("Erreur lors de la sauvegarde: " + (error.response?.data?.detail || error.message));
    }
  };

  const deleteBar = async (index) => {
    if (!window.confirm("Supprimer ce bar ?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/hec/plannings/${selectedPlanning.id}/bar/${index}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bar supprimé");
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Manage bars for evening service
  const [showBarsSoirDialog, setShowBarsSoirDialog] = useState(false);
  const [barsSoirSelection, setBarsSoirSelection] = useState([]);

  const openBarsSoirManager = () => {
    // Get currently assigned bars for the selected day
    const currentBars = getCurrentDaySlots().bars_soir || [];
    const assignedBarNames = currentBars.map(b => b.bar_nom);
    setBarsSoirSelection(assignedBarNames);
    setShowBarsSoirDialog(true);
  };

  const toggleBarSoirSelection = (barName) => {
    if (barsSoirSelection.includes(barName)) {
      setBarsSoirSelection(barsSoirSelection.filter(n => n !== barName));
    } else {
      setBarsSoirSelection([...barsSoirSelection, barName]);
    }
  };

  const saveBarsSoir = async () => {
    try {
      if (!selectedDay) {
        toast.error("Veuillez sélectionner un jour");
        return;
      }
      
      // Build the bars_soir array from selection
      const bars_soir = barsSoirSelection.map(barName => {
        const bar = selectedPlanning.bars.find(b => b.nom === barName);
        return {
          bar_nom: barName,
          bar_index: selectedPlanning.bars.indexOf(bar),
          boissons_actives: bar?.boissons?.map(b => b.nom) || []
        };
      });

      // Update the planning day
      const response = await axios.put(`${API_URL}/hec/plannings/${selectedPlanning.id}/day/${selectedDay}/bars-soir`, {
        bars_soir
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });

      if (response.data.status === "success") {
        toast.success("Bars du soir mis à jour");
        setShowBarsSoirDialog(false);
        loadPlanning(selectedPlanning.id);
      }
    } catch (error) {
      console.error("Error saving bars soir:", error);
      toast.error("Erreur lors de la mise à jour: " + (error.response?.data?.detail || error.message));
    }
  };

  // Sync bar drinks from definitions to all planning slots
  const syncBarDrinks = async () => {
    if (!selectedPlanning) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/sync-bar-drinks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Boissons synchronisées !");
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error syncing bar drinks:", error);
      toast.error("Erreur lors de la synchronisation: " + (error.response?.data?.detail || error.message));
    }
  };

  // Add/remove drink from bar
  const addDrinkToBar = () => {
    setEditingBar({ ...editingBar, boissons: [...(editingBar.boissons || []), { nom: "", prix: 0 }] });
  };

  const removeDrinkFromBar = (index) => {
    const newBoissons = editingBar.boissons.filter((_, i) => i !== index);
    setEditingBar({ ...editingBar, boissons: newBoissons });
  };

  const updateDrink = (index, field, value) => {
    const newBoissons = [...editingBar.boissons];
    if (field === 'prix') {
      // Support comma as decimal separator (French format)
      const normalizedValue = String(value).replace(',', '.');
      // Allow intermediate states like "3," or "3." during typing
      if (normalizedValue === '' || normalizedValue === '.' || normalizedValue.endsWith('.')) {
        newBoissons[index] = { ...newBoissons[index], [field]: normalizedValue === '' ? '' : normalizedValue, _rawPrix: value };
      } else {
        const parsed = parseFloat(normalizedValue);
        newBoissons[index] = { ...newBoissons[index], [field]: isNaN(parsed) ? 0 : parsed, _rawPrix: undefined };
      }
    } else {
      newBoissons[index] = { ...newBoissons[index], [field]: value };
    }
    setEditingBar({ ...editingBar, boissons: newBoissons });
  };

  // Document upload functions
  const handleDocumentUpload = async (file, docType) => {
    if (!file || editingFoodTruckIndex === null) return;
    
    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${editingFoodTruckIndex}/upload-document?doc_type=${docType}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
      );
      
      // Update local state with the new document info
      setEditingFoodTruck(prev => ({
        ...prev,
        [`doc_${docType}`]: response.data.url,
        [`doc_${docType}_filename`]: response.data.filename
      }));
      
      toast.success(`Document "${response.data.filename}" uploadé avec succès`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload du document");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDocumentDelete = async (docType) => {
    if (editingFoodTruckIndex === null) return;
    if (!window.confirm("Supprimer ce document ?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${editingFoodTruckIndex}/document/${docType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setEditingFoodTruck(prev => ({
        ...prev,
        [`doc_${docType}`]: null,
        [`doc_${docType}_filename`]: null
      }));
      
      toast.success("Document supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const downloadDocument = (docUrl, filename) => {
    if (!docUrl) {
      toast.error("Document non disponible");
      return;
    }
    
    // Get the base URL without /api suffix for proper URL construction
    const BASE_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token");
    
    // Check if it's already a full URL or a relative path
    let fullUrl;
    if (docUrl.startsWith('http://') || docUrl.startsWith('https://')) {
      // Already a full URL
      fullUrl = docUrl;
    } else if (docUrl.startsWith('/')) {
      // Relative path - append to BASE_URL directly
      fullUrl = `${BASE_URL}${docUrl}`;
    } else {
      // Assume it needs the full path
      fullUrl = `${BASE_URL}/api/files/${docUrl}`;
    }
    
    // Add token for HEC documents (which require auth)
    if (fullUrl.includes('/hec/documents/') && token) {
      fullUrl += `?token=${token}`;
    }
    
    // Fichiers de la base : URL signée demandée au serveur (accès contrôlé).
    if (fullUrl.includes('/api/files/')) {
      ouvrirFichier(fullUrl)
        .then(() => toast.success(`Ouverture de ${filename || 'document'}...`))
        .catch(() => toast.error("Document indisponible ou accès refusé"));
      return;
    }
    
    console.log("Opening document:", fullUrl);
    
    // Use a link element for more reliable opening (avoids popup blockers)
    const link = document.createElement('a');
    link.href = fullUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Ouverture de ${filename || 'document'}...`);
  };

  // Version history functions
  const loadVersionHistory = async (ftIndex) => {
    if (!selectedPlanning) return;
    
    try {
      setLoadingVersions(true);
      const response = await axios.get(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${ftIndex}/versions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVersionsData(response.data);
      setShowVersionsDialog(true);
    } catch (error) {
      console.error("Error loading versions:", error);
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoadingVersions(false);
    }
  };

  const restoreVersion = async (versionId) => {
    if (!selectedPlanning || editingFoodTruckIndex === null) return;
    
    if (!window.confirm("Êtes-vous sûr de vouloir restaurer cette version ? Les données actuelles seront sauvegardées avant la restauration.")) {
      return;
    }
    
    try {
      setRestoringVersion(true);
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${editingFoodTruckIndex}/restore/${versionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowVersionsDialog(false);
      setShowFoodTruckDialog(false);
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error restoring version:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la restauration");
    } finally {
      setRestoringVersion(false);
    }
  };

  // Add/remove menu item from food truck
  const addMenuItemToFoodTruck = () => {
    setEditingFoodTruck({ 
      ...editingFoodTruck, 
      menu_items: [...(editingFoodTruck.menu_items || []), { nom: "", description: "", prix: 0, photo_url: "" }] 
    });
  };

  const removeMenuItemFromFoodTruck = (index) => {
    const newItems = editingFoodTruck.menu_items.filter((_, i) => i !== index);
    setEditingFoodTruck({ ...editingFoodTruck, menu_items: newItems });
  };

  const updateMenuItem = (index, field, value) => {
    const newItems = [...editingFoodTruck.menu_items];
    if (field === 'prix') {
      // Support comma as decimal separator (French format)
      // Keep the raw input for display during typing, convert only for storage
      const normalizedValue = String(value).replace(',', '.');
      // Allow intermediate states like "3," or "3." during typing
      if (normalizedValue === '' || normalizedValue === '.' || normalizedValue.endsWith('.')) {
        // Store raw value temporarily to allow typing decimals
        newItems[index] = { ...newItems[index], [field]: normalizedValue === '' ? '' : normalizedValue, _rawPrix: value };
      } else {
        const parsed = parseFloat(normalizedValue);
        newItems[index] = { ...newItems[index], [field]: isNaN(parsed) ? 0 : parsed, _rawPrix: undefined };
      }
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setEditingFoodTruck({ ...editingFoodTruck, menu_items: newItems });
  };

  // Menu editing
  const openMenuDialog = (slot, date, creneau, index) => {
    setEditingSlot({ ...slot, date, creneau, index });
    setShowMenuDialog(true);
  };

  const saveMenu = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_URL}/hec/plannings/${selectedPlanning.id}/slot-menu`, null, {
        params: {
          date: editingSlot.date,
          creneau: editingSlot.creneau,
          slot_index: editingSlot.index,
          menu: editingSlot.menu_du_jour || ""
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Menu mis à jour");
      setShowMenuDialog(false);
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error saving menu:", error);
      toast.error("Erreur lors de la sauvegarde: " + (error.response?.data?.detail || error.message));
    }
  };

  // Download menus template
  const downloadMenusTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/hec/menus-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'menus_hec_import.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Template téléchargé !");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  // Export menus for client
  const exportMenusForClient = async () => {
    if (!selectedPlanning?.id) {
      toast.error("Aucun planning sélectionné");
      return;
    }
    
    setExportingClientMenus(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/export-menus-client`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Menus_HEC_Export_Client_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export client téléchargé !");
    } catch (error) {
      console.error("Error exporting menus:", error);
      toast.error("Erreur lors de l'export");
    } finally {
      setExportingClientMenus(false);
    }
  };

  // Import menus from Excel
  const handleMenusImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingMenus(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/import-menus`,
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}` 
          } 
        }
      );
      
      toast.success(response.data.message || "Menus importés avec succès !");
      loadPlanning(selectedPlanning.id);
    } catch (error) {
      console.error("Error importing menus:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'import des menus");
    } finally {
      setImportingMenus(false);
      event.target.value = ''; // Reset file input
    }
  };

  // Get menu for a specific day (new structure: Jeudi, Vendredi, Samedi Midi, Samedi Soir)
  const getMenuForDay = (foodTruck, jour) => {
    if (!foodTruck) return [];
    
    const menuParJour = foodTruck.menu_par_jour;
    if (menuParJour) {
      // Si menu spécifique pour ce jour, l'utiliser
      if (menuParJour[jour] && menuParJour[jour].length > 0) {
        return menuParJour[jour];
      }
    }
    
    // Fallback: utiliser menu_items ou menu
    return foodTruck.menu_items || foodTruck.menu || [];
  };

  // Stats
  const getStats = () => {
    if (!selectedPlanning) return {};
    const foodTruckCount = selectedPlanning.food_trucks?.length || 0;
    const barCount = selectedPlanning.bars?.length || 0;
    const daysCount = selectedPlanning.planning_days?.length || 0;
    let totalMidi = 0, totalSoir = 0, menusRemplis = 0, menusTotal = 0;
    const zonesUsed = new Set();
    
    selectedPlanning.planning_days?.forEach(day => {
      totalMidi += day.midi?.length || 0;
      totalSoir += day.soir?.length || 0;
      day.midi?.forEach(s => { zonesUsed.add(s.zone); menusTotal++; if (s.menu_du_jour) menusRemplis++; });
      day.soir?.forEach(s => { zonesUsed.add(s.zone); menusTotal++; if (s.menu_du_jour) menusRemplis++; });
    });
    
    return { foodTruckCount, barCount, daysCount, totalMidi, totalSoir, menusRemplis, menusTotal, zonesCount: zonesUsed.size };
  };

  // Export PDF function
  const handleExportPDF = async () => {
    if (!selectedPlanning?.id) {
      toast.error("Aucun planning sélectionné");
      return;
    }
    
    setExportingPDF(true);
    try {
      const response = await axios.get(
        `${API_URL}/hec/plannings/${selectedPlanning.id}/export-pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Planning_HEC_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF exporté avec succès !");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  // Export PDF for selected day (frontend generation)
  const exportDayToPDF = async (date) => {
    if (!selectedPlanning) return;
    
    setExportingPDF(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      const dayData = selectedPlanning.planning_days?.find(d => d.date === date);
      if (!dayData) {
        toast.error("Aucune donnée pour ce jour");
        return;
      }

      const dateFormatted = format(parseISO(date), "EEEE d MMMM yyyy", { locale: fr });

      // Header
      doc.setFillColor(122, 45, 45);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Planning HEC - Food Truck United", margin, 14);
      doc.setFontSize(12);
      doc.text(dateFormatted.charAt(0).toUpperCase() + dateFormatted.slice(1), margin, 24);
      
      yPos = 40;

      // MIDI Section
      doc.setTextColor(0, 0, 0);
      doc.setFillColor(255, 251, 235);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 10, "F");
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(180, 83, 9);
      doc.text("Service du Midi", margin + 5, yPos + 7);
      yPos += 15;

      // Group midi slots by zone
      const midiByZone = {};
      (dayData.midi || []).forEach(slot => {
        const zone = slot.zone || "Non assigné";
        if (!midiByZone[zone]) midiByZone[zone] = [];
        midiByZone[zone].push(slot);
      });

      doc.setFontSize(10);
      Object.entries(midiByZone).forEach(([zone, slots]) => {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }
        
        // Zone header
        doc.setFont("helvetica", "bold");
        doc.setTextColor(60, 60, 60);
        doc.text(`Zone ${zone}`, margin + 2, yPos);
        yPos += 5;
        
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        slots.forEach(slot => {
          const ftInfo = selectedPlanning.food_trucks?.find(ft => ft.id === slot.food_truck_id);
          const line = `• ${slot.food_truck_nom} - ${slot.specialite || ""}`;
          doc.text(line, margin + 5, yPos);
          yPos += 5;
          
          if (slot.menu_du_jour) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(`   Menu: ${slot.menu_du_jour.substring(0, 70)}`, margin + 5, yPos);
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            yPos += 4;
          }
        });
        yPos += 5;
      });

      // SOIR Section
      yPos += 5;
      if (yPos > pageHeight - 60) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFillColor(238, 242, 255);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 10, "F");
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(67, 56, 202);
      doc.text("Service du Soir - Hall d'honneur", margin + 5, yPos + 7);
      yPos += 15;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);

      (dayData.soir || []).forEach(slot => {
        if (yPos > pageHeight - 30) {
          doc.addPage();
          yPos = margin;
        }
        
        const line = `• ${slot.food_truck_nom} - ${slot.specialite || ""}`;
        doc.text(line, margin + 5, yPos);
        yPos += 5;
        
        if (slot.menu_du_jour) {
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(`   Menu: ${slot.menu_du_jour.substring(0, 70)}`, margin + 5, yPos);
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          yPos += 4;
        }
      });

      // Bars section
      if (dayData.bars_soir?.length > 0) {
        yPos += 10;
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.setFillColor(254, 243, 199);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 8, "F");
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(180, 83, 9);
        doc.text("Bars du Soir", margin + 5, yPos + 6);
        yPos += 12;

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);
        dayData.bars_soir.forEach(bar => {
          doc.text(`• ${bar.bar_nom}`, margin + 5, yPos);
          yPos += 5;
          // Get drinks from bar definition
          const barDefinition = selectedPlanning?.bars?.find(b => b.nom === bar.bar_nom);
          const boissons = barDefinition?.boissons?.map(b => b.nom) || bar.boissons_actives || [];
          if (boissons.length > 0) {
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(`   Boissons: ${boissons.join(", ")}`, margin + 5, yPos);
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            yPos += 4;
          }
        });
      }

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Généré le ${format(new Date(), "dd/MM/yyyy à HH:mm", { locale: fr })}`, margin, pageHeight - 10);
      doc.text(`${selectedPlanning.nom_evenement}`, pageWidth - margin, pageHeight - 10, { align: "right" });

      // Save
      const filename = `Planning_HEC_${format(parseISO(date), "yyyy-MM-dd")}.pdf`;
      doc.save(filename);
      toast.success(`PDF "${filename}" téléchargé !`);
    } catch (error) {
      console.error("Error exporting day PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  // Export HEC Planning to Excel
  const exportToExcel = () => {
    if (!selectedPlanning) {
      toast.error("Aucun planning sélectionné");
      return;
    }
    
    try {
      const wb = XLSX.utils.book_new();
      
      // ==================== FEUILLE 1: PLANNING PAR JOUR ====================
      const planningRows = [];
      planningRows.push(["Date", "Service", "Zone", "Food Truck", "Spécialité", "Horaires"]);
      
      selectedPlanning.planning_days?.forEach(day => {
        const dateFormatted = format(parseISO(day.date), "EEEE d MMMM yyyy", { locale: fr });
        
        // Midi slots
        (day.midi || []).forEach(slot => {
          planningRows.push([
            dateFormatted,
            "Midi",
            slot.zone || "-",
            slot.food_truck_nom,
            slot.specialite || "-",
            slot.horaires || "10:00-16:00"
          ]);
        });
        
        // Soir slots
        (day.soir || []).forEach(slot => {
          planningRows.push([
            dateFormatted,
            "Soir",
            "Hall d'honneur",
            slot.food_truck_nom,
            slot.specialite || "-",
            slot.horaires || "18:00-23:00"
          ]);
        });
      });
      
      const wsPlanning = XLSX.utils.aoa_to_sheet(planningRows);
      wsPlanning["!cols"] = [
        { wch: 30 }, { wch: 10 }, { wch: 18 }, { wch: 25 }, { wch: 20 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(wb, wsPlanning, "Planning");
      
      // ==================== FEUILLE 2: INFOS FOOD TRUCKS COMPLÈTES ====================
      const ftRows = [];
      ftRows.push([
        "Food Truck", "Spécialité", "Responsable", "Contact Modifié", "Téléphone", "Email",
        "Type Véhicule", "Longueur (m)", "Largeur (m)", "Hauteur (m)", "Plaque Immat.", "Puissance Élec.", "Auvent",
        "Véhicule Personnel", "Plaque VP",
        "ID ✓", "RC Pro ✓", "HACCP ✓", "Assur. Véh. ✓",
        "Participe Samedi Soir", "Notes"
      ]);
      
      (selectedPlanning.food_trucks || []).forEach(ft => {
        ftRows.push([
          ft.name || ft.nom_food_truck || "-",
          ft.specialite || "-",
          ft.responsable || "-",
          ft.contact_modifie || ft.nom_contact || "-",
          ft.telephone || "-",
          ft.email || "-",
          ft.type_vehicule || "-",
          ft.longueur || "-",
          ft.largeur || "-",
          ft.hauteur || "-",
          ft.plaque_immatriculation || "-",
          ft.puissance_electrique || "-",
          ft.auvent ? "Oui" : "Non",
          ft.vehicule_personnel || "-",
          ft.plaque_vp || "-",
          ft.doc_identite ? "✓" : "✗",
          ft.doc_assurance_rc ? "✓" : "✗",
          ft.doc_haccp ? "✓" : "✗",
          ft.doc_assurance_vehicule ? "✓" : "✗",
          ft.participe_samedi_soir ? "Oui" : "Non",
          ft.notes || "-"
        ]);
      });
      
      const wsFT = XLSX.utils.aoa_to_sheet(ftRows);
      wsFT["!cols"] = [
        { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 25 },
        { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 8 },
        { wch: 18 }, { wch: 12 },
        { wch: 6 }, { wch: 8 }, { wch: 8 }, { wch: 10 },
        { wch: 12 }, { wch: 30 }
      ];
      XLSX.utils.book_append_sheet(wb, wsFT, "Infos Food Trucks");
      
      // ==================== FEUILLE 3: ÉQUIPES / EMPLOYÉS ====================
      const equipesRows = [];
      equipesRows.push(["Food Truck", "Nom", "Prénom", "N° Pièce d'identité", "Plaque Immat.", "Fonction", "Téléphone"]);
      
      (selectedPlanning.food_trucks || []).forEach(ft => {
        const ftName = ft.name || ft.nom_food_truck || "N/A";
        const employes = ft.equipe || ft.employes || ft.employees || [];
        
        if (employes.length === 0) {
          equipesRows.push([ftName, "(Aucun employé)", "-", "-", "-", "-", "-"]);
        } else {
          employes.forEach(emp => {
            equipesRows.push([
              ftName,
              emp.nom || "-",
              emp.prenom || "-",
              emp.numero_piece_identite || "-",
              emp.plaque_immatriculation || "-",
              emp.fonction || emp.role || "-",
              emp.telephone || "-"
            ]);
          });
        }
      });
      
      const wsEquipes = XLSX.utils.aoa_to_sheet(equipesRows);
      wsEquipes["!cols"] = [{ wch: 20 }, { wch: 18 }, { wch: 15 }, { wch: 20 }, { wch: 14 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsEquipes, "Équipes FT");
      
      // ==================== FEUILLE 4: MENUS PAR FOOD TRUCK ====================
      const menusRows = [];
      menusRows.push(["Food Truck", "Jour", "Type Menu", "Plat", "Prix (€)", "Description", "Allergènes", "Halal"]);
      
      (selectedPlanning.food_trucks || []).forEach(ft => {
        const ftName = ft.name || ft.nom_food_truck || "N/A";
        const menuParJour = ft.menu_par_jour || {};
        
        // Menu Standard (Jeudi, Vendredi, Samedi Midi)
        ["Jeudi", "Vendredi", "Samedi"].forEach(jour => {
          const menuJour = menuParJour[jour] || ft.menu_items || [];
          if (menuJour.length > 0) {
            menuJour.forEach(item => {
              menusRows.push([
                ftName,
                jour + " Midi",
                "Standard",
                item.nom || "-",
                item.prix || "-",
                item.description || "-",
                item.allergenes || "-",
                item.halal ? "Oui" : "Non"
              ]);
            });
          }
        });
        
        // Menu Samedi Soir
        const menuSamediSoir = ft.menu_samedi_soir || menuParJour["Samedi Soir"] || [];
        if (menuSamediSoir.length > 0) {
          menuSamediSoir.forEach(item => {
            menusRows.push([
              ftName,
              "Samedi Soir",
              "Soirée",
              item.nom || "-",
              "Inclus",
              item.description || "-",
              item.allergenes || "-",
              item.halal ? "Oui" : "Non"
            ]);
          });
        }
      });
      
      if (menusRows.length > 1) {
        const wsMenus = XLSX.utils.aoa_to_sheet(menusRows);
        wsMenus["!cols"] = [
          { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 25 }, { wch: 10 }, { wch: 35 }, { wch: 25 }, { wch: 8 }
        ];
        XLSX.utils.book_append_sheet(wb, wsMenus, "Menus");
      }
      
      // ==================== FEUILLE 5: BARS ====================
      const barsRows = [];
      barsRows.push(["Nom Bar", "Type", "Responsable", "Téléphone", "Notes"]);
      
      (selectedPlanning.bars || []).forEach(bar => {
        barsRows.push([
          bar.nom || "-",
          bar.type || "-",
          bar.responsable || "-",
          bar.telephone || "-",
          bar.notes || "-"
        ]);
      });
      
      if (barsRows.length > 1) {
        const wsBars = XLSX.utils.aoa_to_sheet(barsRows);
        wsBars["!cols"] = [{ wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 30 }];
        XLSX.utils.book_append_sheet(wb, wsBars, "Bars");
      }
      
      // ==================== FEUILLE 6: AFFECTATIONS BARS SOIR ====================
      const barsSoirRows = [];
      barsSoirRows.push(["Date", "Bar", "Boissons"]);
      
      selectedPlanning.planning_days?.forEach(day => {
        const dateFormatted = format(parseISO(day.date), "EEEE d MMMM", { locale: fr });
        
        (day.bars_soir || []).forEach(bar => {
          // Get drinks from bar definition
          const barDefinition = selectedPlanning?.bars?.find(b => b.nom === bar.bar_nom);
          const boissons = barDefinition?.boissons?.map(b => b.nom) || bar.boissons_actives || [];
          barsSoirRows.push([
            dateFormatted,
            bar.bar_nom,
            boissons.join(", ")
          ]);
        });
      });
      
      if (barsSoirRows.length > 1) {
        const wsBarsSoir = XLSX.utils.aoa_to_sheet(barsSoirRows);
        wsBarsSoir["!cols"] = [{ wch: 25 }, { wch: 20 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(wb, wsBarsSoir, "Bars Soir");
      }
      
      // ==================== FEUILLE 7: PRÉSENCES PAR JOUR ====================
      const presenceRows = [];
      presenceRows.push(["Food Truck", "Jeudi Midi", "Vendredi Midi", "Samedi Midi", "Samedi Soir"]);
      
      (selectedPlanning.food_trucks || []).forEach(ft => {
        const ftName = ft.name || ft.nom_food_truck || "N/A";
        
        // Check presence in each day/service
        let jeudiMidi = "Non";
        let vendrediMidi = "Non";
        let samediMidi = "Non";
        let samediSoir = ft.participe_samedi_soir ? "Oui" : "Non";
        
        selectedPlanning.planning_days?.forEach(day => {
          const dayName = format(parseISO(day.date), "EEEE", { locale: fr }).toLowerCase();
          const midiSlots = day.midi || [];
          const soirSlots = day.soir || [];
          
          const isInMidi = midiSlots.some(s => s.food_truck_nom === ftName || s.food_truck_id === ft.id);
          const isInSoir = soirSlots.some(s => s.food_truck_nom === ftName || s.food_truck_id === ft.id);
          
          if (dayName.includes("jeudi") && isInMidi) jeudiMidi = "Oui";
          if (dayName.includes("vendredi") && isInMidi) vendrediMidi = "Oui";
          if (dayName.includes("samedi") && isInMidi) samediMidi = "Oui";
          if (dayName.includes("samedi") && isInSoir) samediSoir = "Oui";
        });
        
        presenceRows.push([ftName, jeudiMidi, vendrediMidi, samediMidi, samediSoir]);
      });
      
      const wsPresence = XLSX.utils.aoa_to_sheet(presenceRows);
      wsPresence["!cols"] = [{ wch: 25 }, { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsPresence, "Présences");
      
      // ==================== FEUILLE 8: ÉQUIPEMENTS ====================
      const equipementsRows = [];
      equipementsRows.push(["Nom", "Type", "Puissance Électrique", "Notes"]);
      
      (equipements || []).forEach(eq => {
        equipementsRows.push([
          eq.nom || "-",
          eq.type_equipement || "-",
          eq.puissance_electrique || "-",
          eq.notes || "-"
        ]);
      });
      
      if (equipementsRows.length > 1) {
        const wsEquipements = XLSX.utils.aoa_to_sheet(equipementsRows);
        wsEquipements["!cols"] = [{ wch: 25 }, { wch: 20 }, { wch: 18 }, { wch: 40 }];
        XLSX.utils.book_append_sheet(wb, wsEquipements, "Équipements");
      }
      
      // ==================== FEUILLE 9: STAFF BAR ====================
      const staffRows = [];
      staffRows.push(["Nom", "Prénom", "N° Pièce d'identité", "Plaque Immat.", "Fonction", "Téléphone", "Jeudi", "Vendredi", "Samedi", "Statut", "Coût/Jour (€)", "Total (€)"]);
      
      let totalStaffCout = 0;
      let totalBenevoles = 0;
      (staffBar?.staff || []).forEach(member => {
        const jours = (member.presence_jeudi ? 1 : 0) + (member.presence_vendredi ? 1 : 0) + (member.presence_samedi ? 1 : 0);
        const coutTotal = member.benevole ? 0 : (member.cout_journalier ? jours * member.cout_journalier : 0);
        if (!member.benevole) {
          totalStaffCout += coutTotal;
        } else {
          totalBenevoles++;
        }
        
        staffRows.push([
          member.nom || "-",
          member.prenom || "-",
          member.numero_piece_identite || "-",
          member.plaque_immatriculation || "-",
          member.fonction || "-",
          member.telephone || "-",
          member.presence_jeudi ? "✓" : "",
          member.presence_vendredi ? "✓" : "",
          member.presence_samedi ? "✓" : "",
          member.benevole ? "Bénévole" : "Salarié",
          member.benevole ? "Gratuit" : (member.cout_journalier || "-"),
          member.benevole ? "0" : (coutTotal > 0 ? coutTotal : "-")
        ]);
      });
      
      // Add totals row
      if (staffBar?.staff?.length > 0) {
        staffRows.push([]);
        if (totalBenevoles > 0) {
          staffRows.push(["", "", "", "", "", "", "", "", "", `Bénévoles: ${totalBenevoles}`, "", ""]);
        }
        staffRows.push(["", "", "", "", "", "", "", "", "", "Sous-total Staff:", "", totalStaffCout]);
        if (staffBar.cout_global) {
          staffRows.push(["", "", "", "", "", "", "", "", "", "Coût Global Additionnel:", "", staffBar.cout_global]);
          staffRows.push(["", "", "", "", "", "", "", "", "", "TOTAL:", "", totalStaffCout + staffBar.cout_global]);
        }
      }
      
      if (staffRows.length > 1) {
        const wsStaff = XLSX.utils.aoa_to_sheet(staffRows);
        wsStaff["!cols"] = [{ wch: 18 }, { wch: 15 }, { wch: 20 }, { wch: 14 }, { wch: 15 }, { wch: 15 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, wsStaff, "Staff Bar");
      }
      
      // Save file
      const filename = `Planning_HEC_${selectedPlanning.nom_evenement?.replace(/\s+/g, "_") || "Export"}_${format(new Date(), "yyyyMMdd")}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(`Excel "${filename}" téléchargé avec ${selectedPlanning.food_trucks?.length || 0} food trucks !`);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Erreur lors de l'export Excel: " + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className="space-y-6" data-testid="hec-planning-page">
      {/* Client Read-Only Banner */}
      {isClient && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Eye className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-800 font-medium">Mode consultation</p>
            <p className="text-blue-600 text-sm">Vous consultez le Planning HEC en lecture seule</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-secondary flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary" />
            Planning HEC
          </h1>
          <p className="text-muted-foreground mt-1">
            {isClient ? "Consultation de l'événement HEC" : "Gestion complète de l'événement HEC"}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadPlannings} className="border-[#E5E0DC]">
              <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
            </Button>
            <Button onClick={handleSeedFromExcel} disabled={seeding} className="bg-primary hover:bg-primary-hover">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {plannings.length > 0 ? "Réimporter Excel" : "Importer depuis Excel"}
            </Button>
          </div>
        )}
        {!canEdit && (
          <Button variant="outline" onClick={loadPlannings} className="border-[#E5E0DC]">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        )}
      </div>

      {plannings.length === 0 ? (
        <Card className="border-[#E5E0DC]">
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold text-secondary mb-2">Aucun planning HEC</h3>
            <p className="text-muted-foreground mb-4">Importez les données depuis le fichier Excel pour commencer</p>
            <Button onClick={handleSeedFromExcel} disabled={seeding} className="bg-primary hover:bg-primary-hover">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Importer le planning
            </Button>
          </CardContent>
        </Card>
      ) : selectedPlanning ? (
        <>
          {/* Event Info Banner */}
          <Card className="border-[#E5E0DC] bg-gradient-to-r from-primary/5 via-white to-primary/5 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-secondary">{selectedPlanning.nom_evenement}</h2>
                    <div className="flex flex-wrap items-center gap-4 mt-1 text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="w-4 h-4" />{selectedPlanning.client}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{format(parseISO(selectedPlanning.date_debut), "d", { locale: fr })} - {format(parseISO(selectedPlanning.date_fin), "d MMMM yyyy", { locale: fr })}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{selectedPlanning.lieu}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Badge className="bg-primary/10 text-primary px-3 py-1.5 text-sm"><Truck className="w-4 h-4 mr-1" />{stats.foodTruckCount} Food Trucks</Badge>
                  <Badge className="bg-amber-100 text-amber-700 px-3 py-1.5 text-sm"><Beer className="w-4 h-4 mr-1" />{stats.barCount} Bars</Badge>
                  <Badge className="bg-blue-100 text-blue-700 px-3 py-1.5 text-sm"><Calendar className="w-4 h-4 mr-1" />{stats.daysCount} Jours</Badge>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExportPDF}
                    disabled={exportingPDF}
                    className="border-primary text-primary hover:bg-primary hover:text-white ml-2"
                    data-testid="export-pdf-btn"
                  >
                    {exportingPDF ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Export PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={exportToExcel}
                    className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                    data-testid="export-excel-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export Excel
                  </Button>
                  {canEdit && (
                    <>
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={() => {
                          setDossierForm({
                            ...dossierForm,
                            objet: `Dossier Complet HEC - ${selectedPlanning?.nom_evenement || 'Événement'}`
                          });
                          setShowSendDossierDialog(true);
                        }}
                        className="bg-[#8B1538] hover:bg-[#6B1028]"
                        data-testid="send-dossier-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer Dossier
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleDownloadAllDocs}
                        disabled={downloadingAllDocs}
                        className="border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538] hover:text-white"
                        data-testid="download-all-docs-btn"
                      >
                        {downloadingAllDocs ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Archive className="w-4 h-4 mr-2" />
                        )}
                        {downloadingAllDocs ? "Préparation..." : "Télécharger docs (.zip)"}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleGenerateClientLink}
                        className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                        data-testid="generate-client-link-btn"
                        title="Générer un lien d'accès client pour consulter le planning"
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        Lien Client
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={cleanupDuplicateSlots}
                        disabled={cleaningDuplicates}
                        className="border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white"
                        data-testid="cleanup-duplicates-btn"
                        title="Supprimer les food trucks en double dans le planning"
                      >
                        {cleaningDuplicates ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Nettoyer doublons
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleDeletePlanning}
                        className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                        data-testid="delete-planning-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border border-[#E5E0DC] p-1 h-auto flex-wrap">
              <TabsTrigger value="info-food-trucks" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <Truck className="w-4 h-4" />
                <span className="hidden sm:inline">Informations Food Trucks</span>
                <span className="sm:hidden">Food Trucks</span>
              </TabsTrigger>
              <TabsTrigger value="equipements" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <Plug className="w-4 h-4" />
                <span className="hidden sm:inline">Équipements</span>
                <span className="sm:hidden">Équip.</span>
              </TabsTrigger>
              <TabsTrigger value="staff-bar" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Staff Bar</span>
                <span className="sm:hidden">Staff</span>
              </TabsTrigger>
              <TabsTrigger value="planning-horaire" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Planning Horaire</span>
                <span className="sm:hidden">Horaires</span>
              </TabsTrigger>
              <TabsTrigger value="coffee-dessert" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <Coffee className="w-4 h-4" />
                <span className="hidden sm:inline">Coffee & Dessert</span>
                <span className="sm:hidden">Coffee</span>
              </TabsTrigger>
              <TabsTrigger value="planning" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <Calendar className="w-4 h-4" />
                Planning
              </TabsTrigger>
              <TabsTrigger value="carte" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <Map className="w-4 h-4" />
                Carte
              </TabsTrigger>
              <TabsTrigger value="menus" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <ChefHat className="w-4 h-4" />
                Menus
              </TabsTrigger>
              <TabsTrigger value="synthese" className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-primary data-[state=active]:text-white">
                <LayoutDashboard className="w-4 h-4" />
                Synthèse
              </TabsTrigger>
            </TabsList>

            {/* TAB 1: INFORMATIONS FOOD TRUCKS */}
            <TabsContent value="info-food-trucks" className="space-y-4" data-testid="tab-info-food-trucks">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  Informations techniques des Food Trucks
                </h3>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={handleSyncFoodTrucks} 
                        disabled={syncing}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                        Synchroniser
                      </Button>
                      {isAdmin && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => setShowMigrationDialog(true)} 
                          disabled={migratingDocs}
                          className="border-purple-300 text-purple-600 hover:bg-purple-50"
                          title="Migrer les documents du disque vers MongoDB pour la persistance en production"
                        >
                          {migratingDocs ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
                          Migrer Docs
                        </Button>
                      )}
                      <Button size="sm" onClick={openAddFoodTruck} className="bg-primary hover:bg-primary-hover">
                        <Plus className="w-4 h-4 mr-1" />Ajouter
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid gap-4">
                {selectedPlanning.food_trucks?.map((ft, index) => (
                  <Card key={index} className="border-[#E5E0DC] hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Logo */}
                          <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-primary/20">
                            {ft.logo_url ? (
                              <img src={ft.logo_url} alt={ft.nom} className="w-full h-full object-cover" />
                            ) : (
                              <Truck className="w-8 h-8 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {/* Nom et Spécialité */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-lg text-secondary">{ft.nom}</h4>
                              <Badge className="bg-primary/10 text-primary">{ft.specialite || "N/A"}</Badge>
                              {ft.auvent && <Badge variant="outline" className="text-xs">Auvent</Badge>}
                            </div>
                            
                            {/* Infos principales */}
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <User className="w-4 h-4 text-primary" />
                                <span className="font-medium">{ft.responsable || "Responsable N/A"}</span>
                              </div>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="w-4 h-4" />
                                <span>{ft.telephone || "N/A"}</span>
                              </div>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Car className="w-4 h-4 text-green-600" />
                                <span>
                                  {ft.type_vehicule || "Véhicule"} 
                                  {ft.ouverture && <span className="text-xs ml-1">({ft.ouverture})</span>}
                                </span>
                              </div>
                              {(ft.longueur || ft.largeur || ft.hauteur) && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <span className="text-xs">Dimensions: {ft.longueur && `L:${ft.longueur}m`}{ft.largeur && ` x ${ft.largeur}m`}{ft.hauteur && ` x H:${ft.hauteur}m`}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Zap className="w-4 h-4 text-yellow-500" />
                                <span>{ft.puissance_electrique || "Électricité N/A"}</span>
                              </div>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <FileText className="w-4 h-4" />
                                <span>Plaque: <code className="bg-gray-100 px-1 rounded text-xs">{ft.plaque_immatriculation || "N/A"}</code></span>
                              </div>
                            </div>

                            {/* Équipe / Employés */}
                            {((ft.equipe && ft.equipe.length > 0) || (ft.employees && ft.employees.length > 0)) && (
                              <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
                                <p className="text-xs text-blue-600 font-semibold mb-2 flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  Équipe ({(ft.equipe || ft.employees || []).length} employé(s))
                                </p>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs">
                                    <thead className="bg-blue-100/50">
                                      <tr>
                                        <th className="px-2 py-1 text-left text-blue-700">Prénom Nom</th>
                                        <th className="px-2 py-1 text-left text-blue-700">N° Pièce ID</th>
                                        <th className="px-2 py-1 text-left text-blue-700">Plaque</th>
                                        <th className="px-2 py-1 text-left text-blue-700">Fonction</th>
                                        <th className="px-2 py-1 text-left text-blue-700">Tél</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-blue-100">
                                      {(ft.equipe || ft.employees || []).map((emp, i) => (
                                        <tr key={i} className="bg-white hover:bg-blue-50">
                                          <td className="px-2 py-1.5 font-medium">
                                            {emp.prenom && `${emp.prenom} `}{emp.nom || '-'}
                                          </td>
                                          <td className="px-2 py-1.5 font-mono text-[11px]">
                                            {emp.numero_piece_identite || <span className="text-gray-400">-</span>}
                                          </td>
                                          <td className="px-2 py-1.5 font-mono text-[11px]">
                                            {emp.plaque_immatriculation || <span className="text-gray-400">-</span>}
                                          </td>
                                          <td className="px-2 py-1.5 text-muted-foreground">
                                            {emp.role || '-'}
                                          </td>
                                          <td className="px-2 py-1.5 text-muted-foreground">
                                            {emp.telephone || '-'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Documents status - clickable to view */}
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Badge 
                                className={`text-xs cursor-pointer transition-colors ${ft.doc_identite ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500'}`}
                                onClick={() => ft.doc_identite && downloadDocument(ft.doc_identite, ft.doc_identite_filename || "Identité")}
                              >
                                ID {ft.doc_identite ? '✓' : '✗'}
                              </Badge>
                              <Badge 
                                className={`text-xs cursor-pointer transition-colors ${ft.doc_assurance_rc ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500'}`}
                                onClick={() => ft.doc_assurance_rc && downloadDocument(ft.doc_assurance_rc, ft.doc_assurance_rc_filename || "RC Pro")}
                              >
                                RC Pro {ft.doc_assurance_rc ? '✓' : '✗'}
                              </Badge>
                              <Badge 
                                className={`text-xs cursor-pointer transition-colors ${ft.doc_haccp ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500'}`}
                                onClick={() => ft.doc_haccp && downloadDocument(ft.doc_haccp, ft.doc_haccp_filename || "HACCP")}
                              >
                                HACCP {ft.doc_haccp ? '✓' : '✗'}
                              </Badge>
                              <Badge 
                                className={`text-xs cursor-pointer transition-colors ${ft.doc_assurance_vehicule ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500'}`}
                                onClick={() => ft.doc_assurance_vehicule && downloadDocument(ft.doc_assurance_vehicule, ft.doc_assurance_vehicule_filename || "Assurance Véhicule")}
                              >
                                Assur. Véh. {ft.doc_assurance_vehicule ? '✓' : '✗'}
                              </Badge>
                            </div>

                            {/* Menu aperçu - Affiche Menu Standard et Menu Samedi Soir */}
                            <div className="mt-3 space-y-2">
                              {/* Menu Standard (Jeudi/Vendredi/Samedi Midi) */}
                              {(() => {
                                const menuStandard = ft.menu_par_jour?.Jeudi || ft.menu_items || [];
                                return menuStandard.length > 0 && (
                                  <div className="p-2 bg-amber-50 rounded-lg border border-amber-100">
                                    <p className="text-xs text-amber-700 font-semibold mb-1 flex items-center gap-1">
                                      <ChefHat className="w-3 h-3" />
                                      Menu Standard ({menuStandard.length} plats) - Jeudi, Vendredi, Samedi Midi
                                    </p>
                                    <div className="space-y-1">
                                      {menuStandard.map((item, i) => (
                                        <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border border-amber-100">
                                          <div className="flex justify-between items-start">
                                            <span className="font-semibold text-gray-800">{item.nom}</span>
                                            <span className="font-bold text-primary ml-2 whitespace-nowrap">{item.prix}€</span>
                                          </div>
                                          {item.ingredients && (
                                            <p className="text-muted-foreground mt-0.5 text-[11px] leading-relaxed">{item.ingredients}</p>
                                          )}
                                          {item.allergenes && (
                                            <p className="text-orange-600 mt-0.5 text-[10px] font-medium">⚠️ Allergènes: {item.allergenes}</p>
                                          )}
                                          {item.halal === true && (
                                            <span className="inline-block mt-0.5 text-[10px] text-green-700 font-medium bg-green-50 px-1 rounded">✓ Halal</span>
                                          )}
                                          {item.halal === false && (
                                            <span className="inline-block mt-0.5 text-[10px] text-gray-500 font-medium bg-gray-50 px-1 rounded">Non-Halal</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Menu Samedi Soir */}
                              {ft.menu_par_jour?.["Samedi Soir"]?.length > 0 && (
                                <div className="p-2 bg-purple-50 rounded-lg border border-purple-100">
                                  <p className="text-xs text-purple-700 font-semibold mb-1 flex items-center gap-1">
                                    <Moon className="w-3 h-3" />
                                    Menu Samedi Soir ({ft.menu_par_jour["Samedi Soir"].length} plats) - <span className="font-normal italic">inclus dans la soirée</span>
                                  </p>
                                  <div className="space-y-1">
                                    {ft.menu_par_jour["Samedi Soir"].map((item, i) => (
                                      <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border border-purple-100">
                                        <span className="font-semibold text-gray-800">{item.nom}</span>
                                        {item.ingredients && (
                                          <p className="text-muted-foreground mt-0.5 text-[11px] leading-relaxed">{item.ingredients}</p>
                                        )}
                                        {item.allergenes && (
                                          <p className="text-orange-600 mt-0.5 text-[10px] font-medium">⚠️ Allergènes: {item.allergenes}</p>
                                        )}
                                        {item.halal === true && (
                                          <span className="inline-block mt-0.5 text-[10px] text-green-700 font-medium bg-green-50 px-1 rounded">✓ Halal</span>
                                        )}
                                        {item.halal === false && (
                                          <span className="inline-block mt-0.5 text-[10px] text-gray-500 font-medium bg-gray-50 px-1 rounded">Non-Halal</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {canEdit && (
                          <div className="flex gap-2 lg:flex-col">
                            <Button variant="outline" size="sm" onClick={() => openEditFoodTruck(ft, index)} className="flex-1 lg:flex-none">
                              <Edit className="w-4 h-4 mr-1" />Modifier
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-1 lg:flex-none" onClick={() => deleteFoodTruck(index)}>
                              <Trash2 className="w-4 h-4 mr-1" />Supprimer
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Bars Section - Soir uniquement au Hall d'honneur */}
              <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                    <Beer className="w-5 h-5 text-amber-600" />
                    Bars - Soir (Hall d'honneur)
                  </h3>
                  {canEdit && (
                    <Button size="sm" onClick={openAddBar} className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="w-4 h-4 mr-1" />Ajouter
                    </Button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedPlanning.bars?.map((bar, index) => (
                    <Card key={index} className={`border ${bar.type === 'truck' ? 'border-amber-200 bg-amber-50/30' : 'border-purple-200 bg-purple-50/30'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${bar.type === 'truck' ? 'bg-amber-100' : 'bg-purple-100'}`}>
                              {bar.type === 'truck' ? <Beer className="w-6 h-6 text-amber-700" /> : <Wine className="w-6 h-6 text-purple-700" />}
                            </div>
                            <div>
                              <h4 className="font-semibold text-secondary">{bar.nom}</h4>
                              <Badge className={`text-xs ${bar.type === 'truck' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                                {bar.type === 'truck' ? 'Beer Truck' : 'Bar Mobile'}
                              </Badge>
                            </div>
                          </div>
                          {canEdit && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditBar(bar, index)}><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteBar(index)}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          )}
                        </div>
                        {bar.responsable && <p className="text-sm text-muted-foreground mb-1"><User className="w-3 h-3 inline mr-1" />{bar.responsable}</p>}
                        {bar.boissons?.length > 0 && (
                          <div className="mt-3 space-y-1">
                            <p className="text-xs text-muted-foreground mb-1">Carte des boissons:</p>
                            {bar.boissons.slice(0, 4).map((b, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span>{typeof b === 'string' ? b : b.nom}</span>
                                {typeof b === 'object' && b.prix > 0 && (
                                  <span className="font-medium text-primary">{b.prix.toFixed(2)}€</span>
                                )}
                              </div>
                            ))}
                            {bar.boissons.length > 4 && (
                              <p className="text-xs text-muted-foreground">+{bar.boissons.length - 4} autres boissons</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* TAB EQUIPEMENTS */}
            <TabsContent value="equipements" className="space-y-4" data-testid="tab-equipements">
              <Card className="border-[#E5E0DC] shadow-lg">
                <CardHeader className="border-b border-[#E5E0DC] bg-gradient-to-r from-yellow-50 to-orange-50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-semibold text-secondary flex items-center gap-2">
                      <Plug className="w-5 h-5 text-yellow-600" />
                      Équipements Supplémentaires (Besoins Électriques)
                    </CardTitle>
                    {canEdit && (
                      <Button size="sm" onClick={handleAddEquipement} className="bg-yellow-600 hover:bg-yellow-700">
                        <Plus className="w-4 h-4 mr-1" />Ajouter
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {equipements.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Plug className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucun équipement supplémentaire</p>
                      {canEdit && <p className="text-sm mt-2">Ajoutez des équipements nécessitant une alimentation électrique</p>}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nom</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                              <Zap className="w-4 h-4 inline mr-1" />Puissance Élec.
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Notes</th>
                            {canEdit && <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 w-24">Actions</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {equipements.map((eq, index) => (
                            <tr key={eq.id || index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{eq.nom}</td>
                              <td className="px-4 py-3">
                                <Badge variant="outline" className="bg-yellow-50 border-yellow-300 text-yellow-700">
                                  {eq.type_equipement === "Tireuse à bière" && <Beer className="w-3 h-3 mr-1" />}
                                  {eq.type_equipement === "Frigo" || eq.type_equipement === "Camion frigo" || eq.type_equipement === "Congélateur" ? <Snowflake className="w-3 h-3 mr-1" /> : null}
                                  {eq.type_equipement}
                                </Badge>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-yellow-700 font-medium">{eq.puissance_electrique || "-"}</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{eq.notes || "-"}</td>
                              {canEdit && (
                                <td className="px-4 py-3 text-center">
                                  <div className="flex gap-1 justify-center">
                                    <Button size="icon" variant="ghost" onClick={() => handleEditEquipement(eq)} className="h-8 w-8">
                                      <Edit className="w-4 h-4 text-blue-600" />
                                    </Button>
                                    <Button size="icon" variant="ghost" onClick={() => handleDeleteEquipement(eq.id)} className="h-8 w-8">
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </Button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Total puissance */}
                  {equipements.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-yellow-800">Total Équipements:</span>
                        <span className="text-yellow-800">{equipements.length} équipement(s)</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB STAFF BAR */}
            <TabsContent value="staff-bar" className="space-y-4" data-testid="tab-staff-bar">
              <Card className="border-[#E5E0DC] shadow-lg">
                <CardHeader className="border-b border-[#E5E0DC] bg-gradient-to-r from-purple-50 to-pink-50">
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <CardTitle className="text-lg font-semibold text-secondary flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      Staff Bar - Équipe & Coûts
                    </CardTitle>
                    {canEdit && (
                      <div className="flex gap-2">
                        {/* Import Excel Button */}
                        <div className="relative">
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleImportStaffExcel}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={importingStaffExcel}
                          />
                          <Button size="sm" variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" disabled={importingStaffExcel}>
                            {importingStaffExcel ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            Importer Excel
                          </Button>
                        </div>
                        <Button size="sm" onClick={handleAddStaffMember} className="bg-purple-600 hover:bg-purple-700">
                          <UserPlus className="w-4 h-4 mr-1" />Ajouter
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-6">
                  {/* Staff Table */}
                  {staffBar.staff.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucun membre du staff bar</p>
                      {canEdit && <p className="text-sm mt-2">Ajoutez les barmans, serveurs et responsables ou importez un fichier Excel</p>}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nom</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Prénom</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">N° Pièce ID</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Plaque</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Fonction</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Téléphone</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Jeu.</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Ven.</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Sam.</th>
                            <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Statut</th>
                            <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Coût/Jour</th>
                            {canEdit && <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 w-24">Actions</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {staffBar.staff.map((member, index) => {
                            const joursPresent = (member.presence_jeudi ? 1 : 0) + (member.presence_vendredi ? 1 : 0) + (member.presence_samedi ? 1 : 0);
                            const coutTotal = member.benevole ? 0 : (member.cout_journalier ? joursPresent * member.cout_journalier : 0);
                            return (
                              <tr key={member.id || index} className={`hover:bg-gray-50 ${member.benevole ? 'bg-green-50/50' : ''}`}>
                                <td className="px-4 py-3 font-medium">{member.nom}</td>
                                <td className="px-4 py-3">{member.prenom || "-"}</td>
                                <td className="px-4 py-3 text-sm font-mono">{member.numero_piece_identite || "-"}</td>
                                <td className="px-4 py-3 text-sm font-mono">{member.plaque_immatriculation || "-"}</td>
                                <td className="px-4 py-3">
                                  <Badge variant="outline" className="bg-purple-50 border-purple-300 text-purple-700">
                                    {member.fonction || "Staff"}
                                  </Badge>
                                </td>
                                <td className="px-4 py-3 text-sm">{member.telephone || "-"}</td>
                                <td className="px-4 py-3 text-center">
                                  {member.presence_jeudi ? <Check className="w-5 h-5 text-green-600 mx-auto" /> : <X className="w-5 h-5 text-gray-300 mx-auto" />}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {member.presence_vendredi ? <Check className="w-5 h-5 text-green-600 mx-auto" /> : <X className="w-5 h-5 text-gray-300 mx-auto" />}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {member.presence_samedi ? <Check className="w-5 h-5 text-green-600 mx-auto" /> : <X className="w-5 h-5 text-gray-300 mx-auto" />}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {member.benevole ? (
                                    <Badge className="bg-green-100 text-green-700 border-green-300">Bénévole</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-gray-600">Salarié</Badge>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {member.benevole ? (
                                    <span className="text-green-600 text-sm">Gratuit</span>
                                  ) : member.cout_journalier ? (
                                    <span className="font-medium">{member.cout_journalier}€</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                {canEdit && (
                                  <td className="px-4 py-3 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button size="icon" variant="ghost" onClick={() => handleEditStaffMember(member)} className="h-8 w-8">
                                        <Edit className="w-4 h-4 text-blue-600" />
                                      </Button>
                                      <Button size="icon" variant="ghost" onClick={() => handleDeleteStaffMember(member.id)} className="h-8 w-8">
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </Button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Costs Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Coût Global Additionnel */}
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <label className="block text-sm font-medium text-purple-800 mb-2">
                        <Euro className="w-4 h-4 inline mr-1" />
                        Coût Global Additionnel
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          placeholder="0"
                          value={staffBar.cout_global || ""}
                          onChange={(e) => setStaffBar({ ...staffBar, cout_global: e.target.value ? parseFloat(e.target.value) : null })}
                          className="max-w-[150px]"
                        />
                        <span className="self-center">€</span>
                        <Button size="sm" onClick={handleSaveStaffBarGlobal} variant="outline" className="ml-auto">
                          <Save className="w-4 h-4 mr-1" />Sauvegarder
                        </Button>
                      </div>
                      <p className="text-xs text-purple-600 mt-1">Frais supplémentaires (matériel, transport...)</p>
                    </div>

                    {/* Total Summary */}
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-semibold text-green-800 mb-2">📊 Résumé des Coûts</h4>
                      {(() => {
                        const { totalJours, totalCout, staffCount, benevoles } = calculateStaffCosts();
                        return (
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Effectif total:</span>
                              <span className="font-medium">{staffCount} personne(s)</span>
                            </div>
                            {benevoles > 0 && (
                              <div className="flex justify-between text-green-600">
                                <span>dont Bénévoles:</span>
                                <span className="font-medium">{benevoles} bénévole(s)</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span>Total jours travaillés:</span>
                              <span className="font-medium">{totalJours} jour(s)</span>
                            </div>
                            <div className="flex justify-between border-t border-green-300 pt-1 mt-1">
                              <span className="font-semibold">Coût Total:</span>
                              <span className="font-bold text-green-700">{totalCout.toFixed(2)} €</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes Staff Bar</label>
                    <textarea
                      className="w-full border rounded-lg p-3 text-sm"
                      rows={3}
                      placeholder="Notes diverses sur le staff bar..."
                      value={staffBar.notes || ""}
                      onChange={(e) => setStaffBar({ ...staffBar, notes: e.target.value })}
                      onBlur={handleSaveStaffBarGlobal}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: PLANNING HORAIRE STAFF */}
            <TabsContent value="planning-horaire" className="space-y-4" data-testid="tab-planning-horaire">
              <Card className="border-[#E5E0DC] shadow-lg">
                <CardHeader className="border-b border-[#E5E0DC] bg-gradient-to-r from-purple-100 to-indigo-100">
                  <div className="flex justify-between items-center flex-wrap gap-3">
                    <CardTitle className="text-lg font-semibold text-secondary flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      Planning Horaire Staff Bar
                    </CardTitle>
                    <div className="flex gap-2 items-center">
                      <Select value={selectedPlanningDay} onValueChange={setSelectedPlanningDay}>
                        <SelectTrigger className="w-32 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PLANNING_DAYS.map(day => (
                            <SelectItem key={day} value={day}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={exportPlanningHoraireExcel}
                        className="bg-white hover:bg-green-50 border-green-600 text-green-700"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Export Excel
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {staffBar.staff.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucun membre du staff bar</p>
                      <p className="text-sm mt-2">Ajoutez d'abord des membres dans l'onglet "Staff Bar"</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Info banner */}
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700 flex items-start gap-2">
                        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                          Cliquez sur une cellule pour assigner/désassigner un membre du staff à un créneau horaire.
                          Le planning est sauvegardé automatiquement.
                        </div>
                      </div>

                      {/* Planning Grid */}
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full min-w-[600px]">
                          <thead className="bg-gradient-to-r from-purple-50 to-indigo-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 border-b border-r w-20 sticky left-0 bg-purple-50 z-10">
                                Heure
                              </th>
                              {staffBar.staff.map((member) => (
                                <th 
                                  key={member.id} 
                                  className="px-2 py-3 text-center text-sm font-semibold text-gray-700 border-b min-w-[100px]"
                                >
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="truncate max-w-[90px]" title={member.prenom ? `${member.prenom} ${member.nom}` : member.nom}>
                                      {member.prenom || member.nom}
                                    </span>
                                    <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 font-normal">
                                      {member.fonction || "Staff"}
                                    </Badge>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {PLANNING_HOURS.map((hour, hourIndex) => {
                              const isLunchTime = hour >= "11:00" && hour <= "14:00";
                              const isDinnerTime = hour >= "18:00" && hour <= "22:00";
                              const isNightTime = hour >= "23:00" || hour <= "01:00";
                              
                              let rowBg = "";
                              if (isLunchTime) rowBg = "bg-yellow-50/50";
                              else if (isDinnerTime) rowBg = "bg-orange-50/50";
                              else if (isNightTime) rowBg = "bg-indigo-50/50";
                              
                              return (
                                <tr key={hour} className={`${rowBg} hover:bg-gray-50 transition-colors`}>
                                  <td className={`px-3 py-2 text-sm font-medium border-r sticky left-0 ${rowBg || 'bg-white'} z-10`}>
                                    <div className="flex items-center gap-1">
                                      {isLunchTime && <Sun className="w-3 h-3 text-yellow-500" />}
                                      {isDinnerTime && <Sunset className="w-3 h-3 text-orange-500" />}
                                      {isNightTime && <Moon className="w-3 h-3 text-indigo-500" />}
                                      {hour}
                                    </div>
                                  </td>
                                  {staffBar.staff.map((member) => {
                                    const isAssigned = getStaffForHour(selectedPlanningDay, hour).includes(member.id);
                                    return (
                                      <td 
                                        key={`${member.id}-${hour}`}
                                        className="px-2 py-2 text-center border-l cursor-pointer group"
                                        onClick={() => toggleStaffHour(member.id, selectedPlanningDay, hour)}
                                      >
                                        <div className={`w-full h-8 rounded flex items-center justify-center transition-all ${
                                          isAssigned 
                                            ? 'bg-purple-500 text-white shadow-sm' 
                                            : 'bg-gray-100 group-hover:bg-purple-100 group-hover:border-purple-300 border border-transparent'
                                        }`}>
                                          {isAssigned && <Check className="w-4 h-4" />}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                            {/* Totals row */}
                            <tr className="bg-gray-100 font-semibold border-t-2 border-purple-300">
                              <td className="px-3 py-3 text-sm sticky left-0 bg-gray-100 z-10">
                                TOTAL
                              </td>
                              {staffBar.staff.map((member) => (
                                <td key={`total-${member.id}`} className="px-2 py-3 text-center border-l">
                                  <Badge className="bg-purple-600 text-white">
                                    {getStaffHoursForDay(member.id, selectedPlanningDay)}h
                                  </Badge>
                                </td>
                              ))}
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Summary by member */}
                      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-white">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-purple-600" />
                            Récapitulatif des heures
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {staffBar.staff.map((member) => {
                              const totalHours = getTotalStaffHours(member.id);
                              return (
                                <div 
                                  key={member.id}
                                  className="p-3 bg-white rounded-lg border border-purple-100 shadow-sm"
                                >
                                  <p className="font-medium text-sm truncate" title={member.prenom ? `${member.prenom} ${member.nom}` : member.nom}>
                                    {member.prenom ? `${member.prenom} ${member.nom}` : member.nom}
                                  </p>
                                  <p className="text-xs text-muted-foreground mb-2">{member.fonction || "Staff"}</p>
                                  <div className="flex justify-between text-xs">
                                    <span>Jeu: <strong>{getStaffHoursForDay(member.id, "Jeudi")}h</strong></span>
                                    <span>Ven: <strong>{getStaffHoursForDay(member.id, "Vendredi")}h</strong></span>
                                    <span>Sam: <strong>{getStaffHoursForDay(member.id, "Samedi")}h</strong></span>
                                  </div>
                                  <div className="mt-2 pt-2 border-t border-purple-100 text-center">
                                    <Badge className="bg-purple-600 text-white">
                                      Total: {totalHours}h
                                    </Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Legend */}
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-4 p-3 bg-gray-50 rounded-lg">
                        <span className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded bg-yellow-50 border"></div>
                          <Sun className="w-3 h-3 text-yellow-500" /> Service midi (11h-14h)
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded bg-orange-50 border"></div>
                          <Sunset className="w-3 h-3 text-orange-500" /> Service soir (18h-22h)
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded bg-indigo-50 border"></div>
                          <Moon className="w-3 h-3 text-indigo-500" /> Service nuit (23h-01h)
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-4 h-4 rounded bg-purple-500"></div>
                          Créneau assigné
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: COFFEE & DESSERT */}
            <TabsContent value="coffee-dessert" className="space-y-4" data-testid="tab-coffee-dessert">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div className="flex items-center gap-2">
                    <Coffee className="w-5 h-5 text-amber-700" />
                    <CardTitle className="text-lg text-amber-900">
                      Food Trucks Coffee & Dessert
                    </CardTitle>
                  </div>
                  {canEdit && (
                    <Button onClick={handleAddCoffeeDessert} size="sm" className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    {canEdit ? "Gérez les food trucks spécialisés en café, desserts, glaces, crêpes, etc. avec leurs jours de présence et horaires." : "Food trucks spécialisés en café, desserts, glaces, crêpes, etc."}
                  </p>

                  {/* Stats résumé */}
                  {coffeeDesserts.length > 0 && (
                    <div className="flex gap-4 flex-wrap">
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 px-3 py-1.5">
                        <Coffee className="w-4 h-4 mr-1" /> {getCoffeeDessertStats().total} Coffee/Dessert
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 px-3 py-1.5">
                        Jeudi: {getCoffeeDessertStats().jeudi}
                      </Badge>
                      <Badge variant="outline" className="bg-green-50 text-green-700 px-3 py-1.5">
                        Vendredi: {getCoffeeDessertStats().vendredi}
                      </Badge>
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 px-3 py-1.5">
                        Samedi: {getCoffeeDessertStats().samedi}
                      </Badge>
                    </div>
                  )}

                  {coffeeDesserts.length === 0 ? (
                    <div className="text-center py-12 bg-amber-50 rounded-lg border border-amber-200">
                      <IceCream className="w-12 h-12 mx-auto text-amber-400 mb-4" />
                      <p className="text-amber-700 font-medium">Aucun food truck Coffee/Dessert</p>
                      <p className="text-amber-600 text-sm mt-1">Cliquez sur "Ajouter" pour configurer les stands de café et desserts</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {coffeeDesserts.map((item, index) => (
                        <div 
                          key={item.id || index} 
                          className="p-4 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Coffee className="w-5 h-5 text-amber-600" />
                                <span className="font-semibold text-gray-800">{item.nom}</span>
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700">
                                  {item.type_service}
                                </Badge>
                              </div>
                              
                              {item.responsable && (
                                <p className="text-sm text-gray-600 mb-1">
                                  <User className="w-3 h-3 inline mr-1" /> {item.responsable}
                                  {item.telephone && <span className="ml-3"><Phone className="w-3 h-3 inline mr-1" />{item.telephone}</span>}
                                </p>
                              )}
                              
                              {item.specialites && (
                                <p className="text-sm text-gray-500 mb-2">{item.specialites}</p>
                              )}

                              {/* Jours de présence */}
                              <div className="flex flex-wrap gap-2 mt-2">
                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${item.presence_jeudi ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'}`}>
                                  <Calendar className="w-3 h-3" />
                                  <span>Jeudi</span>
                                  {item.presence_jeudi && item.horaire_jeudi && (
                                    <span className="ml-1 font-medium">{item.horaire_jeudi}</span>
                                  )}
                                </div>
                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${item.presence_vendredi ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'}`}>
                                  <Calendar className="w-3 h-3" />
                                  <span>Vendredi</span>
                                  {item.presence_vendredi && item.horaire_vendredi && (
                                    <span className="ml-1 font-medium">{item.horaire_vendredi}</span>
                                  )}
                                </div>
                                <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${item.presence_samedi ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-400'}`}>
                                  <Calendar className="w-3 h-3" />
                                  <span>Samedi</span>
                                  {item.presence_samedi && item.horaire_samedi && (
                                    <span className="ml-1 font-medium">{item.horaire_samedi}</span>
                                  )}
                                </div>
                              </div>

                              {item.besoins_electriques && (
                                <p className="text-xs text-orange-600 mt-2">
                                  <Zap className="w-3 h-3 inline mr-1" /> {item.besoins_electriques}
                                </p>
                              )}
                            </div>
                            
                            {canEdit && (
                              <div className="flex gap-1 ml-4">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleEditCoffeeDessert(item, index)}
                                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => handleDeleteCoffeeDessert(index)}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB 2: PLANNING PAR ZONE */}
            <TabsContent value="planning" className="space-y-4" data-testid="tab-planning">
              {/* Day Selector with Export button */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex gap-2 flex-wrap">
                  {selectedPlanning.planning_days?.map((day) => (
                    <Button
                      key={day.date}
                      variant={selectedDay === day.date ? "default" : "outline"}
                      className={selectedDay === day.date ? "bg-primary" : "border-[#E5E0DC]"}
                      onClick={() => setSelectedDay(day.date)}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {format(parseISO(day.date), "EEEE d MMMM", { locale: fr })}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportDayToPDF(selectedDay)}
                  disabled={exportingPDF || !selectedDay}
                  className="border-primary text-primary hover:bg-primary hover:text-white"
                  title="Exporter ce jour en PDF"
                >
                  {exportingPDF ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Printer className="w-4 h-4 mr-2" />
                  )}
                  PDF du jour
                </Button>
              </div>

              {/* Drag instruction - Admin only */}
              {isAdmin && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-2 text-sm text-blue-700">
                  <GripVertical className="w-4 h-4" />
                  <span><strong>Glisser-déposer</strong> : Déplacez les food trucks d'une zone à l'autre en les faisant glisser</span>
                </div>
              )}

              {/* MATIN Section */}
              <Card className="border-[#E5E0DC]">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-[#E5E0DC] py-4">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-blue-700">
                      <Sunrise className="w-6 h-6" />
                      <span className="text-xl">Service du Matin</span>
                    </span>
                    <Badge className="bg-blue-100 text-blue-700 px-3 py-1">{getCurrentDaySlots().matin?.length || 0} food trucks</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {getCurrentDaySlots().matin?.length === 0 ? (
                    <div className="grid md:grid-cols-4 gap-4">
                      {["Volley", "Central", "Foot", "BAT S"].map((zone) => {
                        const colors = getZoneColor(zone);
                        return (
                          <div key={zone} className={`rounded-lg border-2 ${colors.border} overflow-hidden`}>
                            <div className={`${colors.bg} px-3 py-2 border-b ${colors.border} flex items-center justify-between`}>
                              <h4 className={`font-semibold ${colors.text}`}>{zone}</h4>
                              {isAdmin && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className={`h-6 w-6 p-0 ${colors.text} hover:bg-white/50`}
                                  onClick={() => openAddToZone(zone, "matin")}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            <div className="p-2 min-h-[100px]">
                              {isAdmin ? (
                                <button 
                                  className="w-full py-4 text-xs text-muted-foreground hover:bg-blue-50 rounded transition-colors"
                                  onClick={() => openAddToZone(zone, "matin")}
                                >
                                  + Ajouter un FT
                                </button>
                              ) : (
                                <p className="text-xs text-muted-foreground text-center py-4">Zone vide</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-4 gap-4">
                      {["Volley", "Central", "Foot", "BAT S"].map((zone) => {
                        const colors = getZoneColor(zone);
                        const zoneSlots = groupSlotsByZone(getCurrentDaySlots().matin)[zone] || [];
                        const isDropTarget = isAdmin && dragOverZone === `matin-${zone}` && draggedSlot?.service === "matin";
                        
                        return (
                          <div 
                            key={zone} 
                            className={`rounded-lg border-2 ${colors.border} overflow-hidden transition-all ${isDropTarget ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                            onDragOver={isAdmin ? (e) => handleDragOver(e, `matin-${zone}`) : undefined}
                            onDragLeave={isAdmin ? handleDragLeave : undefined}
                            onDrop={isAdmin ? (e) => handleDrop(e, zone, "matin") : undefined}
                          >
                            <div className={`${colors.bg} px-3 py-2 border-b ${colors.border} flex items-center justify-between`}>
                              <h4 className={`font-semibold ${colors.text}`}>{zone}</h4>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={colors.text}>{zoneSlots.length} FT</Badge>
                                {isAdmin && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className={`h-6 w-6 p-0 ${colors.text} hover:bg-white/50`}
                                    onClick={() => openAddToZone(zone, "matin")}
                                    title="Ajouter un food truck"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="p-2 space-y-2 min-h-[100px]">
                              {zoneSlots.length === 0 ? (
                                isAdmin ? (
                                  <button 
                                    className="w-full py-4 text-xs text-muted-foreground hover:bg-blue-50 rounded transition-colors"
                                    onClick={() => openAddToZone(zone, "matin")}
                                  >
                                    + Ajouter un FT
                                  </button>
                                ) : (
                                  <p className="text-xs text-muted-foreground text-center py-4">Zone vide</p>
                                )
                              ) : (
                                zoneSlots.map((slot, idx) => (
                                  <div 
                                    key={idx} 
                                    draggable={isAdmin}
                                    onDragStart={isAdmin ? (e) => handleDragStart(e, slot, "matin") : undefined}
                                    onDragEnd={isAdmin ? handleDragEnd : undefined}
                                    className={`text-sm p-2 rounded border flex items-center justify-between ${draggedSlot && draggedSlot?.originalIndex === slot.originalIndex && draggedSlot?.service === "matin" ? 'opacity-50 border-dashed' : 'bg-white'} ${isAdmin ? 'cursor-move hover:shadow-md' : ''}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{slot.food_truck_nom || "Non assigné"}</p>
                                      <p className="text-xs text-muted-foreground">{slot.specialite}</p>
                                    </div>
                                    {isAdmin && (
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="ml-2 h-6 w-6 p-0 text-muted-foreground hover:text-red-600"
                                        onClick={() => removeFromZone("matin", slot.originalIndex, slot.food_truck_nom)}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* MIDI Section */}
              <Card className="border-[#E5E0DC]">
                <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b border-[#E5E0DC] py-4">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-amber-700">
                      <Sun className="w-6 h-6" />
                      <span className="text-xl">Service du Midi</span>
                    </span>
                    <Badge className="bg-amber-100 text-amber-700 px-3 py-1">{getCurrentDaySlots().midi?.length || 0} food trucks</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {getCurrentDaySlots().midi?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Aucun food truck positionné pour le midi</p>
                  ) : (
                    <div className="grid md:grid-cols-3 gap-4">
                      {["Volley", "Central", "Foot"].map((zone) => {
                        const colors = getZoneColor(zone);
                        const zoneSlots = groupSlotsByZone(getCurrentDaySlots().midi)[zone] || [];
                        const isDropTarget = isAdmin && dragOverZone === `midi-${zone}` && draggedSlot?.service === "midi";
                        
                        return (
                          <div 
                            key={zone} 
                            className={`rounded-lg border-2 ${colors.border} overflow-hidden transition-all ${isDropTarget ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                            onDragOver={isAdmin ? (e) => handleDragOver(e, `midi-${zone}`) : undefined}
                            onDragLeave={isAdmin ? handleDragLeave : undefined}
                            onDrop={isAdmin ? (e) => handleDrop(e, zone, "midi") : undefined}
                          >
                            <div className={`bg-gradient-to-r ${colors.header} px-4 py-2 flex items-center justify-between`}>
                              <h4 className={`font-semibold ${colors.text} flex items-center gap-2`}>
                                <MapPin className="w-4 h-4" />
                                Zone {zone}
                              </h4>
                              <div className="flex items-center gap-2">
                                <Badge className={`${colors.bg} ${colors.text}`}>{zoneSlots.length}</Badge>
                                {isAdmin && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className={`h-6 w-6 p-0 ${colors.text} hover:bg-white/50`}
                                    onClick={() => openAddToZone(zone, "midi")}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className={`p-3 min-h-[150px] space-y-2 ${isDropTarget ? 'bg-blue-50' : ''}`}>
                              {zoneSlots.length === 0 ? (
                                <div className="text-center py-4">
                                  <p className="text-sm text-gray-400">{isAdmin ? "Déposez un food truck ici" : "Aucun food truck"}</p>
                                  {isAdmin && (
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className="mt-2"
                                      onClick={() => openAddToZone(zone, "midi")}
                                    >
                                      <Plus className="w-4 h-4 mr-1" />Ajouter
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                zoneSlots.map((slot, idx) => (
                                  <div 
                                    key={idx}
                                    draggable={isAdmin}
                                    onDragStart={isAdmin ? (e) => handleDragStart(e, slot, "midi", slot.originalIndex) : undefined}
                                    onDragEnd={isAdmin ? handleDragEnd : undefined}
                                    className={`p-3 ${colors.bg} rounded-lg flex items-center justify-between ${isAdmin ? 'cursor-grab active:cursor-grabbing hover:shadow-md' : 'cursor-pointer'} transition-all border border-transparent hover:border-gray-300 group`}
                                  >
                                    <div className="flex items-center gap-3" onClick={() => openFoodTruckDetail(slot)}>
                                      <div className="flex items-center">
                                        {isAdmin && <GripVertical className="w-4 h-4 text-gray-400 mr-1" />}
                                        <div className="w-9 h-9 rounded-full bg-white/80 flex items-center justify-center">
                                          <Truck className={`w-4 h-4 ${colors.text}`} />
                                        </div>
                                      </div>
                                      <div>
                                        <p className="font-medium text-secondary text-sm">{slot.food_truck_nom}</p>
                                        <p className="text-xs text-muted-foreground">{slot.specialite}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {slot.horaire_debut && (
                                        <span className="text-xs text-gray-500">{slot.horaire_debut}-{slot.horaire_fin}</span>
                                      )}
                                      {isAdmin && (
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeFromZone("midi", slot.originalIndex, slot.food_truck_nom);
                                          }}
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* APRÈS-MIDI Section */}
              <Card className="border-[#E5E0DC]">
                <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-[#E5E0DC] py-4">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-orange-700">
                      <Sunset className="w-6 h-6" />
                      <span className="text-xl">Service de l'Après-midi</span>
                    </span>
                    <Badge className="bg-orange-100 text-orange-700 px-3 py-1">{getCurrentDaySlots().apres_midi?.length || 0} food trucks</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {getCurrentDaySlots().apres_midi?.length === 0 ? (
                    <div className="grid md:grid-cols-3 gap-4">
                      {["Volley", "Central", "Foot"].map((zone) => {
                        const colors = getZoneColor(zone);
                        return (
                          <div key={zone} className={`rounded-lg border-2 ${colors.border} overflow-hidden`}>
                            <div className={`${colors.bg} px-3 py-2 border-b ${colors.border} flex items-center justify-between`}>
                              <h4 className={`font-semibold ${colors.text}`}>{zone}</h4>
                              {isAdmin && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className={`h-6 w-6 p-0 ${colors.text} hover:bg-white/50`}
                                  onClick={() => openAddToZone(zone, "apres_midi")}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            <div className="p-2 min-h-[100px]">
                              {isAdmin ? (
                                <button 
                                  className="w-full py-4 text-xs text-muted-foreground hover:bg-orange-50 rounded transition-colors"
                                  onClick={() => openAddToZone(zone, "apres_midi")}
                                >
                                  + Ajouter un FT
                                </button>
                              ) : (
                                <p className="text-xs text-muted-foreground text-center py-4">Zone vide</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-3 gap-4">
                      {["Volley", "Central", "Foot"].map((zone) => {
                        const colors = getZoneColor(zone);
                        const zoneSlots = groupSlotsByZone(getCurrentDaySlots().apres_midi)[zone] || [];
                        const isDropTarget = isAdmin && dragOverZone === `apres_midi-${zone}` && draggedSlot?.service === "apres_midi";
                        
                        return (
                          <div 
                            key={zone} 
                            className={`rounded-lg border-2 ${colors.border} overflow-hidden transition-all ${isDropTarget ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                            onDragOver={isAdmin ? (e) => handleDragOver(e, `apres_midi-${zone}`) : undefined}
                            onDragLeave={isAdmin ? handleDragLeave : undefined}
                            onDrop={isAdmin ? (e) => handleDrop(e, zone, "apres_midi") : undefined}
                          >
                            <div className={`${colors.bg} px-3 py-2 border-b ${colors.border} flex items-center justify-between`}>
                              <h4 className={`font-semibold ${colors.text}`}>{zone}</h4>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={colors.text}>{zoneSlots.length} FT</Badge>
                                {isAdmin && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className={`h-6 w-6 p-0 ${colors.text} hover:bg-white/50`}
                                    onClick={() => openAddToZone(zone, "apres_midi")}
                                    title="Ajouter un food truck"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="p-2 space-y-2 min-h-[100px]">
                              {zoneSlots.length === 0 ? (
                                isAdmin ? (
                                  <button 
                                    className="w-full py-4 text-xs text-muted-foreground hover:bg-orange-50 rounded transition-colors"
                                    onClick={() => openAddToZone(zone, "apres_midi")}
                                  >
                                    + Ajouter un FT
                                  </button>
                                ) : (
                                  <p className="text-xs text-muted-foreground text-center py-4">Zone vide</p>
                                )
                              ) : (
                                zoneSlots.map((slot, idx) => (
                                  <div 
                                    key={idx} 
                                    draggable={isAdmin}
                                    onDragStart={isAdmin ? (e) => handleDragStart(e, slot, "apres_midi") : undefined}
                                    onDragEnd={isAdmin ? handleDragEnd : undefined}
                                    className={`text-sm p-2 rounded border flex items-center justify-between ${draggedSlot && draggedSlot?.originalIndex === slot.originalIndex && draggedSlot?.service === "apres_midi" ? 'opacity-50 border-dashed' : 'bg-white'} ${isAdmin ? 'cursor-move hover:shadow-md' : ''}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{slot.food_truck_nom || "Non assigné"}</p>
                                      <p className="text-xs text-muted-foreground">{slot.specialite}</p>
                                    </div>
                                    {isAdmin && (
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="ml-2 h-6 w-6 p-0 text-muted-foreground hover:text-red-600"
                                        onClick={() => removeFromZone("apres_midi", slot.originalIndex, slot.food_truck_nom)}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* SOIR Section - Hall d'honneur uniquement */}
              <Card className="border-[#E5E0DC]">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-[#E5E0DC] py-4">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-indigo-700">
                      <Moon className="w-6 h-6" />
                      <span className="text-xl">Service du Soir (Hall d'honneur)</span>
                    </span>
                    <Badge className="bg-indigo-100 text-indigo-700 px-3 py-1">{getCurrentDaySlots().soir?.length || 0} food trucks</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-1 gap-4 max-w-2xl mx-auto">
                    {["Hall d'honneur"].map((zone) => {
                      const colors = getZoneColor(zone);
                      // Affiche TOUS les food trucks du soir (pas filtré par zone)
                      const allSoirSlots = getCurrentDaySlots().soir || [];
                      const isDropTarget = isAdmin && dragOverZone === `soir-${zone}` && draggedSlot?.service === "soir";
                      
                      return (
                        <div 
                          key={zone} 
                          className={`p-4 rounded-lg border-2 transition-all ${colors.bg} ${colors.border} ${isDropTarget ? 'ring-2 ring-primary scale-[1.02]' : ''}`}
                          onDragOver={isAdmin ? (e) => handleDragOver(e, `soir-${zone}`) : undefined}
                          onDragLeave={isAdmin ? handleDragLeave : undefined}
                          onDrop={isAdmin ? (e) => handleDrop(e, zone, "soir") : undefined}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${colors.text}`}>{zone}</h4>
                            <div className="flex items-center gap-1">
                              <span className={`text-xs ${colors.text}`}>{allSoirSlots.length} FT</span>
                              {isAdmin && (
                                <button 
                                  onClick={() => openAddToZone(zone, "soir")}
                                  className={`p-1 rounded hover:bg-white/50 ${colors.text}`}
                                  title="Ajouter un food truck"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          {allSoirSlots.length === 0 ? (
                            isAdmin ? (
                              <button 
                                onClick={() => openAddToZone(zone, "soir")}
                                className={`w-full py-3 border-2 border-dashed rounded-lg ${colors.border} ${colors.text} hover:bg-white/30 transition-colors text-sm`}
                              >
                                + Ajouter un FT
                              </button>
                            ) : (
                              <p className={`text-sm italic ${colors.text}`}>Zone vide</p>
                            )
                          ) : (
                            <div className="grid md:grid-cols-4 gap-2">
                              {allSoirSlots.map((slot, idx) => (
                                <div 
                                  key={idx} 
                                  draggable={isAdmin}
                                  onDragStart={isAdmin ? (e) => handleDragStart(e, {...slot, originalIndex: idx}, "soir") : undefined}
                                  onDragEnd={isAdmin ? handleDragEnd : undefined}
                                  className={`text-sm p-2 rounded border flex items-center justify-between ${draggedSlot && draggedSlot?.originalIndex === idx && draggedSlot?.service === "soir" ? 'opacity-50 border-dashed' : 'bg-white'} ${isAdmin ? 'cursor-move hover:shadow-md' : ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <GripVertical className="w-4 h-4 text-gray-400" />
                                    <div>
                                      <p className="font-medium text-secondary">{slot.food_truck_nom}</p>
                                      <p className="text-xs text-muted-foreground">{slot.specialite}</p>
                                    </div>
                                  </div>
                                  {isAdmin && (
                                    <button 
                                      className="text-red-500 hover:text-red-700 p-1"
                                      onClick={() => removeFromZone("soir", idx, slot.food_truck_nom)}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                              {isAdmin && (
                                <button 
                                  onClick={() => openAddToZone(zone, "soir")}
                                  className={`py-2 border-2 border-dashed rounded-lg ${colors.border} ${colors.text} hover:bg-white/30 transition-colors text-xs`}
                                >
                                  + Ajouter
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Bars du soir */}
                  <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                        <Beer className="w-5 h-5" />
                        Bars du soir - Hall d'honneur
                      </h4>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        onClick={openBarsSoirManager}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Gérer les bars
                      </Button>
                    </div>
                    {getCurrentDaySlots().bars_soir?.length > 0 ? (
                      <div className="grid md:grid-cols-3 gap-3">
                        {getCurrentDaySlots().bars_soir?.map((bar, index) => {
                          // Get drinks from bar definition (Informations Food Trucks)
                          const barDefinition = selectedPlanning?.bars?.find(b => b.nom === bar.bar_nom);
                          const boissonsFromDefinition = barDefinition?.boissons?.map(b => b.nom) || [];
                          // Use definition drinks if available, otherwise fallback to stored boissons_actives
                          const boissonsToDisplay = boissonsFromDefinition.length > 0 ? boissonsFromDefinition : (bar.boissons_actives || []);
                          
                          return (
                            <div key={index} className="p-3 bg-white rounded-lg border border-amber-100">
                              <p className="font-medium text-secondary">{bar.bar_nom}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {boissonsToDisplay.map((b, i) => (
                                  <Badge key={i} className="text-xs bg-amber-100 text-amber-800">{b}</Badge>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-amber-600 text-sm italic">Aucun bar assigné pour ce soir. Cliquez sur "Gérer les bars" pour en ajouter.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* SERVICE DE NUIT */}
              <Card className="border-[#E5E0DC] overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white py-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3 text-white">
                      <Moon className="w-6 h-6" />
                      <span className="text-xl">Service de Nuit (00h00 - 02h00)</span>
                    </CardTitle>
                    <Badge className="bg-slate-100 text-slate-700 px-3 py-1">{getCurrentDaySlots().nuit?.length || 0} food trucks</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Hall d'honneur uniquement pour la nuit */}
                  <div className="grid md:grid-cols-1 gap-4 max-w-2xl mx-auto">
                    {["Hall d'honneur"].map((zone) => {
                      const colors = getZoneColor(zone);
                      const zoneSlots = groupSlotsByZone(getCurrentDaySlots().nuit)[zone] || [];
                      const isDropTarget = isAdmin && dragOverZone === `nuit-${zone}` && draggedSlot?.service === "nuit";
                      
                      return (
                        <div 
                          key={zone} 
                          className={`p-4 rounded-lg border-2 transition-all ${colors.bg} ${colors.border} ${isDropTarget ? 'ring-2 ring-primary scale-[1.02]' : ''}`}
                          onDragOver={isAdmin ? (e) => handleDragOver(e, `nuit-${zone}`) : undefined}
                          onDragLeave={isAdmin ? handleDragLeave : undefined}
                          onDrop={isAdmin ? (e) => handleDrop(e, zone, "nuit") : undefined}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={`font-semibold ${colors.text}`}>{zone}</h4>
                            <div className="flex items-center gap-1">
                              <span className={`text-xs ${colors.text}`}>{zoneSlots.length} FT</span>
                              {isAdmin && (
                                <button 
                                  onClick={() => openAddToZone(zone, "nuit")}
                                  className={`p-1 rounded hover:bg-white/50 ${colors.text}`}
                                  title="Ajouter un food truck"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          {zoneSlots.length === 0 ? (
                            isAdmin ? (
                              <button 
                                onClick={() => openAddToZone(zone, "nuit")}
                                className={`w-full py-3 border-2 border-dashed rounded-lg ${colors.border} ${colors.text} hover:bg-white/30 transition-colors text-sm`}
                              >
                                + Ajouter un FT
                              </button>
                            ) : (
                              <p className={`text-sm italic ${colors.text}`}>Zone vide</p>
                            )
                          ) : (
                            <div className="space-y-2">
                              {zoneSlots.map((slot, idx) => (
                                <div 
                                  key={idx} 
                                  draggable={isAdmin}
                                  onDragStart={isAdmin ? (e) => handleDragStart(e, slot, "nuit") : undefined}
                                  onDragEnd={isAdmin ? handleDragEnd : undefined}
                                  className={`text-sm p-2 rounded border flex items-center justify-between ${draggedSlot && draggedSlot?.originalIndex === slot.originalIndex && draggedSlot?.service === "nuit" ? 'opacity-50 border-dashed' : 'bg-white'} ${isAdmin ? 'cursor-move hover:shadow-md' : ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <GripVertical className="w-4 h-4 text-gray-400" />
                                    <div>
                                      <p className="font-medium text-secondary">{slot.food_truck_nom}</p>
                                      <p className="text-xs text-muted-foreground">{slot.specialite}</p>
                                    </div>
                                  </div>
                                  {isAdmin && (
                                    <button 
                                      className="text-red-500 hover:text-red-700 p-1"
                                      onClick={() => removeFromZone("nuit", slot.originalIndex, slot.food_truck_nom)}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                              {isAdmin && (
                                <button 
                                  onClick={() => openAddToZone(zone, "nuit")}
                                  className={`w-full py-2 border-2 border-dashed rounded-lg ${colors.border} ${colors.text} hover:bg-white/30 transition-colors text-xs`}
                                >
                                  + Ajouter un FT
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB CARTE INTERACTIVE */}
            <TabsContent value="carte" className="space-y-4" data-testid="tab-carte">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                  <Map className="w-5 h-5 text-primary" />
                  Carte Interactive du Campus HEC
                </h3>
                <div className="flex gap-2 flex-wrap">
                  {selectedPlanning.planning_days?.map((day) => (
                    <Button
                      key={day.date}
                      variant={selectedDay === day.date ? "default" : "outline"}
                      size="sm"
                      className={selectedDay === day.date ? "bg-primary" : "border-[#E5E0DC]"}
                      onClick={() => setSelectedDay(day.date)}
                    >
                      {format(parseISO(day.date), "EEE d", { locale: fr })}
                    </Button>
                  ))}
                </div>
              </div>
              
              <HECInteractiveMap 
                planning={selectedPlanning}
                selectedDay={selectedDay}
                onReload={() => loadPlanning(selectedPlanning.id)}
              />
            </TabsContent>

            {/* TAB 3: MENUS */}
            {/* TAB 3: MENUS - Redesigned for direct editing */}
            <TabsContent value="menus" className="space-y-4" data-testid="tab-menus">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                  <ChefHat className="w-5 h-5 text-primary" />
                  Gestion des Menus - Photos & Allergènes
                </h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Day Selector - New structure */}
                  <div className="flex items-center gap-1 bg-white rounded-lg border border-[#E5E0DC] p-1">
                    {["Jeudi", "Vendredi", "Samedi Midi", "Samedi Soir"].map((jour) => (
                      <Button
                        key={jour}
                        size="sm"
                        variant={selectedMenuJour === jour ? "default" : "ghost"}
                        className={`${selectedMenuJour === jour ? "bg-primary text-white" : ""} ${jour.includes("Samedi") ? "font-medium" : ""}`}
                        onClick={() => setSelectedMenuJour(jour)}
                      >
                        {jour}
                        {jour === "Samedi Soir" && <Moon className="w-3 h-3 ml-1" />}
                      </Button>
                    ))}
                  </div>
                  
                  {/* Import/Export buttons */}
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={downloadMenusTemplate}
                      className="border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Template Excel
                    </Button>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleMenusImport}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={importingMenus}
                      />
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        disabled={importingMenus}
                      >
                        {importingMenus ? (
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        Importer
                      </Button>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={exportMenusForClient}
                      disabled={exportingClientMenus}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {exportingClientMenus ? (
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4 mr-1" />
                      )}
                      Export Client
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Info banner for day explanation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    <strong>{selectedMenuJour}</strong> — 
                    {selectedMenuJour === "Jeudi" && " Menu du jeudi (standard)"}
                    {selectedMenuJour === "Vendredi" && " Menu du vendredi (standard)"}
                    {selectedMenuJour === "Samedi Midi" && " Menu du samedi midi (standard)"}
                    {selectedMenuJour === "Samedi Soir" && " Menu spécial samedi soir - Sélectionnez les FT participants et personnalisez leurs menus (sans prix)"}
                  </span>
                </div>
                {editingFoodTruck && canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 hover:from-purple-600 hover:to-pink-600"
                    onClick={() => {
                      setMenuGeneratorFoodTruck(editingFoodTruck);
                      setShowMenuGenerator(true);
                    }}
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Générer Menu Design
                  </Button>
                )}
              </div>

              {/* Samedi Soir Selection Info */}
              {selectedMenuJour === "Samedi Soir" && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 text-purple-800">
                      <Moon className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        Sélection des Food Trucks pour la soirée du Samedi
                      </span>
                      <Badge className="bg-purple-200 text-purple-800">
                        {selectedPlanning.food_trucks?.filter(ft => ft.participe_samedi_soir !== false).length || 0} / {selectedPlanning.food_trucks?.length || 0} FT
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-purple-700 border-purple-300"
                        onClick={async () => {
                          // Sélectionner tous les FT
                          const token = localStorage.getItem("token");
                          try {
                            for (let i = 0; i < selectedPlanning.food_trucks.length; i++) {
                              await axios.put(
                                `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${i}`,
                                { participe_samedi_soir: true },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                            }
                            loadPlanning(selectedPlanning.id);
                            toast.success("Tous les FT sélectionnés pour Samedi Soir");
                          } catch (err) {
                            console.error("Error:", err);
                            toast.error("Erreur lors de la sélection");
                          }
                        }}
                      >
                        Tout sélectionner
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-gray-600 border-gray-300"
                        onClick={async () => {
                          // Désélectionner tous les FT
                          const token = localStorage.getItem("token");
                          try {
                            for (let i = 0; i < selectedPlanning.food_trucks.length; i++) {
                              await axios.put(
                                `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${i}`,
                                { participe_samedi_soir: false },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                            }
                            loadPlanning(selectedPlanning.id);
                            toast.success("Tous les FT désélectionnés");
                          } catch (err) {
                            console.error("Error:", err);
                            toast.error("Erreur lors de la désélection");
                          }
                        }}
                      >
                        Tout désélectionner
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Food Truck Selector */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {selectedPlanning.food_trucks?.map((ft, index) => {
                  const menuItems = getMenuForDay(ft, selectedMenuJour);
                  const hasSpecificMenu = ft.menu_par_jour?.[selectedMenuJour]?.length > 0;
                  
                  // Check if FT participates in Samedi Soir
                  const participeSamediSoir = ft.participe_samedi_soir !== false;
                  const isSamediSoir = selectedMenuJour === "Samedi Soir";
                  
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all relative ${
                        !participeSamediSoir && isSamediSoir
                          ? 'border-gray-200 bg-gray-50 opacity-60'
                          : editingFoodTruckIndex === index 
                            ? 'border-primary bg-primary/10' 
                            : 'border-[#E5E0DC] bg-white hover:border-primary/50'
                      }`}
                      onClick={() => {
                        if (!participeSamediSoir && isSamediSoir) return; // Don't select if not participating
                        setEditingFoodTruck({...ft, menu_items: menuItems});
                        setEditingFoodTruckIndex(index);
                      }}
                    >
                      {/* Checkbox for Samedi Soir participation */}
                      {isSamediSoir && (
                        <div 
                          className="absolute top-1 right-1 z-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newValue = !participeSamediSoir;
                            const token = localStorage.getItem("token");
                            axios.put(
                              `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${index}`,
                              { participe_samedi_soir: newValue },
                              { headers: { Authorization: `Bearer ${token}` } }
                            ).then(() => {
                              loadPlanning(selectedPlanning.id);
                              toast.success(newValue ? `${ft.nom} participe au Samedi Soir` : `${ft.nom} ne participe pas au Samedi Soir`);
                            }).catch((err) => {
                              console.error("Error updating participation:", err);
                              toast.error("Erreur lors de la mise à jour");
                            });
                          }}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                            participeSamediSoir 
                              ? 'bg-purple-500 border-purple-500 text-white' 
                              : 'bg-white border-gray-300 hover:border-purple-300'
                          }`}>
                            {participeSamediSoir && <CheckCircle className="w-3 h-3" />}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        {ft.logo_url ? (
                          <img src={ft.logo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <Truck className="w-4 h-4 text-primary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{ft.nom}</p>
                          <p className="text-xs text-muted-foreground truncate">{ft.specialite}</p>
                        </div>
                      </div>
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {(!isSamediSoir || participeSamediSoir) && (
                          <>
                            <Badge className={`text-xs ${menuItems?.length > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                              {menuItems?.length || 0} plats
                            </Badge>
                            {hasSpecificMenu && (
                              <Badge className="text-xs bg-green-100 text-green-700">✓ Menu</Badge>
                            )}
                          </>
                        )}
                        {isSamediSoir && !participeSamediSoir && (
                          <Badge className="text-xs bg-gray-200 text-gray-600">Non participant</Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Menu Editor */}
              {editingFoodTruck && editingFoodTruckIndex !== null && (
                <Card className="border-[#E5E0DC] border-2 border-primary/30">
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-white border-b border-[#E5E0DC]">
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-3">
                        {editingFoodTruck.logo_url ? (
                          <img src={editingFoodTruck.logo_url} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-primary" />
                        ) : (
                          <Truck className="w-6 h-6 text-primary" />
                        )}
                        <span>
                          <span className="text-lg">{editingFoodTruck.nom}</span>
                          <Badge className="ml-2 bg-primary/10 text-primary">{editingFoodTruck.specialite}</Badge>
                        </span>
                      </span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => {
                          const items = editingFoodTruck.menu_items || [];
                          setEditingFoodTruck({...editingFoodTruck, menu_items: [...items, { nom: "", ingredients: "", prix: 0, allergenes: "", photo_url: "" }]});
                        }}>
                          <Plus className="w-4 h-4 mr-1" />Ajouter un plat
                        </Button>
                        <Button size="sm" onClick={saveFoodTruck} className="bg-primary hover:bg-primary-hover">
                          <Save className="w-4 h-4 mr-1" />Sauvegarder
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {/* Info et actions spécifiques au jour */}
                    <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
                      {selectedMenuJour === "Jeudi" && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-blue-800">
                            <Info className="w-4 h-4" />
                            <span className="text-sm">Le menu du Jeudi sera <strong>dupliqué automatiquement</strong> pour Vendredi et Samedi Midi.</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-100"
                            onClick={async () => {
                              try {
                                const currentToken = localStorage.getItem("token");
                                const menuJeudi = editingFoodTruck.menu_items || [];
                                
                                if (menuJeudi.length === 0) {
                                  toast.error("Aucun plat à dupliquer. Ajoutez d'abord des plats au menu.");
                                  return;
                                }
                                
                                // Find the index of the food truck in the planning
                                const ftIndex = editingFoodTruckIndex !== null 
                                  ? editingFoodTruckIndex 
                                  : selectedPlanning.food_trucks?.findIndex(ft => ft.id === editingFoodTruck.id || ft.nom === editingFoodTruck.nom);
                                
                                if (ftIndex === -1 || ftIndex === null || ftIndex === undefined) {
                                  toast.error("Food truck non trouvé dans le planning");
                                  return;
                                }
                                
                                // Create clean copies of menu items (remove any non-serializable properties)
                                const cleanMenuItems = menuJeudi.map(item => ({
                                  nom: item.nom || "",
                                  ingredients: item.ingredients || "",
                                  prix: item.prix || 0,
                                  allergenes: item.allergenes || "",
                                  halal: item.halal || null,
                                  photo: item.photo || "",
                                  photo_url: item.photo_url || ""
                                }));
                                
                                // Create menu_par_jour object
                                const menu_par_jour = {
                                  ...(editingFoodTruck.menu_par_jour || {}),
                                  "Jeudi": cleanMenuItems,
                                  "Vendredi": cleanMenuItems,
                                  "Samedi Midi": cleanMenuItems
                                };
                                
                                // Use the dedicated food truck update endpoint
                                await axios.put(
                                  `${API_URL}/hec/plannings/${selectedPlanning.id}/food-truck/${ftIndex}`,
                                  { menu_par_jour: menu_par_jour },
                                  { headers: { Authorization: `Bearer ${currentToken}` } }
                                );
                                
                                // Update local state
                                const updatedFoodTrucks = JSON.parse(JSON.stringify(selectedPlanning.food_trucks));
                                updatedFoodTrucks[ftIndex].menu_par_jour = menu_par_jour;
                                setSelectedPlanning({...selectedPlanning, food_trucks: updatedFoodTrucks});
                                setEditingFoodTruck({...editingFoodTruck, menu_par_jour: menu_par_jour});
                                
                                toast.success(`Menu dupliqué ! ${cleanMenuItems.length} plat(s) copiés vers Vendredi et Samedi Midi.`);
                              } catch (err) {
                                console.error("Duplication error:", err);
                                toast.error("Erreur lors de la duplication: " + (err.response?.data?.detail || err.message));
                              }
                            }}
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Dupliquer vers Ven/Sam Midi
                          </Button>
                        </div>
                      )}
                      {selectedMenuJour === "Vendredi" && (
                        <div className="flex items-center gap-2 text-blue-800">
                          <Info className="w-4 h-4" />
                          <span className="text-sm">Menu identique au Jeudi (standard).</span>
                        </div>
                      )}
                      {selectedMenuJour === "Samedi Midi" && (
                        <div className="flex items-center gap-2 text-blue-800">
                          <Info className="w-4 h-4" />
                          <span className="text-sm">Menu identique au Jeudi (standard).</span>
                        </div>
                      )}
                      {selectedMenuJour === "Samedi Soir" && (
                        <div className="flex items-center gap-2 text-purple-800 bg-purple-50 -m-3 p-3 rounded-lg border-purple-200">
                          <Moon className="w-4 h-4" />
                          <span className="text-sm"><strong>Menu spécial Samedi Soir</strong> — Différent du menu standard, <strong>sans prix affiché</strong> (inclus dans la soirée).</span>
                        </div>
                      )}
                    </div>
                    
                    {editingFoodTruck.menu_items?.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <ChefHat className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p>Aucun plat dans le menu</p>
                        <p className="text-sm">Cliquez sur "Ajouter un plat" pour commencer</p>
                      </div>
                    )}
                    <div className="grid gap-4">
                      {editingFoodTruck.menu_items?.map((item, idx) => (
                        <div key={idx} className="p-4 bg-[#F9F7F5] rounded-lg border border-[#E5E0DC] relative">
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="ghost" 
                            className="absolute top-2 right-2 text-red-500 hover:bg-red-50"
                            onClick={() => removeMenuItemFromFoodTruck(idx)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          
                          <div className="grid grid-cols-12 gap-4">
                            {/* Photo */}
                            <div className="col-span-12 md:col-span-3">
                              <Label className="text-xs text-muted-foreground mb-1 block">Photo du plat</Label>
                              {item.photo_url ? (
                                <div className="relative">
                                  <img src={item.photo_url} alt={item.nom} className="w-full h-32 rounded-lg object-cover border border-[#E5E0DC]" />
                                  <Button 
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="absolute top-1 right-1 bg-white/80 h-6 w-6 p-0"
                                    onClick={() => updateMenuItem(idx, 'photo_url', '')}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="w-full h-32 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center">
                                  <Camera className="w-8 h-8 text-gray-400 mb-1" />
                                  <span className="text-xs text-gray-500">Pas de photo</span>
                                </div>
                              )}
                              <Input 
                                className="mt-2 text-xs" 
                                placeholder="URL de la photo" 
                                value={item.photo_url || ""} 
                                onChange={(e) => updateMenuItem(idx, 'photo_url', e.target.value)} 
                              />
                            </div>
                            
                            {/* Infos plat */}
                            <div className="col-span-12 md:col-span-9 space-y-3">
                              <div className="grid grid-cols-12 gap-3">
                                <div className={selectedMenuJour === "Samedi Soir" ? "col-span-12" : "col-span-8"}>
                                  <Label className="text-xs text-muted-foreground">Nom du plat *</Label>
                                  <Input 
                                    className="font-medium" 
                                    value={item.nom || ""} 
                                    onChange={(e) => updateMenuItem(idx, 'nom', e.target.value)} 
                                    placeholder="Ex: Pad Thaï au poulet"
                                  />
                                </div>
                                {selectedMenuJour !== "Samedi Soir" && (
                                  <div className="col-span-4">
                                    <Label className="text-xs text-muted-foreground">Prix (€) *</Label>
                                    <div className="relative">
                                      <Input 
                                        type="text"
                                        inputMode="decimal"
                                        className="pr-8 font-bold text-primary" 
                                        value={item._rawPrix !== undefined ? item._rawPrix : (item.prix !== undefined && item.prix !== null && item.prix !== '' ? String(item.prix).replace('.', ',') : "")} 
                                        onChange={(e) => updateMenuItem(idx, 'prix', e.target.value)} 
                                        placeholder="3,50"
                                      />
                                      <Euro className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div>
                                <Label className="text-xs text-muted-foreground">Ingrédients</Label>
                                <Textarea 
                                  rows={2}
                                  value={item.ingredients || item.description || ""} 
                                  onChange={(e) => updateMenuItem(idx, 'ingredients', e.target.value)} 
                                  placeholder="Liste des ingrédients: poulet, nouilles de riz, cacahuètes, sauce soja..."
                                />
                              </div>
                              
                              <div>
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3 text-orange-500" />
                                  Allergènes
                                </Label>
                                <Input 
                                  value={item.allergenes || ""} 
                                  onChange={(e) => updateMenuItem(idx, 'allergenes', e.target.value)} 
                                  placeholder="Ex: Gluten, Arachides, Crustacés, Soja, Sésame..."
                                  className="border-orange-200 focus:border-orange-400"
                                />
                                {item.allergenes && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {(typeof item.allergenes === 'string' ? item.allergenes.split(',') : Array.isArray(item.allergenes) ? item.allergenes : []).map((a, i) => (
                                      <Badge key={i} className="text-xs bg-orange-100 text-orange-700">{typeof a === 'string' ? a.trim() : a}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {!editingFoodTruck && (
                <Card className="border-[#E5E0DC]">
                  <CardContent className="p-12 text-center">
                    <ChefHat className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="text-lg font-semibold text-secondary mb-2">Sélectionnez un Food Truck</h3>
                    <p className="text-muted-foreground">Cliquez sur un food truck ci-dessus pour modifier son menu</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* TAB 4: SYNTHÈSE */}
            <TabsContent value="synthese" className="space-y-6" data-testid="tab-synthese">
              <h3 className="text-lg font-semibold text-secondary flex items-center gap-2">
                <LayoutDashboard className="w-5 h-5 text-primary" />
                Synthèse Globale de l'Événement
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-[#E5E0DC] bg-gradient-to-br from-primary/5 to-white">
                  <CardContent className="p-5 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Truck className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-3xl font-bold text-primary">{stats.foodTruckCount}</p>
                    <p className="text-sm text-muted-foreground">Food Trucks</p>
                  </CardContent>
                </Card>
                <Card className="border-[#E5E0DC] bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="p-5 text-center">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                      <Beer className="w-6 h-6 text-amber-700" />
                    </div>
                    <p className="text-3xl font-bold text-amber-700">{stats.barCount}</p>
                    <p className="text-sm text-muted-foreground">Bars (Soir)</p>
                  </CardContent>
                </Card>
                <Card className="border-[#E5E0DC] bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-5 text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                      <Calendar className="w-6 h-6 text-blue-700" />
                    </div>
                    <p className="text-3xl font-bold text-blue-700">{stats.daysCount}</p>
                    <p className="text-sm text-muted-foreground">Jours</p>
                  </CardContent>
                </Card>
                <Card className="border-[#E5E0DC] bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-5 text-center">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                      <MapPin className="w-6 h-6 text-green-700" />
                    </div>
                    <p className="text-3xl font-bold text-green-700">{stats.zonesCount}</p>
                    <p className="text-sm text-muted-foreground">Zones</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-[#E5E0DC]">
                <CardHeader className="border-b border-[#E5E0DC]">
                  <CardTitle className="text-base">Répartition des Services</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center">
                        <Sun className="w-8 h-8 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-secondary">{stats.totalMidi}</p>
                        <p className="text-muted-foreground">Créneaux Midi</p>
                        <p className="text-xs text-muted-foreground">3 zones (Volley, Central, Foot)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center">
                        <Moon className="w-8 h-8 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-secondary">{stats.totalSoir}</p>
                        <p className="text-muted-foreground">Créneaux Soir</p>
                        <p className="text-xs text-muted-foreground">Hall d'honneur uniquement</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#E5E0DC]">
                <CardHeader className="border-b border-[#E5E0DC]">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ChefHat className="w-5 h-5" />
                    Progression des Menus
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium">Menus renseignés</span>
                        <span className="text-sm text-muted-foreground">{stats.menusRemplis}/{stats.menusTotal}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${stats.menusRemplis === stats.menusTotal ? 'bg-green-500' : 'bg-yellow-500'}`}
                          style={{ width: `${stats.menusTotal > 0 ? (stats.menusRemplis / stats.menusTotal) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                    <Badge className={stats.menusRemplis === stats.menusTotal ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                      {stats.menusTotal > 0 ? Math.round((stats.menusRemplis / stats.menusTotal) * 100) : 0}%
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* DISPATCH BIÈRE BLONDE - RÉPARTITION ENTRE POINTS DE VENTE */}
              <Card className="border-[#E5E0DC] border-2 border-blue-400">
                <CardHeader className="bg-gradient-to-r from-blue-100 to-indigo-50 border-b border-blue-200">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2 text-blue-800">
                      <ArrowLeftRight className="w-5 h-5" />
                      Dispatch Bière Blonde 2026
                    </span>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={saveProjections}
                        disabled={saving}
                        className="bg-white hover:bg-blue-50"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                        Sauvegarder
                      </Button>
                      <Badge className="bg-blue-700 text-white px-3 py-1">
                        Total: {biereDispatch.totalBlonde.toLocaleString()} verres
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
                    <Info className="w-4 h-4 inline mr-1" />
                    Répartissez le total estimé de bière blonde entre le <strong>Beer Truck</strong> (fûts 30L) et les <strong>Bars Mobiles</strong> (fûts 30L).
                  </div>

                  {/* Input total verres */}
                  <div className="mb-6 p-4 bg-white rounded-lg border-2 border-blue-300">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Bière Blonde estimé (verres 50cl)
                    </label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={biereDispatch.totalBlonde === 0 ? "" : biereDispatch.totalBlonde}
                      onChange={(e) => {
                        const val = e.target.value;
                        setBiereDispatch(prev => ({ ...prev, totalBlonde: val === "" ? 0 : parseInt(val) || 0 }));
                      }}
                      className="text-lg font-bold text-center w-48"
                      placeholder="0"
                    />
                  </div>

                  {/* Répartition avec sliders/inputs */}
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    {/* Beer Truck */}
                    <div className="p-4 bg-gradient-to-br from-amber-100 to-yellow-50 rounded-lg border-2 border-amber-300">
                      <div className="flex items-center gap-2 mb-3">
                        <Beer className="w-5 h-5 text-amber-700" />
                        <span className="font-bold text-amber-800">Beer Truck</span>
                        <Badge className="bg-amber-600 text-white text-xs">Fûts 30L</Badge>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={biereDispatch.beerTruckPct}
                          onChange={(e) => updateDispatch('beerTruckPct', e.target.value)}
                          className="w-20 text-center font-bold"
                          placeholder="0"
                        />
                        <span className="text-gray-600">%</span>
                      </div>
                      <div className="text-center mt-3 p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-amber-700">{getDispatchVerres(biereDispatch.beerTruckPct).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">verres</p>
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-3xl font-bold text-amber-800">{getDispatchFuts(biereDispatch.beerTruckPct, 30)}</p>
                          <p className="text-xs text-amber-600">fûts 30L (~60 verres/fût)</p>
                        </div>
                      </div>
                    </div>

                    {/* Bar Mobile 1 */}
                    <div className="p-4 bg-gradient-to-br from-indigo-100 to-blue-50 rounded-lg border-2 border-indigo-300">
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-5 h-5 text-indigo-700" />
                        <span className="font-bold text-indigo-800">Bar Mobile 1</span>
                        <Badge className="bg-indigo-600 text-white text-xs">Fûts 30L</Badge>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={biereDispatch.barMobile1Pct}
                          onChange={(e) => updateDispatch('barMobile1Pct', e.target.value)}
                          className="w-20 text-center font-bold"
                          placeholder="0"
                        />
                        <span className="text-gray-600">%</span>
                      </div>
                      <div className="text-center mt-3 p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-indigo-700">{getDispatchVerres(biereDispatch.barMobile1Pct).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">verres</p>
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-3xl font-bold text-indigo-800">{getDispatchFuts(biereDispatch.barMobile1Pct, 30)}</p>
                          <p className="text-xs text-indigo-600">fûts 30L (~60 verres/fût)</p>
                        </div>
                      </div>
                    </div>

                    {/* Bar Mobile 2 */}
                    <div className="p-4 bg-gradient-to-br from-pink-100 to-rose-50 rounded-lg border-2 border-pink-300">
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-5 h-5 text-pink-700" />
                        <span className="font-bold text-pink-800">Bar Mobile 2</span>
                        <Badge className="bg-pink-600 text-white text-xs">Fûts 30L</Badge>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={biereDispatch.barMobile2Pct}
                          onChange={(e) => updateDispatch('barMobile2Pct', e.target.value)}
                          className="w-20 text-center font-bold"
                          placeholder="0"
                        />
                        <span className="text-gray-600">%</span>
                      </div>
                      <div className="text-center mt-3 p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-pink-700">{getDispatchVerres(biereDispatch.barMobile2Pct).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">verres</p>
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-3xl font-bold text-pink-800">{getDispatchFuts(biereDispatch.barMobile2Pct, 30)}</p>
                          <p className="text-xs text-pink-600">fûts 30L (~60 verres/fût)</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Validation pourcentages */}
                  {(biereDispatch.beerTruckPct + biereDispatch.barMobile1Pct + biereDispatch.barMobile2Pct) !== 100 && (
                    <div className="p-3 bg-red-100 rounded-lg border border-red-300 text-red-700 text-sm mb-4">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      La somme des pourcentages doit être égale à 100%. Actuellement: {biereDispatch.beerTruckPct + biereDispatch.barMobile1Pct + biereDispatch.barMobile2Pct}%
                    </div>
                  )}

                  {/* SYNTHÈSE FÛTS DISPATCH */}
                  <div className="p-4 bg-gradient-to-r from-green-100 to-emerald-50 rounded-lg border-2 border-green-400">
                    <h5 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Synthèse Fûts - Dispatch Blonde
                    </h5>
                    <div className="grid md:grid-cols-4 gap-3">
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-amber-400">
                        <Beer className="w-6 h-6 mx-auto mb-1 text-amber-700" />
                        <p className="text-xs text-gray-500">Beer Truck</p>
                        <p className="text-3xl font-bold text-amber-800">{getDispatchFuts(biereDispatch.beerTruckPct, 30)}</p>
                        <p className="text-xs text-amber-600">fûts 30L</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-indigo-400">
                        <Building2 className="w-6 h-6 mx-auto mb-1 text-indigo-700" />
                        <p className="text-xs text-gray-500">Bar Mobile 1</p>
                        <p className="text-3xl font-bold text-indigo-800">{getDispatchFuts(biereDispatch.barMobile1Pct, 30)}</p>
                        <p className="text-xs text-indigo-600">fûts 30L</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-pink-400">
                        <Building2 className="w-6 h-6 mx-auto mb-1 text-pink-700" />
                        <p className="text-xs text-gray-500">Bar Mobile 2</p>
                        <p className="text-3xl font-bold text-pink-800">{getDispatchFuts(biereDispatch.barMobile2Pct, 30)}</p>
                        <p className="text-xs text-pink-600">fûts 30L</p>
                      </div>
                      <div className="text-center p-3 bg-green-200 rounded-lg border-2 border-green-500">
                        <Calculator className="w-6 h-6 mx-auto mb-1 text-green-700" />
                        <p className="text-xs text-gray-600">Total Fûts 30L</p>
                        <p className="text-3xl font-bold text-green-800">
                          {getDispatchFuts(biereDispatch.beerTruckPct, 30) + getDispatchFuts(biereDispatch.barMobile1Pct, 30) + getDispatchFuts(biereDispatch.barMobile2Pct, 30)}
                        </p>
                        <p className="text-xs text-green-700">Tous fûts 30L</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* PROJECTION BOISSONS 2026 - BEER TRUCK */}
              <Card className="border-[#E5E0DC] border-2 border-amber-300">
                <CardHeader className="bg-gradient-to-r from-amber-100 to-yellow-50 border-b border-amber-200">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2 text-amber-800">
                      <Beer className="w-5 h-5" />
                      Beer Truck - Projections 2026
                      <Badge className="bg-green-500 text-white text-xs">NOUVEAU</Badge>
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-600 text-white">Fûts 30L</Badge>
                      <Badge className="bg-amber-700 text-white px-3">
                        Total: {getTotalBeerTruck()} verres
                      </Badge>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200 text-sm text-blue-700">
                    <Info className="w-4 h-4 inline mr-1" />
                    Le Beer Truck n'existait pas en 2025. Toutes les bières étaient servies aux bars mobiles.
                  </div>
                  
                  {/* Liste des boissons Beer Truck */}
                  <div className="space-y-2 mb-4">
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 px-2">
                      <div className="col-span-5">Boisson</div>
                      <div className="col-span-2">Type</div>
                      <div className="col-span-4 text-center">Projection 2026 (verres)</div>
                      <div className="col-span-1"></div>
                    </div>
                    {projections.beerTruck.map((item) => (
                      <div key={item.id} className="grid grid-cols-12 gap-2 items-center p-2 bg-amber-50 rounded-lg border border-amber-100">
                        <div className="col-span-5">
                          <Input 
                            value={item.nom} 
                            onChange={(e) => updateProjection('beerTruck', item.id, 'nom', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <select 
                            value={item.type}
                            onChange={(e) => updateProjection('beerTruck', item.id, 'type', e.target.value)}
                            className="w-full h-8 text-sm border rounded-md px-2"
                          >
                            <option value="biere">Bière 🍺</option>
                            <option value="soft">Soft</option>
                          </select>
                        </div>
                        <div className="col-span-4">
                          <Input 
                            type="number"
                            value={item.projection2026} 
                            onChange={(e) => updateProjection('beerTruck', item.id, 'projection2026', e.target.value)}
                            className="h-8 text-sm text-center font-semibold"
                          />
                        </div>
                        <div className="col-span-1 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeBoisson('beerTruck', item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Ajouter une boisson */}
                  <div className="grid grid-cols-12 gap-2 items-center p-2 bg-green-50 rounded-lg border-2 border-dashed border-green-300">
                    <div className="col-span-5">
                      <Input 
                        placeholder="Nouvelle boisson..."
                        value={newBoissonBeerTruck.nom} 
                        onChange={(e) => setNewBoissonBeerTruck({...newBoissonBeerTruck, nom: e.target.value})}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <select 
                        value={newBoissonBeerTruck.type}
                        onChange={(e) => setNewBoissonBeerTruck({...newBoissonBeerTruck, type: e.target.value})}
                        className="w-full h-8 text-sm border rounded-md px-2"
                      >
                        <option value="biere">Bière 🍺</option>
                        <option value="soft">Soft</option>
                      </select>
                    </div>
                    <div className="col-span-4">
                      <Input 
                        type="number"
                        placeholder="0"
                        value={newBoissonBeerTruck.projection2026 || ''} 
                        onChange={(e) => setNewBoissonBeerTruck({...newBoissonBeerTruck, projection2026: parseInt(e.target.value) || 0})}
                        className="h-8 text-sm text-center"
                      />
                    </div>
                    <div className="col-span-1 text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
                        onClick={() => addBoisson('beerTruck')}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Calcul fûts Beer Truck - 30L */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-amber-200 to-yellow-100 rounded-lg">
                    <h5 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      Calcul des Fûts 30L (Beer Truck)
                    </h5>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-gray-500">Total Bières</p>
                        <p className="text-2xl font-bold text-amber-700">{getTotalBiereBeerTruck()}</p>
                        <p className="text-xs text-gray-400">verres 50cl</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg border-2 border-amber-400">
                        <p className="text-xs text-gray-500">Fûts 30L nécessaires</p>
                        <p className="text-3xl font-bold text-amber-800">{calculerFuts(getTotalBiereBeerTruck(), 30, 50)}</p>
                        <p className="text-xs text-amber-600">~60 verres/fût</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-xs text-gray-500">Soft Drinks</p>
                        <p className="text-2xl font-bold text-blue-600">{getTotalByType('beerTruck', 'soft')}</p>
                        <p className="text-xs text-gray-400">verres</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* BAR MOBILE 1 - Projections 2026 */}
              <Card className="border-[#E5E0DC] border-2 border-indigo-300">
                <CardHeader className="bg-gradient-to-r from-indigo-100 to-blue-50 border-b border-indigo-200">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2 text-indigo-800">
                      <Building2 className="w-5 h-5" />
                      Bar Mobile 1 - Projections 2026
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-indigo-600 text-white">Fûts 30L</Badge>
                      <Badge className="bg-indigo-700 text-white px-3">
                        Total: {getTotalBar1()} verres
                      </Badge>
                      {saving && <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Liste des boissons Bar Mobile 1 */}
                  <div className="space-y-2 mb-4">
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 px-2">
                      <div className="col-span-4">Boisson</div>
                      <div className="col-span-2">Type</div>
                      <div className="col-span-2 text-center">Réf. 2025</div>
                      <div className="col-span-3 text-center">Projection 2026</div>
                      <div className="col-span-1"></div>
                    </div>
                    {projections.barMobile1.map((item) => (
                      <div key={item.id} className={`grid grid-cols-12 gap-2 items-center p-2 rounded-lg border ${item.type === 'biere' ? 'bg-amber-50 border-amber-200' : item.type === 'cidre' ? 'bg-green-50 border-green-200' : 'bg-indigo-50 border-indigo-100'}`}>
                        <div className="col-span-4">
                          <Input 
                            value={item.nom} 
                            onChange={(e) => updateProjection('barMobile1', item.id, 'nom', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <select 
                            value={item.type}
                            onChange={(e) => updateProjection('barMobile1', item.id, 'type', e.target.value)}
                            className="w-full h-8 text-xs border rounded-md px-1"
                          >
                            <option value="biere">🍺 Blonde</option>
                            <option value="cidre">🍏 Cidre</option>
                            <option value="soft">Soft</option>
                            <option value="vin">🍷 Vin</option>
                            <option value="cocktail">🍹 Cocktail</option>
                          </select>
                        </div>
                        <div className="col-span-2 text-center text-sm text-gray-500">
                          {item.ref2025 || 0}
                        </div>
                        <div className="col-span-3">
                          <Input 
                            type="number"
                            value={item.projection2026} 
                            onChange={(e) => updateProjection('barMobile1', item.id, 'projection2026', e.target.value)}
                            className="h-8 text-sm text-center font-semibold"
                          />
                        </div>
                        <div className="col-span-1 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeBoisson('barMobile1', item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Ajouter une boisson Bar 1 */}
                  <div className="grid grid-cols-12 gap-2 items-center p-2 bg-green-50 rounded-lg border-2 border-dashed border-green-300 mb-4">
                    <div className="col-span-4">
                      <Input 
                        placeholder="Nouvelle boisson..."
                        value={newBoissonBar1.nom} 
                        onChange={(e) => setNewBoissonBar1({...newBoissonBar1, nom: e.target.value})}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <select 
                        value={newBoissonBar1.type}
                        onChange={(e) => setNewBoissonBar1({...newBoissonBar1, type: e.target.value})}
                        className="w-full h-8 text-xs border rounded-md px-1"
                      >
                        <option value="soft">Soft</option>
                        <option value="vin">🍷 Vin</option>
                        <option value="biere">🍺 Blonde</option>
                        <option value="cidre">🍏 Cidre</option>
                      </select>
                    </div>
                    <div className="col-span-2 text-center text-sm text-gray-400">-</div>
                    <div className="col-span-3">
                      <Input 
                        type="number"
                        placeholder="0"
                        value={newBoissonBar1.projection2026 || ''} 
                        onChange={(e) => setNewBoissonBar1({...newBoissonBar1, projection2026: parseInt(e.target.value) || 0})}
                        className="h-8 text-sm text-center"
                      />
                    </div>
                    <div className="col-span-1 text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
                        onClick={() => addBoisson('barMobile1')}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Synthèse Fûts Bar Mobile 1 */}
                  <div className="p-4 bg-gradient-to-r from-indigo-200 to-blue-100 rounded-lg">
                    <h5 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      Synthèse Fûts 30L - Bar Mobile 1
                    </h5>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="text-center p-2 bg-white rounded border">
                        <p className="text-xs text-gray-500">🍺 Blonde</p>
                        <p className="text-lg font-bold text-indigo-700">{getTotalByType('barMobile1', 'biere')}</p>
                        <p className="text-xs text-gray-400">verres</p>
                      </div>
                      <div className="text-center p-2 bg-amber-100 rounded border-2 border-amber-400">
                        <p className="text-xs text-gray-600">Fûts Blonde</p>
                        <p className="text-2xl font-bold text-amber-800">{getFutsBlondeBar1()}</p>
                        <p className="text-xs text-amber-600">30L</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border">
                        <p className="text-xs text-gray-500">🍏 Cidre</p>
                        <p className="text-lg font-bold text-indigo-700">{getTotalByType('barMobile1', 'cidre')}</p>
                        <p className="text-xs text-gray-400">verres</p>
                      </div>
                      <div className="text-center p-2 bg-green-100 rounded border-2 border-green-400">
                        <p className="text-xs text-gray-600">Fûts Cidre</p>
                        <p className="text-2xl font-bold text-green-800">{getFutsCidreBar1()}</p>
                        <p className="text-xs text-green-600">30L</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* BAR MOBILE 2 - Projections 2026 */}
              <Card className="border-[#E5E0DC] border-2 border-pink-300">
                <CardHeader className="bg-gradient-to-r from-pink-100 to-rose-50 border-b border-pink-200">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2 text-pink-800">
                      <Building2 className="w-5 h-5" />
                      Bar Mobile 2 - Projections 2026
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-pink-600 text-white">Fûts 30L</Badge>
                      <Badge className="bg-pink-700 text-white px-3">
                        Total: {getTotalBar2()} verres
                      </Badge>
                      {saving && <Loader2 className="w-4 h-4 animate-spin text-pink-600" />}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Liste des boissons Bar Mobile 2 */}
                  <div className="space-y-2 mb-4">
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 px-2">
                      <div className="col-span-4">Boisson</div>
                      <div className="col-span-2">Type</div>
                      <div className="col-span-2 text-center">Réf. 2025</div>
                      <div className="col-span-3 text-center">Projection 2026</div>
                      <div className="col-span-1"></div>
                    </div>
                    {projections.barMobile2.map((item) => (
                      <div key={item.id} className={`grid grid-cols-12 gap-2 items-center p-2 rounded-lg border ${item.type === 'biere' ? 'bg-amber-50 border-amber-200' : item.type === 'cidre' ? 'bg-green-50 border-green-200' : item.type === 'cocktail' ? 'bg-pink-50 border-pink-200' : 'bg-rose-50 border-rose-100'}`}>
                        <div className="col-span-4">
                          <Input 
                            value={item.nom} 
                            onChange={(e) => updateProjection('barMobile2', item.id, 'nom', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <select 
                            value={item.type}
                            onChange={(e) => updateProjection('barMobile2', item.id, 'type', e.target.value)}
                            className="w-full h-8 text-xs border rounded-md px-1"
                          >
                            <option value="biere">🍺 Blonde</option>
                            <option value="cidre">🍏 Cidre</option>
                            <option value="vin">🍷 Vin</option>
                            <option value="cocktail">🍹 Cocktail</option>
                            <option value="soft">Soft</option>
                          </select>
                        </div>
                        <div className="col-span-2 text-center text-sm text-gray-500">
                          {item.ref2025 || 0}
                        </div>
                        <div className="col-span-3">
                          <Input 
                            type="number"
                            value={item.projection2026} 
                            onChange={(e) => updateProjection('barMobile2', item.id, 'projection2026', e.target.value)}
                            className="h-8 text-sm text-center font-semibold"
                          />
                        </div>
                        <div className="col-span-1 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeBoisson('barMobile2', item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Ajouter une boisson Bar 2 */}
                  <div className="grid grid-cols-12 gap-2 items-center p-2 bg-green-50 rounded-lg border-2 border-dashed border-green-300 mb-4">
                    <div className="col-span-4">
                      <Input 
                        placeholder="Nouvelle boisson..."
                        value={newBoissonBar2.nom} 
                        onChange={(e) => setNewBoissonBar2({...newBoissonBar2, nom: e.target.value})}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <select 
                        value={newBoissonBar2.type}
                        onChange={(e) => setNewBoissonBar2({...newBoissonBar2, type: e.target.value})}
                        className="w-full h-8 text-xs border rounded-md px-1"
                      >
                        <option value="soft">Soft</option>
                        <option value="vin">🍷 Vin</option>
                        <option value="cocktail">🍹 Cocktail</option>
                        <option value="biere">🍺 Blonde</option>
                        <option value="cidre">🍏 Cidre</option>
                      </select>
                    </div>
                    <div className="col-span-2 text-center text-sm text-gray-400">-</div>
                    <div className="col-span-3">
                      <Input 
                        type="number"
                        placeholder="0"
                        value={newBoissonBar2.projection2026 || ''} 
                        onChange={(e) => setNewBoissonBar2({...newBoissonBar2, projection2026: parseInt(e.target.value) || 0})}
                        className="h-8 text-sm text-center"
                      />
                    </div>
                    <div className="col-span-1 text-center">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
                        onClick={() => addBoisson('barMobile2')}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Synthèse Fûts Bar Mobile 2 */}
                  <div className="p-4 bg-gradient-to-r from-pink-200 to-rose-100 rounded-lg">
                    <h5 className="font-bold text-pink-800 mb-3 flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      Synthèse Fûts 30L - Bar Mobile 2
                    </h5>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="text-center p-2 bg-white rounded border">
                        <p className="text-xs text-gray-500">🍺 Blonde</p>
                        <p className="text-lg font-bold text-pink-700">{getTotalByType('barMobile2', 'biere')}</p>
                        <p className="text-xs text-gray-400">verres</p>
                      </div>
                      <div className="text-center p-2 bg-amber-100 rounded border-2 border-amber-400">
                        <p className="text-xs text-gray-600">Fûts Blonde</p>
                        <p className="text-2xl font-bold text-amber-800">{getFutsBlondeBar2()}</p>
                        <p className="text-xs text-amber-600">30L</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border">
                        <p className="text-xs text-gray-500">🍏 Cidre</p>
                        <p className="text-lg font-bold text-pink-700">{getTotalByType('barMobile2', 'cidre')}</p>
                        <p className="text-xs text-gray-400">verres</p>
                      </div>
                      <div className="text-center p-2 bg-green-100 rounded border-2 border-green-400">
                        <p className="text-xs text-gray-600">Fûts Cidre</p>
                        <p className="text-2xl font-bold text-green-800">{getFutsCidreBar2()}</p>
                        <p className="text-xs text-green-600">30L</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* RÉCAPITULATIF GLOBAL */}
              <Card className="border-[#E5E0DC] border-2 border-green-300">
                <CardHeader className="bg-gradient-to-r from-green-100 to-emerald-50 border-b border-green-200">
                  <CardTitle className="text-base flex items-center gap-2 text-green-800">
                    <TrendingUp className="w-5 h-5" />
                    Récapitulatif Global 2026
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-amber-100 rounded-lg">
                      <Beer className="w-8 h-8 mx-auto mb-2 text-amber-700" />
                      <p className="text-2xl font-bold text-amber-800">{getTotalBeerTruck()}</p>
                      <p className="text-sm text-amber-600">Beer Truck</p>
                      <p className="text-xs text-amber-500 mt-1">{calculerFuts(getTotalBiereBeerTruck(), 30, 50)} fûts 30L</p>
                    </div>
                    <div className="text-center p-4 bg-purple-100 rounded-lg">
                      <Wine className="w-8 h-8 mx-auto mb-2 text-purple-700" />
                      <p className="text-2xl font-bold text-purple-800">{getTotalStands()}</p>
                      <p className="text-sm text-purple-600">Bars Mobiles</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-300">
                      <p className="text-xs text-gray-500">Fûts Blonde 30L</p>
                      <p className="text-2xl font-bold text-amber-800">{calculerFuts(getTotalBiereStands(), 30, 50)}</p>
                      <p className="text-xs text-amber-600">(Bars Mobiles)</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-300">
                      <p className="text-xs text-gray-500">Fûts Cidre 30L</p>
                      <p className="text-2xl font-bold text-green-800">{calculerFuts(getTotalCidreStands(), 30, 50)}</p>
                      <p className="text-xs text-green-600">(Bars Mobiles)</p>
                    </div>
                    <div className="text-center p-4 bg-blue-100 rounded-lg">
                      <BarChart3 className="w-8 h-8 mx-auto mb-2 text-blue-700" />
                      <p className="text-2xl font-bold text-blue-800">{getTotalBeerTruck() + getTotalStands()}</p>
                      <p className="text-sm text-blue-600">Total Général</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      ) : null}

      {/* Food Truck Detail Modal (click from planning) */}
      <Dialog open={showFoodTruckDetail} onOpenChange={setShowFoodTruckDetail}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <Truck className="w-5 h-5" />
              {selectedFoodTruckDetail?.slot?.food_truck_nom}
            </DialogTitle>
          </DialogHeader>
          {selectedFoodTruckDetail && (
            <div className="space-y-6">
              {/* Info technique */}
              <div className="p-4 bg-[#F9F7F5] rounded-lg">
                <h4 className="font-semibold text-secondary mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Informations techniques
                </h4>
                {selectedFoodTruckDetail.ftInfo ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-muted-foreground">Responsable:</span> <span className="font-medium">{selectedFoodTruckDetail.ftInfo.responsable || "N/A"}</span></div>
                    <div><span className="text-muted-foreground">Téléphone:</span> <span className="font-medium">{selectedFoodTruckDetail.ftInfo.telephone || "N/A"}</span></div>
                    <div><span className="text-muted-foreground">Spécialité:</span> <span className="font-medium">{selectedFoodTruckDetail.ftInfo.specialite || "N/A"}</span></div>
                    <div><span className="text-muted-foreground">Véhicule:</span> <span className="font-medium">{selectedFoodTruckDetail.ftInfo.type_vehicule || "N/A"}</span></div>
                    <div><span className="text-muted-foreground">Électricité:</span> <span className="font-medium">{selectedFoodTruckDetail.ftInfo.puissance_electrique || "N/A"}</span></div>
                    <div><span className="text-muted-foreground">Plaque:</span> <code className="bg-gray-100 px-1 rounded text-xs">{selectedFoodTruckDetail.ftInfo.plaque_immatriculation || "N/A"}</code></div>
                    <div><span className="text-muted-foreground">Personnel:</span> <span className="font-medium">{selectedFoodTruckDetail.ftInfo.personnel || 1}</span></div>
                    <div><span className="text-muted-foreground">Ouverture:</span> <span className="font-medium">{selectedFoodTruckDetail.ftInfo.ouverture || "N/A"}</span></div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">Informations non disponibles</p>
                )}
              </div>

              {/* Menu avec photos et prix */}
              {selectedFoodTruckDetail.ftInfo?.menu_items?.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                    <ChefHat className="w-4 h-4" />
                    Carte des plats ({selectedFoodTruckDetail.ftInfo.menu_items.length})
                  </h4>
                  <div className="grid gap-3">
                    {selectedFoodTruckDetail.ftInfo.menu_items.map((item, i) => (
                      <div key={i} className="p-3 bg-white rounded-lg border border-amber-100">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-secondary">{item.nom}</p>
                          <span className="text-lg font-bold text-primary whitespace-nowrap ml-2">{item.prix?.toFixed(2) || "0.00"}€</span>
                        </div>
                        
                        {/* Ingrédients */}
                        {item.ingredients && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="text-muted-foreground">Ingrédients : </span>
                            {item.ingredients}
                          </p>
                        )}
                        
                        {/* Description si pas d'ingrédients */}
                        {!item.ingredients && item.description && (
                          <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                        )}
                        
                        {/* Allergènes */}
                        {item.allergenes && item.allergenes.length > 0 && item.allergenes !== "" && (
                          <div className="mt-2 pt-2 border-t border-orange-200 bg-orange-50 -mx-3 -mb-3 px-3 pb-2 rounded-b-lg">
                            <p className="text-xs font-medium text-orange-700 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Allergènes : {item.allergenes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Planning sur l'événement */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Présence sur l'événement
                </h4>
                <div className="space-y-2">
                  {selectedFoodTruckDetail.menus.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100">
                      <div className="flex items-center gap-2">
                        {m.creneau === 'midi' ? <Sun className="w-4 h-4 text-yellow-500" /> : <Moon className="w-4 h-4 text-indigo-500" />}
                        <span className="font-medium">{format(parseISO(m.date), "EEEE d MMMM", { locale: fr })}</span>
                        <Badge variant="outline" className="text-xs">{m.creneau}</Badge>
                        <Badge className={`text-xs ${getZoneColor(m.zone).bg} ${getZoneColor(m.zone).text}`}>{m.zone}</Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">{m.specialite}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFoodTruckDetail(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Food Truck Edit Dialog - Complete with employees, dimensions, logo, photos, menu with allergens */}
      <Dialog open={showFoodTruckDialog} onOpenChange={setShowFoodTruckDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-xl text-primary">
                {editingFoodTruckIndex !== null ? "Modifier le Food Truck" : "Ajouter un Food Truck"}
              </DialogTitle>
              {/* Version History Button - Only show when editing existing FT */}
              {editingFoodTruckIndex !== null && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => loadVersionHistory(editingFoodTruckIndex)}
                  disabled={loadingVersions}
                  className="ml-4"
                >
                  {loadingVersions ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <History className="w-4 h-4 mr-2" />
                  )}
                  Historique
                </Button>
              )}
            </div>
          </DialogHeader>
          {editingFoodTruck && (
            <div className="space-y-6">
              {/* Section 0: Import depuis adhérent (seulement en mode ajout) */}
              {editingFoodTruckIndex === null && (
                <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                  <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Importer depuis un adhérent existant
                  </h4>
                  <p className="text-sm text-green-600 mb-3">
                    Sélectionnez un food truck adhérent pour récupérer automatiquement ses informations et documents.
                  </p>
                  <Select 
                    value={selectedAdherentId || "none"} 
                    onValueChange={(v) => importAdherentData(v === "none" ? null : v)}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Choisir un adhérent..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Saisie manuelle --</SelectItem>
                      {allAdherents
                        .filter(a => !selectedPlanning?.food_trucks?.some(ft => ft.adherent_id === a.id))
                        .map((adherent) => (
                          <SelectItem key={adherent.id} value={adherent.id}>
                            {adherent.nom_food_truck || adherent.nom_commercial || adherent.nom || "Sans nom"} 
                            {adherent.specialite && ` (${adherent.specialite})`}
                            {adherent.doc_identite && " ✓ ID"}
                            {adherent.doc_haccp && " ✓ HACCP"}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {selectedAdherentId && (
                    <div className="mt-2 p-2 bg-white rounded border border-green-200 text-sm text-green-700">
                      ✓ Données importées - Vous pouvez les modifier ci-dessous si nécessaire.
                    </div>
                  )}
                </div>
              )}
              
              {/* Section 1: Infos générales */}
              <div className="p-4 bg-[#F9F7F5] rounded-lg">
                <h4 className="font-semibold text-secondary mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-primary" />
                  Informations générales
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><Label>Nom *</Label><Input value={editingFoodTruck.nom || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, nom: e.target.value})} /></div>
                  <div><Label>Spécialité *</Label><Input value={editingFoodTruck.specialite || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, specialite: e.target.value})} placeholder="Ex: Thaï, Burger, Pizza..." /></div>
                  <div><Label>Responsable</Label><Input value={editingFoodTruck.responsable || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, responsable: e.target.value})} /></div>
                  <div><Label>Téléphone</Label><Input value={editingFoodTruck.telephone || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, telephone: e.target.value})} /></div>
                  <div className="col-span-2"><Label>Logo (URL)</Label><Input value={editingFoodTruck.logo_url || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, logo_url: e.target.value})} placeholder="https://..." /></div>
                </div>
              </div>

              {/* Section 2: Équipe / Employés */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Équipe / Employés ({(editingFoodTruck.equipe || editingFoodTruck.employees || []).length})
                  </h4>
                  <Button type="button" size="sm" variant="outline" onClick={() => {
                    const equipe = editingFoodTruck.equipe || editingFoodTruck.employees || [];
                    setEditingFoodTruck({...editingFoodTruck, equipe: [...equipe, { nom: "", prenom: "", role: "", telephone: "", numero_piece_identite: "", plaque_immatriculation: "" }]});
                  }}>
                    <Plus className="w-4 h-4 mr-1" />Ajouter
                  </Button>
                </div>
                {(editingFoodTruck.equipe || editingFoodTruck.employees || []).map((emp, index) => (
                  <div key={index} className="bg-white p-3 rounded-lg border mb-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-2">
                        <Label className="text-xs text-muted-foreground">Nom *</Label>
                        <Input placeholder="Nom" value={emp.nom || ""} onChange={(e) => {
                          const newEmps = [...(editingFoodTruck.equipe || editingFoodTruck.employees || [])];
                          newEmps[index] = {...newEmps[index], nom: e.target.value};
                          setEditingFoodTruck({...editingFoodTruck, equipe: newEmps});
                        }} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-muted-foreground">Prénom</Label>
                        <Input placeholder="Prénom" value={emp.prenom || ""} onChange={(e) => {
                          const newEmps = [...(editingFoodTruck.equipe || editingFoodTruck.employees || [])];
                          newEmps[index] = {...newEmps[index], prenom: e.target.value};
                          setEditingFoodTruck({...editingFoodTruck, equipe: newEmps});
                        }} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-muted-foreground">N° Pièce ID</Label>
                        <Input placeholder="CNI / Passeport" value={emp.numero_piece_identite || ""} onChange={(e) => {
                          const newEmps = [...(editingFoodTruck.equipe || editingFoodTruck.employees || [])];
                          newEmps[index] = {...newEmps[index], numero_piece_identite: e.target.value};
                          setEditingFoodTruck({...editingFoodTruck, equipe: newEmps});
                        }} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-muted-foreground">Plaque Immat.</Label>
                        <Input placeholder="AA-123-BB" value={emp.plaque_immatriculation || ""} onChange={(e) => {
                          const newEmps = [...(editingFoodTruck.equipe || editingFoodTruck.employees || [])];
                          newEmps[index] = {...newEmps[index], plaque_immatriculation: e.target.value};
                          setEditingFoodTruck({...editingFoodTruck, equipe: newEmps});
                        }} />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-muted-foreground">Téléphone</Label>
                        <Input placeholder="Tél" value={emp.telephone || ""} onChange={(e) => {
                          const newEmps = [...(editingFoodTruck.equipe || editingFoodTruck.employees || [])];
                          newEmps[index] = {...newEmps[index], telephone: e.target.value};
                          setEditingFoodTruck({...editingFoodTruck, equipe: newEmps});
                        }} />
                      </div>
                      <div className="col-span-2 flex items-end gap-1 pb-1">
                        <div className="flex-1">
                          <Label className="text-xs text-muted-foreground">Rôle</Label>
                          <Input placeholder="Fonction" value={emp.role || ""} onChange={(e) => {
                            const newEmps = [...(editingFoodTruck.equipe || editingFoodTruck.employees || [])];
                            newEmps[index] = {...newEmps[index], role: e.target.value};
                            setEditingFoodTruck({...editingFoodTruck, equipe: newEmps});
                          }} />
                        </div>
                        <Button type="button" size="sm" variant="ghost" className="text-red-500 shrink-0" onClick={() => {
                          const newEmps = (editingFoodTruck.equipe || editingFoodTruck.employees || []).filter((_, i) => i !== index);
                          setEditingFoodTruck({...editingFoodTruck, equipe: newEmps});
                        }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {(!editingFoodTruck.equipe || editingFoodTruck.equipe.length === 0) && (!editingFoodTruck.employees || editingFoodTruck.employees.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-2">Aucun employé ajouté</p>
                )}
              </div>

              {/* Section 3: Véhicule - Dimensions et infos techniques */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  Véhicule - Dimensions et infos techniques
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><Label>Type véhicule</Label>
                    <Select value={editingFoodTruck.type_vehicule || ""} onValueChange={(v) => setEditingFoodTruck({...editingFoodTruck, type_vehicule: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Camion">Camion</SelectItem>
                        <SelectItem value="Remorque">Remorque</SelectItem>
                        <SelectItem value="Triporteur">Triporteur</SelectItem>
                        <SelectItem value="Food Trailer">Food Trailer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Longueur (m)</Label><Input type="text" inputMode="decimal" value={editingFoodTruck.longueur || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, longueur: e.target.value})} placeholder="Ex: 6.5" /></div>
                  <div><Label>Largeur (m)</Label><Input type="text" inputMode="decimal" value={editingFoodTruck.largeur || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, largeur: e.target.value})} placeholder="Ex: 2.2" /></div>
                  <div><Label>Hauteur (m)</Label><Input type="text" inputMode="decimal" value={editingFoodTruck.hauteur || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, hauteur: e.target.value})} placeholder="Ex: 3.0" /></div>
                  <div><Label>Ouverture</Label>
                    <Select value={editingFoodTruck.ouverture || ""} onValueChange={(v) => setEditingFoodTruck({...editingFoodTruck, ouverture: v})}>
                      <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Côté passager">Côté passager</SelectItem>
                        <SelectItem value="Côté conducteur">Côté conducteur</SelectItem>
                        <SelectItem value="Arrière">Arrière</SelectItem>
                        <SelectItem value="Les deux côtés">Les deux côtés</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Plaque immatriculation</Label><Input value={editingFoodTruck.plaque_immatriculation || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, plaque_immatriculation: e.target.value})} placeholder="Ex: AB-123-CD" /></div>
                  <div><Label>Puissance électrique</Label><Input value={editingFoodTruck.puissance_electrique || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, puissance_electrique: e.target.value})} placeholder="Ex: 2 PC 16 A" /></div>
                  <div className="flex items-center gap-2 pt-6">
                    <input type="checkbox" id="auvent" checked={editingFoodTruck.auvent || false} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, auvent: e.target.checked})} className="w-4 h-4" />
                    <Label htmlFor="auvent">Auvent</Label>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-green-200">
                  <div><Label>Véhicule personnel</Label><Input value={editingFoodTruck.vehicule_personnel || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, vehicule_personnel: e.target.value})} placeholder="Marque/Modèle" /></div>
                  <div><Label>Plaque VP</Label><Input value={editingFoodTruck.plaque_vp || ""} onChange={(e) => setEditingFoodTruck({...editingFoodTruck, plaque_vp: e.target.value})} placeholder="Ex: CD-456-EF" /></div>
                </div>
              </div>

              {/* Section Services proposés */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                  <Coffee className="w-4 h-4" />
                  Services proposés
                </h4>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="propose_cafe" 
                      checked={editingFoodTruck.propose_cafe || false} 
                      onChange={(e) => setEditingFoodTruck({...editingFoodTruck, propose_cafe: e.target.checked})} 
                      className="w-5 h-5 accent-amber-600" 
                    />
                    <Label htmlFor="propose_cafe" className="flex items-center gap-2 cursor-pointer">
                      <Coffee className="w-4 h-4 text-amber-700" />
                      Propose du café
                    </Label>
                  </div>
                </div>
              </div>

              {/* Section 4: Documents sécurisés */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documents (Zone sécurisée)
                </h4>
                {editingFoodTruckIndex === null ? (
                  <div className="p-4 bg-white rounded-lg border border-red-100 text-center text-gray-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                    <p>Sauvegardez d'abord le food truck pour pouvoir uploader des documents.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Pièce d'identité */}
                    <div className="p-3 bg-white rounded-lg border border-red-100">
                      <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-red-600" />
                        Pièce d'identité
                      </Label>
                      {editingFoodTruck.doc_identite ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-green-700 truncate flex-1" title={editingFoodTruck.doc_identite_filename}>
                            {editingFoodTruck.doc_identite_filename || "Document uploadé"}
                          </span>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800" onClick={() => downloadDocument(editingFoodTruck.doc_identite, editingFoodTruck.doc_identite_filename)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-800" onClick={() => handleDocumentDelete('identite')}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <input type="file" id="doc-identite" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => e.target.files?.[0] && handleDocumentUpload(e.target.files[0], 'identite')} disabled={uploadingDoc === 'identite'} />
                          <label htmlFor="doc-identite" className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingDoc === 'identite' ? 'border-gray-300 bg-gray-50' : 'border-red-200 hover:border-red-400 hover:bg-red-50'}`}>
                            {uploadingDoc === 'identite' ? <Loader2 className="w-5 h-5 animate-spin text-red-500" /> : <Upload className="w-5 h-5 text-red-400" />}
                            <span className="text-sm text-gray-600">{uploadingDoc === 'identite' ? 'Upload en cours...' : 'Cliquez pour importer'}</span>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Assurance RC Pro */}
                    <div className="p-3 bg-white rounded-lg border border-red-100">
                      <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-red-600" />
                        Assurance RC Pro
                      </Label>
                      {editingFoodTruck.doc_assurance_rc ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-green-700 truncate flex-1" title={editingFoodTruck.doc_assurance_rc_filename}>
                            {editingFoodTruck.doc_assurance_rc_filename || "Document uploadé"}
                          </span>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800" onClick={() => downloadDocument(editingFoodTruck.doc_assurance_rc, editingFoodTruck.doc_assurance_rc_filename)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-800" onClick={() => handleDocumentDelete('assurance_rc')}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <input type="file" id="doc-assurance-rc" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => e.target.files?.[0] && handleDocumentUpload(e.target.files[0], 'assurance_rc')} disabled={uploadingDoc === 'assurance_rc'} />
                          <label htmlFor="doc-assurance-rc" className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingDoc === 'assurance_rc' ? 'border-gray-300 bg-gray-50' : 'border-red-200 hover:border-red-400 hover:bg-red-50'}`}>
                            {uploadingDoc === 'assurance_rc' ? <Loader2 className="w-5 h-5 animate-spin text-red-500" /> : <Upload className="w-5 h-5 text-red-400" />}
                            <span className="text-sm text-gray-600">{uploadingDoc === 'assurance_rc' ? 'Upload en cours...' : 'Cliquez pour importer'}</span>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Certification HACCP */}
                    <div className="p-3 bg-white rounded-lg border border-red-100">
                      <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-red-600" />
                        Certification HACCP
                      </Label>
                      {editingFoodTruck.doc_haccp ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-green-700 truncate flex-1" title={editingFoodTruck.doc_haccp_filename}>
                            {editingFoodTruck.doc_haccp_filename || "Document uploadé"}
                          </span>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800" onClick={() => downloadDocument(editingFoodTruck.doc_haccp, editingFoodTruck.doc_haccp_filename)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-800" onClick={() => handleDocumentDelete('haccp')}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <input type="file" id="doc-haccp" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => e.target.files?.[0] && handleDocumentUpload(e.target.files[0], 'haccp')} disabled={uploadingDoc === 'haccp'} />
                          <label htmlFor="doc-haccp" className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingDoc === 'haccp' ? 'border-gray-300 bg-gray-50' : 'border-red-200 hover:border-red-400 hover:bg-red-50'}`}>
                            {uploadingDoc === 'haccp' ? <Loader2 className="w-5 h-5 animate-spin text-red-500" /> : <Upload className="w-5 h-5 text-red-400" />}
                            <span className="text-sm text-gray-600">{uploadingDoc === 'haccp' ? 'Upload en cours...' : 'Cliquez pour importer'}</span>
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Assurance Véhicule */}
                    <div className="p-3 bg-white rounded-lg border border-red-100">
                      <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                        <Car className="w-4 h-4 text-red-600" />
                        Assurance Véhicule
                      </Label>
                      {editingFoodTruck.doc_assurance_vehicule ? (
                        <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-green-700 truncate flex-1" title={editingFoodTruck.doc_assurance_vehicule_filename}>
                            {editingFoodTruck.doc_assurance_vehicule_filename || "Document uploadé"}
                          </span>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800" onClick={() => downloadDocument(editingFoodTruck.doc_assurance_vehicule, editingFoodTruck.doc_assurance_vehicule_filename)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-800" onClick={() => handleDocumentDelete('assurance_vehicule')}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="relative">
                          <input type="file" id="doc-assurance-vehicule" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => e.target.files?.[0] && handleDocumentUpload(e.target.files[0], 'assurance_vehicule')} disabled={uploadingDoc === 'assurance_vehicule'} />
                          <label htmlFor="doc-assurance-vehicule" className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingDoc === 'assurance_vehicule' ? 'border-gray-300 bg-gray-50' : 'border-red-200 hover:border-red-400 hover:bg-red-50'}`}>
                            {uploadingDoc === 'assurance_vehicule' ? <Loader2 className="w-5 h-5 animate-spin text-red-500" /> : <Upload className="w-5 h-5 text-red-400" />}
                            <span className="text-sm text-gray-600">{uploadingDoc === 'assurance_vehicule' ? 'Upload en cours...' : 'Cliquez pour importer'}</span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-xs text-red-500 mt-3 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Formats acceptés : PDF, JPG, PNG, DOC, DOCX (max 10 Mo)
                </p>
              </div>

              {/* Section 5: Photos du food truck */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-purple-700 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Photos du Food Truck
                  </h4>
                  <Button type="button" size="sm" variant="outline" onClick={() => {
                    const photos = editingFoodTruck.photos || [];
                    setEditingFoodTruck({...editingFoodTruck, photos: [...photos, ""]});
                  }}>
                    <Plus className="w-4 h-4 mr-1" />Ajouter photo
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {(editingFoodTruck.photos || []).map((photo, index) => (
                    <div key={index} className="relative">
                      <Input placeholder="URL de la photo" value={photo || ""} onChange={(e) => {
                        const newPhotos = [...(editingFoodTruck.photos || [])];
                        newPhotos[index] = e.target.value;
                        setEditingFoodTruck({...editingFoodTruck, photos: newPhotos});
                      }} />
                      <Button type="button" size="sm" variant="ghost" className="absolute right-1 top-1 text-red-500 h-6 w-6 p-0" onClick={() => {
                        const newPhotos = (editingFoodTruck.photos || []).filter((_, i) => i !== index);
                        setEditingFoodTruck({...editingFoodTruck, photos: newPhotos});
                      }}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                {(!editingFoodTruck.photos || editingFoodTruck.photos.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-2">Aucune photo ajoutée</p>
                )}
              </div>

              {/* Section 5: Menu avec plats, prix, allergènes et photos */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                    <ChefHat className="w-4 h-4" />
                    Menu (Plats, Prix, Allergènes, Photos)
                  </h4>
                  <Button type="button" size="sm" variant="outline" className="border-amber-300" onClick={addMenuItemToFoodTruck}>
                    <Plus className="w-4 h-4 mr-1" />Ajouter un plat
                  </Button>
                </div>
                {editingFoodTruck.menu_items?.map((item, index) => (
                  <div key={index} className="p-3 bg-white rounded-lg border border-amber-100 mb-3">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-6 md:col-span-4">
                        <Label className="text-xs">Nom du plat *</Label>
                        <Input value={item.nom || ""} onChange={(e) => updateMenuItem(index, 'nom', e.target.value)} />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-xs">Prix (€) *</Label>
                        <Input type="text" inputMode="decimal" value={item._rawPrix !== undefined ? item._rawPrix : (item.prix !== undefined && item.prix !== null && item.prix !== '' ? String(item.prix).replace('.', ',') : "")} onChange={(e) => updateMenuItem(index, 'prix', e.target.value)} placeholder="3,50" />
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <Label className="text-xs">Description</Label>
                        <Input value={item.description || ""} onChange={(e) => updateMenuItem(index, 'description', e.target.value)} placeholder="Description courte du plat" />
                      </div>
                      <div className="col-span-6">
                        <Label className="text-xs">Allergènes</Label>
                        <Input value={item.allergenes || ""} onChange={(e) => updateMenuItem(index, 'allergenes', e.target.value)} placeholder="Ex: Gluten, Lait, Œufs..." />
                      </div>
                      <div className="col-span-5">
                        <Label className="text-xs">Photo URL</Label>
                        <Input value={item.photo_url || ""} onChange={(e) => updateMenuItem(index, 'photo_url', e.target.value)} placeholder="https://..." />
                      </div>
                      <div className="col-span-1 flex items-end justify-center pb-1">
                        <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => removeMenuItemFromFoodTruck(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {(!editingFoodTruck.menu_items || editingFoodTruck.menu_items.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucun plat ajouté au menu</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFoodTruckDialog(false)}>Annuler</Button>
            <Button onClick={saveFoodTruck} className="bg-primary hover:bg-primary-hover"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bar Edit Dialog with Drink Prices */}
      <Dialog open={showBarDialog} onOpenChange={setShowBarDialog}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-amber-700">
              {editingBarIndex !== null ? "Modifier le Bar" : "Ajouter un Bar"}
            </DialogTitle>
          </DialogHeader>
          {editingBar && (
            <div className="space-y-4">
              <div><Label>Nom *</Label><Input value={editingBar.nom || ""} onChange={(e) => setEditingBar({...editingBar, nom: e.target.value})} /></div>
              <div><Label>Type</Label>
                <Select value={editingBar.type || "mobile"} onValueChange={(v) => setEditingBar({...editingBar, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="truck">Beer Truck</SelectItem>
                    <SelectItem value="mobile">Bar Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Responsable</Label><Input value={editingBar.responsable || ""} onChange={(e) => setEditingBar({...editingBar, responsable: e.target.value})} /></div>
              <div><Label>Téléphone</Label><Input value={editingBar.telephone || ""} onChange={(e) => setEditingBar({...editingBar, telephone: e.target.value})} /></div>
              
              {/* Drinks with Prices */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Carte des boissons (avec prix)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addDrinkToBar}>
                    <Plus className="w-4 h-4 mr-1" />Ajouter
                  </Button>
                </div>
                {editingBar.boissons?.map((drink, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                    <div className="col-span-7">
                      <Input placeholder="Nom de la boisson" value={drink.nom || ""} onChange={(e) => updateDrink(index, 'nom', e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Input type="text" inputMode="decimal" placeholder="Prix €" value={drink._rawPrix !== undefined ? drink._rawPrix : (drink.prix !== undefined && drink.prix !== null && drink.prix !== '' ? String(drink.prix).replace('.', ',') : "")} onChange={(e) => updateDrink(index, 'prix', e.target.value)} />
                    </div>
                    <div className="col-span-2 flex justify-center">
                      <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => removeDrinkFromBar(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <div><Label>Notes</Label><Textarea value={editingBar.notes || ""} onChange={(e) => setEditingBar({...editingBar, notes: e.target.value})} rows={2} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBarDialog(false)}>Annuler</Button>
            <Button onClick={saveBar} className="bg-amber-600 hover:bg-amber-700"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Edit Dialog */}
      <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">Menu du jour - {editingSlot?.food_truck_nom}</DialogTitle>
          </DialogHeader>
          {editingSlot && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F9F7F5] rounded-lg">
                <p className="text-sm"><strong>Date:</strong> {editingSlot.date && format(parseISO(editingSlot.date), "EEEE d MMMM yyyy", { locale: fr })}</p>
                <p className="text-sm"><strong>Créneau:</strong> {editingSlot.creneau === "midi" ? "Midi" : "Soir"}</p>
                <p className="text-sm"><strong>Spécialité:</strong> {editingSlot.specialite}</p>
              </div>
              <div>
                <Label>Menu du jour</Label>
                <Textarea value={editingSlot.menu_du_jour || ""} onChange={(e) => setEditingSlot({...editingSlot, menu_du_jour: e.target.value})} rows={5} placeholder="Décrivez le menu proposé pour ce créneau..." />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMenuDialog(false)}>Annuler</Button>
            <Button onClick={saveMenu} className="bg-primary hover:bg-primary-hover"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bars du Soir Manager Dialog */}
      <Dialog open={showBarsSoirDialog} onOpenChange={setShowBarsSoirDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-amber-700 flex items-center gap-2">
              <Beer className="w-5 h-5" />
              Gérer les bars du soir
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les bars qui seront présents au Hall d'honneur le {selectedDay && format(parseISO(selectedDay), "EEEE d MMMM", { locale: fr })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {selectedPlanning?.bars?.length > 0 ? (
              selectedPlanning.bars.map((bar, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    barsSoirSelection.includes(bar.nom)
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                  onClick={() => toggleBarSoirSelection(bar.nom)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        barsSoirSelection.includes(bar.nom)
                          ? 'border-amber-500 bg-amber-500'
                          : 'border-gray-300'
                      }`}>
                        {barsSoirSelection.includes(bar.nom) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-secondary">{bar.nom}</p>
                        <p className="text-xs text-muted-foreground">{bar.type || "Mobile"}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {bar.boissons?.length || 0} boissons
                    </Badge>
                  </div>
                  {bar.boissons?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2 ml-8">
                      {bar.boissons.slice(0, 4).map((b, i) => (
                        <Badge key={i} className="text-xs bg-amber-100 text-amber-700">{b.nom}</Badge>
                      ))}
                      {bar.boissons.length > 4 && (
                        <Badge className="text-xs bg-gray-100 text-gray-600">+{bar.boissons.length - 4}</Badge>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-4">
                Aucun bar configuré. Ajoutez des bars dans l'onglet "Informations Food Trucks".
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBarsSoirDialog(false)}>Annuler</Button>
            <Button onClick={saveBarsSoir} className="bg-amber-600 hover:bg-amber-700">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer ({barsSoirSelection.length} bar{barsSoirSelection.length > 1 ? 's' : ''})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog - Ajouter food truck à une zone */}
      <Dialog open={showAddToZoneDialog} onOpenChange={setShowAddToZoneDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Ajouter un Food Truck
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Zone:</strong> {addToZoneConfig.zone}<br/>
                <strong>Service:</strong> {
                  addToZoneConfig.service === "matin" ? "Matin" : 
                  addToZoneConfig.service === "midi" ? "Midi" : 
                  addToZoneConfig.service === "apres_midi" ? "Après-midi" : 
                  addToZoneConfig.service === "soir" ? "Soir" : "Nuit"
                }<br/>
                <strong>Jour:</strong> {addToZoneConfig.jour}
              </p>
            </div>
            
            {/* Option 1: Select existing food truck */}
            <div>
              <Label>Sélectionner un Food Truck existant</Label>
              <Select value={selectedFtForZone || ""} onValueChange={setSelectedFtForZone}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un food truck..." />
                </SelectTrigger>
                <SelectContent>
                  {selectedPlanning?.food_trucks?.map((ft) => (
                    <SelectItem key={ft.id} value={ft.id}>
                      {ft.nom || ft.nom_food_truck || "Sans nom"} 
                      {ft.specialite && ` (${ft.specialite})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Food trucks déjà ajoutés au planning HEC.
              </p>
            </div>

            {/* Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            {/* Option 2: Create new food truck */}
            <div className="text-center">
              <Button 
                variant="outline" 
                className="w-full border-dashed border-2 hover:border-primary hover:bg-primary/5"
                onClick={() => {
                  // Sauvegarder le contexte de la zone pour l'ajouter automatiquement après création
                  setPendingZoneAssignment({
                    zone: addToZoneConfig.zone,
                    service: addToZoneConfig.service,
                    jour: addToZoneConfig.jour
                  });
                  setShowAddToZoneDialog(false);
                  openAddFoodTruck();
                }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Créer un nouveau Food Truck
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                Le food truck sera automatiquement ajouté à la zone {addToZoneConfig.zone}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddToZoneDialog(false)}>Annuler</Button>
            <Button onClick={addFoodTruckToZone} disabled={!selectedFtForZone}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter à la zone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog - Version History */}
      <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Historique des versions - {versionsData.current?.nom}
            </DialogTitle>
            <DialogDescription>
              Sélectionnez une version précédente pour la restaurer. La version actuelle sera automatiquement sauvegardée.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Version */}
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-green-600 text-white mb-2">Version actuelle</Badge>
                  <p className="text-sm text-green-800">
                    Dernière modification: {versionsData.current?.updated_at 
                      ? format(parseISO(versionsData.current.updated_at), "dd/MM/yyyy HH:mm", { locale: fr })
                      : "N/A"
                    }
                  </p>
                </div>
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>

            {/* Previous Versions */}
            {versionsData.versions?.length > 0 ? (
              <div className="space-y-2">
                <h4 className="font-semibold text-secondary flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Versions précédentes ({versionsData.versions.length})
                </h4>
                {versionsData.versions.map((version, index) => (
                  <div 
                    key={version.id} 
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-secondary">
                        Version #{versionsData.versions.length - index}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Sauvegardé le {format(parseISO(version.saved_at), "dd/MM/yyyy à HH:mm", { locale: fr })}
                        {version.saved_by_name && ` par ${version.saved_by_name}`}
                      </p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => restoreVersion(version.id)}
                      disabled={restoringVersion}
                      className="text-amber-600 border-amber-300 hover:bg-amber-50"
                    >
                      {restoringVersion ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4 mr-1" />
                      )}
                      Restaurer
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground bg-gray-50 rounded-lg">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucune version précédente disponible</p>
                <p className="text-xs mt-1">Les versions sont créées automatiquement lors des modifications</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionsDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-600" />
              Gestion des Documents HEC
            </DialogTitle>
            <DialogDescription>
              Migrez les documents vers MongoDB ou nettoyez les références invalides.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Migration Result */}
            {migrationResult && (
              <div className={`border rounded-lg p-4 ${migrationResult.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`font-medium mb-2 ${migrationResult.status === 'completed' ? 'text-green-800' : 'text-red-800'}`}>
                  {migrationResult.status === 'completed' ? '✅ Migration terminée' : '❌ Erreur'}
                </h4>
                {migrationResult.status === 'completed' && (
                  <div className="text-sm space-y-2">
                    <p className="text-green-700">
                      <strong>{migrationResult.migrated_count}</strong> document(s) migré(s)
                      {migrationResult.error_count > 0 && (
                        <span className="text-amber-600 ml-2">
                          ({migrationResult.error_count} erreur(s))
                        </span>
                      )}
                    </p>
                    {migrationResult.errors?.length > 0 && (
                      <details className="mt-2" open>
                        <summary className="cursor-pointer text-amber-600 hover:text-amber-800">
                          Voir les erreurs ({migrationResult.errors.length})
                        </summary>
                        <ul className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1 bg-white/50 rounded p-2 text-red-600">
                          {migrationResult.errors.map((e, i) => (
                            <li key={i} className="truncate">
                              FT[{e.ft_index}] {e.doc_type}: {e.error}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                {migrationResult.error && (
                  <p className="text-sm text-red-700">{migrationResult.error}</p>
                )}
              </div>
            )}

            {/* Cleanup Result */}
            {cleanupResult && (
              <div className={`border rounded-lg p-4 ${cleanupResult.status === 'completed' ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className={`font-medium mb-2 ${cleanupResult.status === 'completed' ? 'text-blue-800' : 'text-red-800'}`}>
                  {cleanupResult.status === 'completed' ? '🧹 Nettoyage terminé' : '❌ Erreur'}
                </h4>
                {cleanupResult.status === 'completed' && (
                  <div className="text-sm space-y-2">
                    <p className="text-blue-700">
                      <strong>{cleanupResult.cleaned_count}</strong> référence(s) invalide(s) supprimée(s)
                    </p>
                    {cleanupResult.cleaned?.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                          Voir les détails ({cleanupResult.cleaned.length})
                        </summary>
                        <ul className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1 bg-white/50 rounded p-2">
                          {cleanupResult.cleaned.map((c, i) => (
                            <li key={i} className="truncate">
                              {c.ft_name} - {c.doc_type}: nettoyé ✓
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    <p className="text-blue-600 mt-2 text-xs">
                      Vous pouvez maintenant ré-uploader les documents.
                    </p>
                  </div>
                )}
                {cleanupResult.error && (
                  <p className="text-sm text-red-700">{cleanupResult.error}</p>
                )}
              </div>
            )}

            {/* Actions Info */}
            {!migrationResult && !cleanupResult && (
              <div className="space-y-4">
                {/* Migration Option */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Option 1: Migration
                  </h4>
                  <p className="text-sm text-purple-700 mb-2">
                    Migre les fichiers existants sur le disque vers MongoDB.
                  </p>
                  <p className="text-xs text-purple-600">
                    ⚠️ Ne fonctionne que si les fichiers existent encore sur le serveur.
                  </p>
                </div>

                {/* Cleanup Option */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                    <Eraser className="w-4 h-4" />
                    Option 2: Nettoyage
                  </h4>
                  <p className="text-sm text-orange-700 mb-2">
                    Supprime les références vers des fichiers qui n'existent plus.
                  </p>
                  <p className="text-xs text-orange-600">
                    ✓ Permet de ré-uploader proprement les documents.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => {
              setShowMigrationDialog(false);
              setMigrationResult(null);
              setCleanupResult(null);
            }}>
              Fermer
            </Button>
            
            {!migrationResult && !cleanupResult && (
              <>
                <Button 
                  onClick={handleMigrateDocuments}
                  disabled={migratingDocs || cleaningDocs}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {migratingDocs ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Migration...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Migrer
                    </>
                  )}
                </Button>
                <Button 
                  onClick={handleCleanupDocuments}
                  disabled={migratingDocs || cleaningDocs}
                  variant="outline"
                  className="border-orange-400 text-orange-600 hover:bg-orange-50"
                >
                  {cleaningDocs ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Nettoyage...
                    </>
                  ) : (
                    <>
                      <Eraser className="w-4 h-4 mr-2" />
                      Nettoyer
                    </>
                  )}
                </Button>
              </>
            )}

            {(migrationResult || cleanupResult) && (
              <Button 
                onClick={() => {
                  setMigrationResult(null);
                  setCleanupResult(null);
                }}
                variant="outline"
              >
                Nouvelle action
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Équipement */}
      <Dialog open={showEquipementDialog} onOpenChange={setShowEquipementDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plug className="w-5 h-5 text-yellow-600" />
              {editingEquipement?.id ? "Modifier l'équipement" : "Ajouter un équipement"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nom *</label>
              <Input
                value={editingEquipement?.nom || ""}
                onChange={(e) => setEditingEquipement({ ...editingEquipement, nom: e.target.value })}
                placeholder="Ex: Tireuse principale"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type d'équipement</label>
              <select
                className="w-full border rounded-lg p-2"
                value={editingEquipement?.type_equipement || TYPES_EQUIPEMENT[0]}
                onChange={(e) => setEditingEquipement({ ...editingEquipement, type_equipement: e.target.value })}
              >
                {TYPES_EQUIPEMENT.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                <Zap className="w-4 h-4 inline mr-1" />
                Puissance Électrique
              </label>
              <Input
                value={editingEquipement?.puissance_electrique || ""}
                onChange={(e) => setEditingEquipement({ ...editingEquipement, puissance_electrique: e.target.value })}
                placeholder="Ex: 3 PC 16, 2.5 kW"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                className="w-full border rounded-lg p-2 text-sm"
                rows={2}
                value={editingEquipement?.notes || ""}
                onChange={(e) => setEditingEquipement({ ...editingEquipement, notes: e.target.value })}
                placeholder="Notes additionnelles..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEquipementDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveEquipement} className="bg-yellow-600 hover:bg-yellow-700">
              <Save className="w-4 h-4 mr-1" />Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Staff Member */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              {editingStaffMember?.id ? "Modifier le membre" : "Ajouter un membre"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom *</label>
                <Input
                  value={editingStaffMember?.nom || ""}
                  onChange={(e) => setEditingStaffMember({ ...editingStaffMember, nom: e.target.value })}
                  placeholder="Nom de famille"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Prénom</label>
                <Input
                  value={editingStaffMember?.prenom || ""}
                  onChange={(e) => setEditingStaffMember({ ...editingStaffMember, prenom: e.target.value })}
                  placeholder="Prénom"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">N° Pièce d'identité</label>
              <Input
                value={editingStaffMember?.numero_piece_identite || ""}
                onChange={(e) => setEditingStaffMember({ ...editingStaffMember, numero_piece_identite: e.target.value })}
                placeholder="Numéro CNI / Passeport"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Plaque d'immatriculation</label>
              <Input
                value={editingStaffMember?.plaque_immatriculation || ""}
                onChange={(e) => setEditingStaffMember({ ...editingStaffMember, plaque_immatriculation: e.target.value })}
                placeholder="AA-123-BB"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Fonction</label>
                <Input
                  value={editingStaffMember?.fonction || ""}
                  onChange={(e) => setEditingStaffMember({ ...editingStaffMember, fonction: e.target.value })}
                  placeholder="Barman, Serveur..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <Input
                  value={editingStaffMember?.telephone || ""}
                  onChange={(e) => setEditingStaffMember({ ...editingStaffMember, telephone: e.target.value })}
                  placeholder="06 xx xx xx xx"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Jours de présence</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStaffMember?.presence_jeudi || false}
                    onChange={(e) => setEditingStaffMember({ ...editingStaffMember, presence_jeudi: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Jeudi</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStaffMember?.presence_vendredi || false}
                    onChange={(e) => setEditingStaffMember({ ...editingStaffMember, presence_vendredi: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Vendredi</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStaffMember?.presence_samedi || false}
                    onChange={(e) => setEditingStaffMember({ ...editingStaffMember, presence_samedi: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span>Samedi</span>
                </label>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-green-50 transition-colors">
                <input
                  type="checkbox"
                  checked={editingStaffMember?.benevole || false}
                  onChange={(e) => setEditingStaffMember({ ...editingStaffMember, benevole: e.target.checked })}
                  className="w-5 h-5 text-green-600"
                />
                <span className="font-medium">Bénévole</span>
                <span className="text-xs text-gray-500">(pas de coût)</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                <Euro className="w-4 h-4 inline mr-1" />
                Coût journalier (€)
              </label>
              <Input
                type="number"
                value={editingStaffMember?.cout_journalier || ""}
                onChange={(e) => setEditingStaffMember({ ...editingStaffMember, cout_journalier: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="150"
                disabled={editingStaffMember?.benevole}
                className={editingStaffMember?.benevole ? 'bg-gray-100 cursor-not-allowed' : ''}
              />
              {editingStaffMember?.benevole && (
                <p className="text-xs text-green-600 mt-1">Bénévole = pas de coût</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                className="w-full border rounded-lg p-2 text-sm"
                rows={2}
                value={editingStaffMember?.notes || ""}
                onChange={(e) => setEditingStaffMember({ ...editingStaffMember, notes: e.target.value })}
                placeholder="Notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveStaffMember} className="bg-purple-600 hover:bg-purple-700">
              <Save className="w-4 h-4 mr-1" />Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Coffee/Dessert */}
      <Dialog open={showCoffeeDessertDialog} onOpenChange={setShowCoffeeDessertDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coffee className="w-5 h-5 text-amber-600" />
              {editingCoffeeDessert?._index !== undefined ? "Modifier" : "Ajouter"} un Coffee/Dessert
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom *</label>
                <Input
                  value={editingCoffeeDessert?.nom || ""}
                  onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, nom: e.target.value })}
                  placeholder="Nom du stand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type de service</label>
                <Select
                  value={editingCoffeeDessert?.type_service || "Coffee & Dessert"}
                  onValueChange={(val) => setEditingCoffeeDessert({ ...editingCoffeeDessert, type_service: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_COFFEE_DESSERT.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Responsable</label>
                <Input
                  value={editingCoffeeDessert?.responsable || ""}
                  onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, responsable: e.target.value })}
                  placeholder="Nom du responsable"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <Input
                  value={editingCoffeeDessert?.telephone || ""}
                  onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, telephone: e.target.value })}
                  placeholder="06 xx xx xx xx"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                value={editingCoffeeDessert?.email || ""}
                onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, email: e.target.value })}
                placeholder="email@exemple.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Spécialités</label>
              <Input
                value={editingCoffeeDessert?.specialites || ""}
                onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, specialites: e.target.value })}
                placeholder="Café de spécialité, Pâtisseries maison..."
              />
            </div>

            {/* Jours de présence avec horaires */}
            <div className="border rounded-lg p-4 bg-amber-50">
              <label className="block text-sm font-medium mb-3 text-amber-800">Jours de présence et horaires</label>
              
              {/* Jeudi */}
              <div className="flex items-center gap-3 mb-3">
                <label className="flex items-center gap-2 cursor-pointer min-w-[100px]">
                  <input
                    type="checkbox"
                    checked={editingCoffeeDessert?.presence_jeudi || false}
                    onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, presence_jeudi: e.target.checked })}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className="font-medium text-blue-700">Jeudi</span>
                </label>
                <Input
                  value={editingCoffeeDessert?.horaire_jeudi || ""}
                  onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, horaire_jeudi: e.target.value })}
                  placeholder="10:00-18:00 ou À préciser"
                  className="flex-1"
                  disabled={!editingCoffeeDessert?.presence_jeudi}
                />
              </div>

              {/* Vendredi */}
              <div className="flex items-center gap-3 mb-3">
                <label className="flex items-center gap-2 cursor-pointer min-w-[100px]">
                  <input
                    type="checkbox"
                    checked={editingCoffeeDessert?.presence_vendredi || false}
                    onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, presence_vendredi: e.target.checked })}
                    className="w-4 h-4 accent-green-600"
                  />
                  <span className="font-medium text-green-700">Vendredi</span>
                </label>
                <Input
                  value={editingCoffeeDessert?.horaire_vendredi || ""}
                  onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, horaire_vendredi: e.target.value })}
                  placeholder="10:00-18:00 ou À préciser"
                  className="flex-1"
                  disabled={!editingCoffeeDessert?.presence_vendredi}
                />
              </div>

              {/* Samedi */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer min-w-[100px]">
                  <input
                    type="checkbox"
                    checked={editingCoffeeDessert?.presence_samedi || false}
                    onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, presence_samedi: e.target.checked })}
                    className="w-4 h-4 accent-purple-600"
                  />
                  <span className="font-medium text-purple-700">Samedi</span>
                </label>
                <Input
                  value={editingCoffeeDessert?.horaire_samedi || ""}
                  onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, horaire_samedi: e.target.value })}
                  placeholder="10:00-22:00 ou À préciser"
                  className="flex-1"
                  disabled={!editingCoffeeDessert?.presence_samedi}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                <Zap className="w-4 h-4 inline mr-1 text-orange-500" />
                Besoins électriques
              </label>
              <Input
                value={editingCoffeeDessert?.besoins_electriques || ""}
                onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, besoins_electriques: e.target.value })}
                placeholder="Ex: 2 PC 16A, Machine à café 3kW"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <Textarea
                value={editingCoffeeDessert?.notes || ""}
                onChange={(e) => setEditingCoffeeDessert({ ...editingCoffeeDessert, notes: e.target.value })}
                placeholder="Notes supplémentaires..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCoffeeDessertDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveCoffeeDessert} className="bg-amber-600 hover:bg-amber-700">
              <Save className="w-4 h-4 mr-1" />Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Dossier Dialog */}
      <Dialog open={showSendDossierDialog} onOpenChange={setShowSendDossierDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#8B1538]" />
              Envoyer le Dossier Complet HEC
            </DialogTitle>
            <DialogDescription>
              Envoyez le dossier complet de l'événement par email incluant tous les détails.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email destinataire *</label>
              <Input
                type="email"
                value={dossierForm.email_destinataire}
                onChange={(e) => setDossierForm({ ...dossierForm, email_destinataire: e.target.value })}
                placeholder="destinataire@hec.edu"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Objet de l'email</label>
              <Input
                value={dossierForm.objet}
                onChange={(e) => setDossierForm({ ...dossierForm, objet: e.target.value })}
                placeholder={`Dossier Complet HEC - ${selectedPlanning?.nom_evenement || 'Événement'}`}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Message personnalisé (optionnel)</label>
              <Textarea
                value={dossierForm.message}
                onChange={(e) => setDossierForm({ ...dossierForm, message: e.target.value })}
                placeholder="Bonjour,\n\nVeuillez trouver ci-joint le dossier complet pour l'événement..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium">Contenu inclus :</label>
              <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm">
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" /> Planning complet (PDF en pièce jointe)
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" /> ⚡ Besoins électriques (Food Trucks & Équipements)
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" /> 🍺 Liste des bars
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" /> 📍 Planning par zone
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" /> 🍽️ Menus (Jeudi, Vendredi, Samedi midi & Samedi soir Prépayé)
                </div>
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" /> 📄 Statut des documents (HACCP, ID, Assurances)
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dossierForm.include_photos_link}
                    onChange={(e) => setDossierForm({ ...dossierForm, include_photos_link: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-gray-700">📷 Inclure le lien vers les photos</span>
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDossierDialog(false)}>Annuler</Button>
            <Button 
              onClick={handleSendDossier} 
              disabled={sendingDossier || !dossierForm.email_destinataire}
              className="bg-[#8B1538] hover:bg-[#6B1028]"
            >
              {sendingDossier ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi en cours...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Envoyer le dossier</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Generator Modal */}
      <MenuGeneratorModal
        isOpen={showMenuGenerator}
        onClose={() => {
          setShowMenuGenerator(false);
          setMenuGeneratorFoodTruck(null);
        }}
        foodTruck={menuGeneratorFoodTruck}
        planningId={selectedPlanning?.id}
        selectedDay={selectedMenuJour}
        token={localStorage.getItem("token")}
      />
    </div>
  );
}
